import { LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { UsageSummary } from "../../../src/infra/provider-usage.ts";
import {
  handleChannelConfigReload as handleChannelConfigReloadInternal,
  handleChannelConfigSave as handleChannelConfigSaveInternal,
  handleNostrProfileCancel as handleNostrProfileCancelInternal,
  handleNostrProfileEdit as handleNostrProfileEditInternal,
  handleNostrProfileFieldChange as handleNostrProfileFieldChangeInternal,
  handleNostrProfileImport as handleNostrProfileImportInternal,
  handleNostrProfileSave as handleNostrProfileSaveInternal,
  handleNostrProfileToggleAdvanced as handleNostrProfileToggleAdvancedInternal,
  handleWhatsAppLogout as handleWhatsAppLogoutInternal,
  handleWhatsAppStart as handleWhatsAppStartInternal,
  handleWhatsAppWait as handleWhatsAppWaitInternal,
} from "./app-channels.ts";
import {
  drainOlderChatHistory,
  handleFleetTodoStatusChange,
  handleRejectArtifactChange,
  handleRestoreCheckpoint,
  loadOlderChatHistory,
  refreshChatCheckpoints,
  restorePersistedScrollAnchor,
} from "./app-chat-cursor-parity.ts";
import {
  activateChatSession as activateChatSessionTab,
  closeChatTab as closeChatTabInternal,
  ensureChatTabsInitialized,
  getChatOpenTabs,
  handleForkCurrentChatTab as handleForkCurrentChatTabInternal,
  archiveChatSessionInUi,
  handleArchiveAndNewChat as handleArchiveAndNewChatInternal,
  handleNewEmptyChatTab as handleNewEmptyChatTabInternal,
  openChatHistorySession as openChatHistorySessionInternal,
  restoreArchivedChatSession,
  openChatTab as openChatTabInternal,
  selectChatMessagesForSession,
  type ChatTabsHost,
} from "./app-chat-tabs.ts";
import {
  cancelChatEditRewindConfirm,
  CHAT_SESSIONS_ACTIVE_MINUTES,
  clearChatPendingMessageEdit,
  confirmChatEditRewindAndSend,
  handleAbortChat as handleAbortChatInternal,
  handleStopThinking as handleStopThinkingInternal,
  handleEditUserMessage as handleEditUserMessageInternal,
  handleImplementPlan as handleImplementPlanInternal,
  dismissPlanImplementOffer as dismissPlanImplementOfferInternal,
  handleRegenerateAssistant as handleRegenerateAssistantInternal,
  handleSendChat as handleSendChatInternal,
  editQueuedMessage as editQueuedMessageInternal,
  removeQueuedMessage as removeQueuedMessageInternal,
  loadChatHistoryCatalog,
  applyResetSessionIdentityHost,
} from "./app-chat.ts";
import { DEFAULT_CRON_FORM, DEFAULT_LOG_LEVEL_FILTERS } from "./app-defaults.ts";
import type { EventLogEntry } from "./app-events.ts";
import {
  handleFleetQuestionFreeTextChange as fleetQuestionFreeTextChange,
  handleFleetQuestionSubmit as fleetQuestionSubmit,
  handleFleetQuestionToggleOption as fleetQuestionToggleOption,
  refreshFleetInteraction as refreshFleetInteractionState,
} from "./app-fleet-interaction.ts";
import {
  connectGateway as connectGatewayInternal,
  pushPerSessionRing,
  resetPerSessionEventRingKeys,
} from "./app-gateway.ts";
import {
  handleConnected,
  handleDisconnected,
  handleFirstUpdated,
  handleUpdated,
} from "./app-lifecycle.ts";
import { reloadLogs } from "./app-logs.ts";
import { renderApp } from "./app-render.ts";
import {
  exportLogs as exportLogsInternal,
  handleChatScroll as handleChatScrollInternal,
  handleLogsScroll as handleLogsScrollInternal,
  noteChatMessagesGrew as noteChatMessagesGrewInternal,
  resetChatScroll as resetChatScrollInternal,
  resetChatScrollNav as resetChatScrollNavInternal,
  scrollChatToBottom as scrollChatToBottomInternal,
} from "./app-scroll.ts";
import {
  applySettings as applySettingsInternal,
  canonicalizeSessionKeyForRehydrate as canonicalizeSessionKeyForRehydrateInternal,
  loadCron as loadCronInternal,
  loadOverview as loadOverviewInternal,
  setTab as setTabInternal,
  setHelpTopic as setHelpTopicInternal,
  setEmployeeDetail as setEmployeeDetailInternal,
  setTheme as setThemeInternal,
  onPopState as onPopStateInternal,
  syncUrlWithSessionKey as syncUrlWithSessionKeyInternal,
} from "./app-settings.ts";
import {
  clearStickyLiveRunHint,
  resetToolStream as resetToolStreamInternal,
  type ToolStreamEntry,
  type ToolStreamHost,
} from "./app-tool-stream.js";
import type { CompactionStatus } from "./app-tool-stream.ts";
import type {
  AppViewState,
  WorkforceAgentRow,
  WorkforceBindingRow,
  WorkforceConfig,
  WorkforceInsightsState,
  WorkforceRoleRow,
  WorkforceSimulateResultState,
  WorkforceUnitRow,
} from "./app-view-state.ts";
import { resolveInjectedAssistantIdentity } from "./assistant-identity.ts";
import type { AgentFleetGraphSnapshot } from "./chat/agent-fleet-graph-model.ts";
// JAISIU-types-sweep: `state.chatAttachments` and the chat-controller
// bindings use the narrow `ChatAttachment` shape (filename/mime/
// sizeBytes/file url) instead of the wide `ui-types.ts`
// counterpart (dataUrl/previewUrl). The narrow type lives next to
// the chat-attachment controllers that consume those fields.
import type { ChatAttachment } from "./chat/attachment-types.ts";
import { consumeBackgroundRuns, recordBackgroundRuns } from "./chat/chat-background-runs.ts";
import { clearLiveState } from "./chat/chat-live-state.ts";
import type {
  ChatEditRewindConfirmPayload,
  ChatPendingMessageEdit,
} from "./chat/chat-pending-edit.ts";
import {
  restorePendingUserMessageIdsForSession,
  stashPendingUserMessageIdsForSession,
} from "./chat/chat-pending-user-messages.ts";
import {
  resolveChatSendOptions,
  setChatSendOptionForSession,
  type FleetChatSendOptions,
} from "./chat/chat-send-options.ts";
import { removeArchivedSessionKey } from "./chat/chat-session-archive.ts";
import {
  startSessionHealthMonitor,
  type SessionHealthMonitor,
} from "./chat/chat-session-health-monitor.ts";
import {
  type SessionHealthVerdict,
  computeSessionHealth,
  DEFAULT_SESSION_HEALTH_THRESHOLDS,
} from "./chat/chat-session-health.ts";
import { ChatSessionStore } from "./chat/chat-session-store.ts";
import {
  findFirstMatchingMessageIdAnywhere,
  SIDEBAR_JUMP_MIN_QUERY_LENGTH,
} from "./chat/chat-sidebar-search-jump.ts";
import { removeChatOpenTab } from "./chat/chat-tabs.ts";
import {
  clearHighlights,
  highlightMatchesIn,
  scrollToMatch,
  setActiveMatch,
} from "./chat/chat-thread-search-highlight.ts";
import {
  captureThreadSearchAnchor,
  findThreadScrollContainer,
  resolveThreadSearchRestoreTarget,
  type ChatThreadSearchAnchor,
} from "./chat/chat-thread-search-restore.ts";
import { clearChatWaitState } from "./chat/chat-wait-ticker.ts";
import type { GatewayModelChoice } from "./chat/delegate-router.ts";
import {
  resolveEffectiveChatComposerMode,
  type FleetComposerMode,
} from "./chat/fleet-composer-mode.ts";
import { pruneChatSendProjectId } from "./chat/fleet-projects-session.ts";
import { clearDeepBatchSearchState } from "./chat/global-search-deep-batch.ts";
import {
  getViewerNote,
  messageIdFromMarkdown as messageIdFromMarkdownForViewer,
  saveViewerNote,
  type ViewerNoteRecord,
} from "./chat/message-viewer-notes.ts";
import { resolveMainSessionKey } from "./chat/resolve-main-session.ts";
import { resolveAgentIdForSession } from "./chat/resolve-session-agent.ts";
import {
  canSessionNavigatorBack,
  canSessionNavigatorForward,
  createSessionNavigatorHistory,
  navigateSessionNavigatorBack,
  navigateSessionNavigatorForward,
  pushSessionNavigatorHistory,
} from "./chat/session-navigator-history.ts";
import {
  bumpSessionTabUnread as bumpSessionTabUnreadMap,
  clearSessionTabUnread,
  setSessionTabRunning,
  syncSubagentTabRunning,
} from "./chat/session-tab-activity.ts";
import { isSubagentRunActive } from "./chat/subagent-run-status.ts";
import {
  clearChatSubagentsForHost,
  collectAllStashedSubagents,
  lookupSubagentCard,
  restoreChatSubagentsForSession,
  stashChatSubagentsForSession,
} from "./chat/subagents-by-session.ts";
import { withProgrammaticScroll } from "./chat/with-programmatic-scroll.ts";
import {
  loadAgentFleetGraph,
  reapplyAgentFleetGraphLiveOverlay,
  scheduleAgentFleetGraphRefresh as scheduleAgentFleetGraphRefreshDebounced,
} from "./controllers/agent-fleet-graph.ts";
import {
  addAgentToConfigForm,
  ensureAgentInConfigForm,
  removeAgentFromConfigForm,
  updateAgentFieldsInConfigForm,
  updateSandboxFieldsInConfigForm,
  type SandboxFieldPatch,
} from "./controllers/agents-config.ts";
import {
  discoverAgentsFromSession,
  importSelectedAgentsFromChat,
  loadSessionsForAgentsImport,
  toggleAgentsImportSelection,
} from "./controllers/agents-import-from-chat.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadAssistantIdentity as loadAssistantIdentityInternal } from "./controllers/assistant-identity.ts";
import {
  closeChatContextPanel,
  loadChatContextBreakdown,
  openChatContextPanel,
  type SessionContextBreakdown,
} from "./controllers/chat-context-breakdown.ts";
import {
  buildBulkArchiveKeys,
  confirmBulkDelete,
  requestBulkDelete,
} from "./controllers/chat-history-bulk-select.ts";
import { loadChatModelCatalog as loadChatModelCatalogInternal } from "./controllers/chat-models.ts";
import {
  addChatPalaceContinuityPoint,
  clearChatPalaceContinuityForSession,
  closeChatPalaceContinuity,
  loadChatPalaceContinuity,
  openChatPalaceContinuity,
  removeChatPalaceContinuityPoint,
  resetChatPalaceContinuityForSessionChange,
  setChatPalaceContinuityDraft,
  setChatPalaceContinuityDraftCategory,
  toggleChatPalaceContinuity,
  type PalaceContinuitySnapshot,
} from "./controllers/chat-palace-continuity.ts";
import { type WorkspaceTreeNode } from "./controllers/chat-workspace.ts";
import {
  injectChatMessage,
  loadChatHistory,
  newestAssistantTimestampMs,
  resetChatSession,
} from "./controllers/chat.ts";
import { loadConfig, patchConfig, saveConfig } from "./controllers/config.ts";
import type { DevicePairingList } from "./controllers/devices.ts";
import type { EmployeeAccountOptions } from "./controllers/employees.js";
import {
  loadEmployeesData as loadEmployeesDataInternal,
  setSelectedEmployee as setSelectedEmployeeInternal,
  updateEmployee as updateEmployeeInternal,
  addEmployeeChannel as addEmployeeChannelInternal,
  removeEmployeeChannel as removeEmployeeChannelInternal,
  runEmployeesSync as runEmployeesSyncInternal,
  testGeneralConnection as testEmployeesConnectionInternal,
  createEmployee as createEmployeeInternal,
  deleteEmployee as deleteEmployeeInternal,
} from "./controllers/employees.js";
import type { ExecApprovalRequest } from "./controllers/exec-approval.ts";
import type { ExecApprovalsFile, ExecApprovalsSnapshot } from "./controllers/exec-approvals.ts";
import type { FleetGraphBindingDraft } from "./controllers/fleet-graph-config.ts";
import type { FleetGraphConfigDraft } from "./controllers/fleet-graph-config.ts";
import { fetchFleetGraphConfig } from "./controllers/fleet-graph-config.ts";
import {
  addFleetGraphBindingRow,
  closeFleetGraphEditor,
  deleteFleetGraphAgentFromEditor,
  openFleetGraphAgentEditor,
  openFleetGraphCreateAgent,
  openFleetGraphInboundEditor,
  patchFleetGraphAgentDraft,
  patchFleetGraphBinding,
  removeFleetGraphBindingRow,
  submitFleetGraphEditor,
  toggleFleetGraphAllowAgent,
  EMPTY_FLEET_GRAPH_AGENT_DRAFT,
  EMPTY_FLEET_GRAPH_BEHAVIOR_DRAFT,
  EMPTY_FLEET_GRAPH_CONFIG_DRAFT,
  type FleetGraphAgentDraft,
  type FleetGraphBehaviorDraft,
  type FleetGraphEditorMode,
  type FleetGraphEditorTab,
  saveFleetGraphBehaviorTab,
  setFleetGraphEditorTab,
} from "./controllers/fleet-graph-editor.ts";
import { previewFleetNodeReset, resetFleetNode } from "./controllers/fleet-node-reset.ts";
import {
  createFleetProject,
  deleteFleetProject,
  discoverFleetProjects,
  loadFleetProjects,
  resolveActiveFleetProject,
  updateFleetProject,
} from "./controllers/fleet-projects.ts";
import {
  shouldClearFleetAnswerEcho,
  shouldKeepPollingAfterFleetAnswer,
} from "./controllers/fleet-question.ts";
import {
  emptyFleetRoleEditorState,
  hydrateFleetRolesFromConfig,
  type FleetRoleEditorState,
} from "./controllers/fleet-roles.ts";
import { handleChatSendSelfImprovementChange } from "./controllers/fleet-self-improvement-ui.ts";
import { loadFleetSessionFiles, readFleetSessionFile } from "./controllers/fleet-session-files.ts";
import { loadFlociAgents } from "./controllers/floci.ts";
import {
  cancelDebouncedGlobalSearch,
  clearGlobalSearch,
  mergeGlobalSearchResults,
  runDebouncedGlobalSearch,
  type GlobalSearchDeepSource,
  type GlobalSearchResult,
} from "./controllers/global-search.ts";
import {
  clearGoogleDriveServiceAccount,
  saveGoogleDriveServiceAccount,
  testGoogleDriveConnection,
  type GoogleDriveHost,
} from "./controllers/google-drive.ts";
import {
  createLiveSession as createLiveSessionInternal,
  endLiveSession as endLiveSessionInternal,
  loadLiveSessions as loadLiveSessionsInternal,
  pauseLiveSession as pauseLiveSessionInternal,
  resumeLiveSession as resumeLiveSessionInternal,
  selectLiveSession as selectLiveSessionInternal,
  startLiveSession as startLiveSessionInternal,
  type LiveSessionCreateInput,
} from "./controllers/live-sessions.ts";
import { emptyLlmConnectState, type LlmConnectState } from "./controllers/llm-connect.ts";
import { loadNetwork, type NetworkInstance } from "./controllers/network.ts";
import {
  finalizeRecordSkill as finalizeRecordSkillInternal,
  loadRecordSkillPrivacy as loadRecordSkillPrivacyInternal,
  loadRecordSkills as loadRecordSkillsInternal,
  selectRecordSkill as selectRecordSkillInternal,
  startRecordSkill as startRecordSkillInternal,
  stopRecordSkill as stopRecordSkillInternal,
} from "./controllers/record-skill.ts";
import {
  loadInspectorSessionLogs,
  loadSessionArtifactDetail,
  loadSessionArtifacts,
  saveSessionWorkspaceFile,
} from "./controllers/session-artifacts.ts";
import {
  clearSessionPreview,
  loadSessionEnvironment,
  recreateSessionEnvironment,
  registerSessionPreview,
  type SessionEnvironmentState,
} from "./controllers/session-environment.ts";
import {
  SESSION_KEYED_CACHE_MAX_SESSIONS,
  emptySessionUploadsState,
  rememberSessionKeyedEntry,
} from "./controllers/session-uploads.ts";
import {
  cancelSessionDelete,
  cancelSessionDeleteAll,
  confirmSessionDelete,
  confirmSessionDeleteAll,
  loadSessions,
  patchSession,
  requestSessionDelete,
  requestSessionDeleteAll,
  loadSessionHistory,
  closeSessionHistory,
} from "./controllers/sessions.ts";
import type { SkillMessage } from "./controllers/skills.ts";
import {
  loadProcsetStatuses,
  loadSubagentRegistryRuns,
  requestCancelAllSubagents,
  requestFanInSynthesis,
  requestSessionsCancel,
  requestSessionsSpawn,
  syncCardsFromRegistry,
  overlayProcsetStatusOnCards,
} from "./controllers/subagents.ts";
import {
  cancelWorkUnitsForDeadTunnel,
  createTunnelHealthController,
  type TunnelHealthController,
} from "./controllers/tunnel-health.ts";
import {
  loadYouTrackIssue as loadYouTrackIssueInternal,
  searchYouTrackIssues as searchYouTrackIssuesInternal,
  createYouTrackIssue as createYouTrackIssueInternal,
  transitionYouTrackIssue as transitionYouTrackIssueInternal,
  addYouTrackComment as addYouTrackCommentInternal,
  saveYouTrackFilter as saveYouTrackFilterInternal,
  deleteYouTrackFilter as deleteYouTrackFilterInternal,
  loadYouTrackSavedFilters as loadYouTrackSavedFiltersInternal,
  searchMyIssues as searchMyIssuesInternal,
} from "./controllers/youtrack.ts";
import type { GatewayBrowserClient, GatewayHelloOk } from "./gateway.ts";
import { loadShortcuts, saveShortcuts, type ShortcutsConfig } from "./keyboard-shortcuts.ts";
import type { Tab } from "./navigation.ts";
import { loadSettings, loadFavoriteChats, toggleFavoriteChat, type UiSettings } from "./storage.ts";
import {
  idbLoadDraft,
  idbLoadPinnedActiveJob,
  idbSavePinnedActiveJob,
} from "./storage/idb-session-cache.ts";
// JAISIU-3474 — scroll-anchor IDB helpers are imported lazily inside
// `loadScrollAnchorForActiveSession` / `flushScrollAnchorSave` so the
// IDB shim is only loaded when an anchor restore is in play (it
// adds ~30 KB of code and we don't want it on every cold start).
import type { ResolvedTheme, ThemeMode } from "./theme.ts";
import { TunnelManager, type TunnelRecord } from "./tunnel/tunnel-state.ts";
import type {
  AgentsListResult,
  AgentsFilesListResult,
  AgentIdentityResult,
  ConfigSnapshot,
  ConfigUiHints,
  CronJob,
  CronRunLogEntry,
  CronStatus,
  HealthSnapshot,
  LogEntry,
  LogLevel,
  PresenceEntry,
  ChannelsStatusSnapshot,
  SessionsListResult,
  SkillStatusReport,
  StatusSummary,
  NostrProfile,
  YouTrackIssue,
} from "./types.ts";
import type { FleetProjectSummary } from "./types.ts";
import {
  type AgentRosterItem,
  type ChatQueueItem,
  type ChatSubagentCard,
  type CronFormState,
} from "./ui-types.ts";
import type { NostrProfileFormState } from "./views/channels.nostr-profile-form.ts";
import type { ChatSessionContextMeta } from "./views/chat-session-context.ts";
import { scrollChatSessionTabIntoView } from "./views/chat-session-tabs.ts";
import { syncChatComposeDraft } from "./views/chat.ts";
import type { EmployeeRow, ConnectorInfo, SyncHistoryRow } from "./views/employees.js";

declare global {
  interface Window {
    __OPENCLAW_CONTROL_UI_BASE_PATH__?: string;
  }
}

const injectedAssistantIdentity = resolveInjectedAssistantIdentity();

/**
 * JAISIU-3503 — how long the "Run stopped" confirmation stays visible
 * in the chat composer after a Stop-button / Stop-run / /stop abort.
 * 4 seconds is enough for the operator to register the feedback without
 * crowding the next prompt they begin typing.
 */
const CHAT_STOP_TOAST_MS = 4000;

function resolveOnboardingMode(): boolean {
  if (!window.location.search) {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("onboarding");
  if (!raw) {
    return false;
  }
  const normalized = raw.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

/* JAISIU-3588 (followup) — ref-counted document mousedown listener
 * that closes the `+` popover when the operator clicks anywhere
 * outside the dock OR the popover panel. The panel is rendered with
 * `position: fixed` (so it can escape the dock's clip ancestors), so
 * `event.target` may be the panel itself, the trigger button, or
 * anything else on the page. The listener detaches itself when it
 * fires (so a fast double-click outside doesn't toggle the popover
 * twice) and is also detached when the host closes the popover via
 * any other path (Esc, row pick, etc.) to avoid a stale listener
 * triggering after the popover is already closed. */
let chatComposerPlusOutsideClickAttached = false;
const onChatComposerPlusDocMousedown = (event: MouseEvent) => {
  const target = event.target;
  if (!(target instanceof Node)) return;
  // Click inside the popover panel — keep the popover open.
  if (target instanceof Element && target.closest?.("[data-testid='chat-composer-plus-panel']")) {
    return;
  }
  // Click on the `+` trigger itself — the button's own click
  // handler will toggle. Don't fire our close handler or the popover
  // would close and immediately re-open from the same click.
  if (target instanceof Element && target.closest?.("[data-testid='chat-composer-plus']")) {
    return;
  }
  // Click outside: detach synchronously, then close.
  if (typeof document !== "undefined") {
    document.removeEventListener("mousedown", onChatComposerPlusDocMousedown);
  }
  chatComposerPlusOutsideClickAttached = false;
  chatComposerPlusOutsideClickOnClose?.();
};

let chatComposerPlusOutsideClickOnClose: (() => void) | null = null;

export function registerChatComposerPlusOutsideClick(onClose: () => void): void {
  if (typeof document === "undefined") return;
  chatComposerPlusOutsideClickOnClose = onClose;
  if (chatComposerPlusOutsideClickAttached) return;
  document.addEventListener("mousedown", onChatComposerPlusDocMousedown);
  chatComposerPlusOutsideClickAttached = true;
}

export function unregisterChatComposerPlusOutsideClick(): void {
  if (typeof document === "undefined") return;
  if (!chatComposerPlusOutsideClickAttached) return;
  document.removeEventListener("mousedown", onChatComposerPlusDocMousedown);
  chatComposerPlusOutsideClickAttached = false;
  chatComposerPlusOutsideClickOnClose = null;
}

@customElement("jaisiu-app")
export class OpenClawApp extends LitElement {
  // JAISIU-types-sweep: the controllers/session-logs.ts adapter writes
  // dynamic per-prefix log buffers through bracket notation
  // (e.g. `app['gatewayLogsLoading'] = true`). The strict class shape
  // didn't allow that pattern, so every consumer that wrote through
  // the adapter got TS errors. The index signature preserves the
  // typed `@state()` declarations (those win at lookup time, the
  // index signature is the catch-all) and lets app-render.ts call
  // `loadSessionLogsByPrefix(state, ...)` and `stateAdapter(state, ...)`
  // without casting through `Record<string, unknown>` on every call.
  [key: string]: unknown;

  @state() settings: UiSettings = loadSettings();
  @state() shortcutsConfig: ShortcutsConfig = loadShortcuts();
  @state() password = "";
  @state() tab: Tab = "chat";
  @state() helpTopic: import("./help/help-content.ts").HelpTopicId = "intro";
  @state() onboarding = resolveOnboardingMode();
  @state() connected = false;
  @state() theme: ThemeMode = this.settings.theme ?? "system";
  @state() themeResolved: ResolvedTheme = "dark";
  @state() hello: GatewayHelloOk | null = null;
  @state() lastError: string | null = null;
  /**
   * Per-session, per-host tunnel health records, keyed by `hostId` so
   * a future multi-host world can co-exist with the current single-
   * host UI without changing the call site. The inspector's
   * "Tunnels" tab reads this map. The map is rebuilt by
   * {@link tunnelHealthController} whenever a record changes.
   *
   * Crucial: this field is the ONLY way the UI is allowed to surface
   * tunnel state. `lastError` above is reserved for chat/run errors
   * the operator needs to act on inline; tunnel transitions NEVER
   * write to it.
   */
  @state() tunnelRecords: Record<string, TunnelRecord> = {};
  /**
   * Tunnel state machine. One manager per app instance, owned by
   * `OpenClawApp`. The controller below bridges gateway-client
   * events into the manager and re-paints `tunnelRecords`.
   */
  private tunnelManager: TunnelManager = new TunnelManager();
  private tunnelHealthController: TunnelHealthController = createTunnelHealthController(
    this.tunnelManager,
  );
  /**
   * JAISIU-4089 — tri-state session health verdict (online / degraded
   * / offline). Updated by `sessionHealthMonitor` on a 5s poll plus
   * explicit `noteFailure` / `noteReconnect` calls. The topbar
   * indicator in `app-render.ts` reads this via the render state and
   * paints the green / amber / red pill accordingly.
   */
  @state() sessionHealthVerdict: SessionHealthVerdict | null = null;
  /**
   * JAISIU-4089 — monitor that stamps `sessionHealthVerdict` on a
   * fixed interval and on demand. Started in `firstUpdated`,
   * disposed in `disconnectedCallback`. Not reactive (private).
   */
  private sessionHealthMonitor: SessionHealthMonitor | null = null;
  /** Short-lived confirmation after delegating to subagents (Fleet composer). */
  @state() fleetSendAck: string | null = null;
  @state() eventLog: EventLogEntry[] = [];
  private eventLogBuffer: EventLogEntry[] = [];
  private eventLogBySession: Record<string, EventLogEntry[]> = {};
  private toolStreamSyncTimer: number | null = null;
  private sidebarCloseTimer: number | null = null;

  @state() assistantName = injectedAssistantIdentity.name;
  @state() assistantAvatar = injectedAssistantIdentity.avatar;
  @state() assistantAgentId = injectedAssistantIdentity.agentId ?? null;

  // JAISIU-XXXX — don't silently resume a fork from a previous page load.
  // The webchat client persists the last-active session key on every send
  // (see `setLastActiveSessionKey` in app-chat.ts), and reloads rehydrate
  // from it. If the previous session was a fork (`agent:*:fork:*`),
  // rehydrating there makes the operator's next "hello" land in a
  // sidetracked conversation that they can't see in the sidebar. The
  // bare "main" string canonicalises to `agent:main:main` server-side
  // (see `canonicalizeMainSessionAlias` in
  // `src/config/sessions/main-session.ts`), so seeding with it routes the
  // next message back to the main session.
  //
  // The guard now runs through `canonicalizeSessionKeyForRehydrate` in
  // `app-settings.ts`, which is also called from `applySettings` and
  // `applySettingsFromUrl` so every rehydrate path is protected
  // (`settings.sessionKey`, `settings.lastActiveSessionKey`, the
  // `chatOpenTabs` array, and the `?session=` URL param).
  @state() sessionKey = canonicalizeSessionKeyForRehydrateInternal(this.settings.sessionKey);
  @state() chatLoading = false;
  /** Covers the transcript pane until the selected chat's history lands. */
  @state() chatTranscriptLoading = false;
  /** Warm = cached transcript painted; cold = skeleton while fetching. */
  @state() chatHydrationMode: "idle" | "warm" | "cold" = "idle";
  /**
   * JAISIU-3496 — programmatic-scroll guard. Set to `true` while
   * `withProgrammaticScroll` brackets a `scrollTop` write that the
   * scroll handler in `app-scroll.ts` should classify as a baseline
   * adjustment (NOT a user gesture), so the up/down chevron
   * accumulators don't trigger on the resulting `scroll` event.
   */
  @state() chatProgrammaticScroll = false;
  /** Outgoing session transcript kept visible during cold hydration. */
  @state() chatHydrationOutgoing: { sessionKey: string; messages: unknown[] } | null = null;
  @state() chatHistoryHydrationError: string | null = null;
  /** Brief highlight on the scroll-restored anchor message (warm return). */
  @state() chatScrollHighlightMessageId: string | null = null;
  chatHistoryGeneration = 0;
  /**
   * JAISIU-3307 — currently-running `loadChatHistory` abort handle.
   * `activateChatSession` fires `controller.abort()` on session
   * switch so the in-flight chat.history from the previous session
   * skips its mutation branch instead of painting chat-A's transcript
   * onto chat-B's view. Not a `@state()` — Lit does not need to
   * re-render when this slot flips.
   */
  chatHistoryAbort: AbortController | null = null;
  /**
   * Fleet Control chat staleness verification. Not `@state()` — Lit does
   * not need to re-render when a clock ms advances; the staleness helper
   * is recomputed on each render via a 1s tick. Stamped by
   * `loadChatHistory`, `handleChatEvent`, and `app-gateway.ts` close/open.
   */
  chatSync: {
    lastSyncedAt: number | null;
    lastEventAt: number | null;
    lastWsCloseAt: number | null;
  } = {
    lastSyncedAt: null,
    lastEventAt: null,
    lastWsCloseAt: null,
  };
  /** Interval handle for the 1s staleness pill tick. Cleared on disconnect. */
  chatStalenessTicker: number | null = null;
  pendingUserMessageIds: string[] = [];
  /** Session Dialogue Spine — per-session transcript + pending-id cache. */
  private readonly chatSessionStore = new ChatSessionStore();

  /** Shared with `controllers/chat.ts` via `wireChatControllerState`. */
  readonly chatMessagesBySession: import("./chat/chat-messages-by-session.js").ChatMessagesBySessionCache =
    this.chatSessionStore.messagesBySession;

  /** Shared with `controllers/chat.ts` via `wireChatControllerState`. */
  readonly pendingUserMessageIdsBySession: Map<string, string[]> =
    this.chatSessionStore.pendingIdsBySession;
  sessionBackgroundRuns: Set<string> = new Set();
  @state() sessionBackgroundRunIds: string[] = [];
  @state() chatSending = false;
  @state() chatMessage = "";
  /** Staged user-message edit (rewind happens on send after confirm). */
  @state() chatPendingMessageEdit: ChatPendingMessageEdit | null = null;
  @state() chatEditRewindConfirm: ChatEditRewindConfirmPayload | null = null;
  @state() sessionsDeleteConfirm: import("./controllers/sessions.ts").SessionsDeleteConfirm | null =
    null;
  @state() sessionsDeleteAllConfirm:
    | import("./controllers/sessions.ts").SessionsDeleteAllConfirm
    | null = null;
  @state() sessionsRowsForDeleteAll: import("./types.ts").GatewaySessionRow[] = [];
  /** Effective composer mode for the active send (mirrors settings + per-session). */
  @state() chatComposerMode = this.settings.chatComposerMode;
  /** Send with @all delegation (Fleet Control composer). */
  @state() chatSendOrchestrateAll = false;
  /** Create a new project + forked session before sending. */
  @state() chatSendCreateNewProject = false;
  /** Self improvement: YouTrack task + code-fix worker + feature-ship (no @all). */
  @state() chatSendSelfImprovement = this.settings.chatSendSelfImprovement ?? false;
  /** Repo checkout path for code-fix worker while Self improvement is checked (UI preview only). */
  @state() chatSelfImprovementRepoWorkspace: string | null = null;
  /** Existing project from this chat to send into (mutually exclusive with create new). */
  @state() chatSendProjectId: string | null = null;
  @state() chatHistorySearchQuery = "";
  // JAISIU-3655 (followup) — sidebar-side counterpart of
  // `chatMessageSearchInActivities`. Default OFF — the sidebar
  // row filter does not look at body text inside the activity
  // section. Surfaced through a gear-icon popover next to the ×
  // in the sidebar search input.
  @state() chatHistorySearchInActivities = false;
  /**
   * JAISIU-3653 — one-shot jump target the sidebar wants the next
   * session switch to land on. Populated by
   * `handleOpenChatHistorySession` when the operator clicks a
   * filtered sidebar row, consumed (and cleared) by
   * `activateChatSession` once the chat has opened + anchored. A
   * non-null value here arms `restorePersistedScrollAnchor` for
   * the active session with a synthetic anchor pointing at the
   * matched message, so the existing centered-fetch + scroll
   * pipeline (JAISIU-3533) does the heavy lifting (including the
   * `around=<messageId>` fallback for matches in older batches).
   *
   * Cleared on every successful activateChatSession entry so the
   * field never leaks across unrelated navigation. The captured
   * outgoing chat's anchor is preserved separately by the
   * existing `captureScrollAnchorForSessionSwitchOut` path, so
   * pressing Esc / clearing the search and tabbing back lands the
   * operator on the chat they were reading, not on the matched row.
   */
  @state() chatHistoryPendingJumpToMessageId: string | null = null;
  /**
   * JAISIU-3653 — when a sidebar click jump lands on a chat row,
   * this carries the same `messageId` so the chat view can paint a
   * transient focus highlight (the "you landed here" pulse) on the
   * matched row. Cleared on a timer after the row has been focused,
   * or on the next session switch / search-clear / scroll away,
   * whichever comes first. The visual focus is independent of the
   * scroll anchor — the anchor places the viewport, this field
   * draws the eye to the row.
   */
  @state() chatHistoryJumpFocusMessageId: string | null = null;
  @state() chatHistoryShowArchived = false;
  /** JAISIU-3500: show chat preview snippets under each sidebar row (off by default). */
  @state() chatHistoryShowPreviews = false;
  /** JAISIU-3327 / UX-R2 — bulk-select mode toggle (local). */
  @state() chatHistorySelectMode = false;
  /** JAISIU-3327 / UX-R2 — keys currently checked in select mode. */
  @state() chatHistorySelectedKeys: string[] = [];
  /** JAISIU-3327 / UX-R2 — all keys visible to the sidebar right now. */
  @state() chatHistoryAllSelectableKeys: string[] = [];
  /** JAISIU-3327 / UX-R2 — cached GatewaySessionRow[] for the bulk-delete confirm. */
  @state() chatHistoryBulkDeleteRows: import("./types.ts").GatewaySessionRow[] = [];
  /** JAISIU-3328 / UX-R3 — favorited (pinned) chat keys. Hydrated from storage. */
  @state() chatHistoryFavoriteKeys: Set<string> = new Set(loadFavoriteChats());
  @state() chatMessages: unknown[] = [];
  @state() chatToolMessages: unknown[] = [];
  @state() chatStream: string | null = null;
  @state() chatReasoningStream: string | null = null;
  @state() chatLiveRunHint: string | null = null;
  @state() chatInjectedAt: number | null = null;
  @state() chatCollectQueueDepth = 0;
  /**
   * Sticky fallback for `chatLiveRunHint`. Per-session so it auto-resets
   * on session switch. JAISIU-3153.
   */
  @state() stickyLiveRunHintBySession: Record<string, string | null> = {};
  /**
   * JAISIU-3503 — Transient operator-facing feedback line that surfaces
   * in the chat composer after the model has been stopped via the Stop
   * button, Stop run footer pill, or `/stop` slash command. Auto-clears
   * after {@link CHAT_STOP_TOAST_MS} or on the next draft change so it
   * never lingers past the user's next action. `null` means "no toast
   * currently rendered". Per-session so a switch to another tab while
   * the toast is still on the timer does not flash it onto the new
   * tab — it is owned by the session that triggered the abort.
   */
  @state() chatStopToastBySession: Record<string, string | null> = {};
  private chatStopToastTimers: Map<string, number> = new Map();
  @state() chatActiveFileToolPath: string | null = null;
  @state() chatFileDiffOpen: { changeId: string; path: string } | null = null;
  @state() chatFileDiffView: "diff" | "file" = "diff";
  @state() runArtifactPreviewsBySession: Record<
    string,
    import("./controllers/run-artifact-previews.ts").RunArtifactPreviewEntry[]
  > = {};
  @state() chatActivityPanelCollapsed = false;
  @state() chatHistoryDisplayLimit = 200;
  // JAISIU-3473 — upward-pagination cursor.
  // `chatHistoryOldestLoadedId` is the id of the OLDEST message currently
  // rendered (or null when no history has been loaded yet). The client
  // sends it back as `before` to request the next older page.
  // `chatHistoryHasMoreOlder` is true when the server has more older
  // messages than what the client currently holds. When false, the
  // scroll-up pager stops firing.
  // `chatHistoryLoadingOlder` is the in-flight guard — only one older
  // request may be active at a time (rapid scroll events are coalesced).
  @state() chatHistoryOldestLoadedId: string | null = null;
  @state() chatHistoryHasMoreOlder = false;
  // JAISIU-3536 — newest-direction cursor (mirror of the older pair).
  // `chatHistoryNewestLoadedId` is the id of the newest message
  // currently rendered, sent back as `after` on the next newer-page
  // request. `chatHistoryHasMoreNewer` is true iff a saved anchor
  // exists AND there are messages below the rendered window.
  @state() chatHistoryNewestLoadedId: string | null = null;
  @state() chatHistoryHasMoreNewer = false;
  @state() chatHistoryLoadingOlder = false;
  // JAISIU-3536 — in-flight guards. The older-direction guard
  // (`chatHistoryLoadingOlder`) was already in place for JAISIU-3473;
  // 3536 adds the newer and centered variants so a duplicate
  // `loadNewerChatHistory` / centered-restore call awaits the
  // in-flight promise instead of issuing a fresh request.
  @state() chatHistoryLoadingNewer = false;
  @state() chatHistoryLoadingCentered = false;
  // JAISIU-3536 — non-blocking error toast for a failed history
  // fetch. Set on throw inside `loadOlderChatHistory` /
  // `loadNewerChatHistory` / centered-restore; cleared on the next
  // successful fetch or when the operator dismisses via Retry.
  @state() chatHistoryError: {
    direction: "older" | "newer" | "centered";
    cursorKey: string;
    message: string;
    at: number;
  } | null = null;
  // JAISIU-3533 — authoritative scroll-anchor schema (replaces JAISIU-3474).
  //   `viewportOffset`     px from the top of the message element to the top
  //                        of the viewport at capture time.
  //   `capturedAt`         Date.now() at capture — diagnostic only.
  //   `transcriptRevision` monotonic counter for the slice used at capture.
  // In-memory mirror of the IDB record for the ACTIVE session only;
  // captured on every scroll event after a 150 ms idle debounce, plus
  // synchronously on session switch-out of the outgoing chat. Per-session
  // map (`chatScrollAnchorBySession`) lives alongside it for the cache-hit
  // restore path (JAISIU-3533 #4) and the session-switch-out capture.
  @state() chatScrollAnchor: import("./storage/idb-session-cache.ts").IdbScrollAnchorEntry | null =
    null;
  // JAISIU-3533 — per-session scroll-anchor mirror. Keys are sessionKey
  // (trimmed), values are the full IdbScrollAnchorEntry shape. This is
  // the authoritative source for cross-session restoration; the
  // single-value `chatScrollAnchor` above is the active-session cache
  // for fast re-reads in the same render.
  @state() chatScrollAnchorBySession: Map<
    string,
    import("./storage/idb-session-cache.ts").IdbScrollAnchorEntry
  > = new Map();
  // JAISIU-3533 — monotonic counter incremented on every chat history
  // commit. Stored alongside the anchor so the restore path can refuse
  // an anchor that points at a stale transcript slice (the row was
  // deleted or reordered by a later server response). The counter
  // resets to 0 on session switch; the value is opaque to the server.
  @state() chatScrollTranscriptRevision: string = "0";
  // True while we owe a scroll restore after the next history commit.
  // Set when a saved anchor for the active session is loaded from IDB
  // at boot. The bootstrap clears it once the viewport has been
  // placed (or after a short TTL if no anchor row could be found).
  @state() chatScrollRestorationPending = false;
  // JAISIU-3533 — debounce handle for the IDB scroll-anchor save.
  // We never want to write to IDB on every scroll event, so the
  // handler schedules a single write 150 ms after the last scroll
  // (tightened from the 3474-era 250 ms so streaming-token appends
  // do not silently land the operator mid-message after a stop).
  @state() chatScrollAnchorSaveTimer: number | null = null;
  /**
   * JAISIU-3653 — timer handle for the sidebar-click jump-focus
   * pulse. Cleared on the next jump (so rapid A→B clicks always
   * paint on the new target) and on session switch / host
   * disconnect (so a stale timer does not run its cleanup under
   * the wrong chat).
   */
  chatHistoryJumpFocusTimer: number | null = null;
  @state() chatMessageSearchQuery = "";
  // JAISIU-3655 (followup) — operator toggle: should the
  // in-thread highlight paint marks INSIDE the assistant-turn
  // activity section (`[data-testid="chat-turn-activity"]` —
  // the collapsible "▸ Activity · N steps" panel that holds tool
  // calls, command outputs, reasoning chunks)? Default OFF — the
  // visible chat text and the final answer are highlighted, the
  // activity section is skipped. Surfaced through a gear-icon
  // popover next to the in-thread nav (and next to the × in the
  // sidebar search).
  @state() chatMessageSearchInActivities = false;
  // JAISIU-3655 — total match count for the chat-thread search input.
  // Updated by `handleChatMessageSearchQueryChange` (live cache pass)
  // and by `handleChatMessageDeepBatchResult` (deep-batch pass). The
  // chat view reads this prop to render the "X / Y" counter.
  @state() chatMessageSearchTotal = 0;
  // JAISIU-3655 — 0-based index of the currently-active match. The
  // counter shows +1 of this for human display.
  @state() chatMessageSearchCurrentIndex = 0;
  // JAISIU-3655 (followup) — "should the next highlight pass
  // also smooth-scroll the active match into view?". Set to
  // true when the operator has just established a new search
  // ("" → non-empty) or pressed ↑/↓; reset to false once the
  // scroll runs. The flag exists so other reactive ticks
  // (transcript updates from streaming, deep-batch results,
  // activity-toggle flip) re-paint the active match WITHOUT
  // moving the viewport — the operator is mid-read in those
  // cases and a surprise scroll would feel like the page
  // jumped.
  @state() chatMessageSearchFocusOnNextPaint = false;
  // JAISIU-3656 — last deep-batch search result. Indexed by
  // messageId so navigation can resolve deep matches back to their
  // rows. Cleared when the query is cleared or the session switches.
  @state() chatThreadDeepBatchResult:
    | import("./chat/chat-thread-search-deep-batch.ts").DeepBatchSearchResult
    | null = null;
  // JAISIU-3656 — the message ids the host knows how to navigate to,
  // in counter order (live matches first, then deep matches in
  // oldest-first order). Recomputed whenever the deep-batch result
  // changes or the chat session switches.
  @state() chatMessageSearchMatchIds: string[] = [];
  // JAISIU-3655 — DOM handles for the `<mark class="chat-search-hit">`
  // nodes the host painted into the rendered thread during the most
  // recent highlight pass. Held in plain (non-reactive) state because
  // DOM elements are not JSON-serialisable — we never want Lit to
  // dirty-trigger a re-render just because the highlight pass
  // resolved a new marks array. Re-built on every query change AND
  // every transcript change by `runChatMessageSearchHighlightPass`
  // from `updated()`. Indexed off `chatMessageSearchCurrentIndex` to
  // drive the `.chat-search-hit--active` modifier + smooth-scroll
  // via `setActiveMatch` / `scrollToMatch`.
  private chatMessageSearchMarks: HTMLElement[] = [];
  // JAISIU-3657 — ephemeral scroll anchor captured at the moment the
  // operator starts an in-thread search (empty→non-empty transition).
  // Cleared on session switch and on clear/Esc. NEVER persisted to
  // IndexedDB — it lives only for the duration of the search session.
  // We use it to restore the exact pre-search reading position on
  // Esc/clear, even when `loadOlderChatHistory` prepended older
  // batches during deep-match navigation and shifted the anchor row's
  // `offsetTop`. See `chat/chat-thread-search-restore.ts`.
  @state() chatThreadSearchAnchor: ChatThreadSearchAnchor | null = null;
  @state() chatFileContextIndex = 0;
  @state() chatFolderContextIndex = 0;
  @state() chatPendingReviewChangeIds: string[] = [];
  @state() chatComposerFileReviewError: string | null = null;
  /** Last completed run file edits per session (composer dock until next run). */
  @state() chatLastRunBySession: Record<
    string,
    import("./chat/chat-last-run-files.ts").ChatLastRunSnapshot
  > = {};
  @state() chatFileDiffSplitView = false;
  @state() chatArtifactReviewBusy = false;
  @state()
  chatCheckpoints: import("./controllers/session-artifacts-review.ts").ArtifactCheckpoint[] = [];
  @state() chatCheckpointsLoading = false;
  @state() chatCheckpointsCollapsed = true;
  /** JAISIU-3551 — set of chat message ids currently in the manually-collapsed
   * state (avatar-driven toggle). Lives in app state so it survives re-renders
   * and session switches (we don't drop it on chat-switch; the id is unique
   * enough that a stale entry is harmless). */
  @state() chatCollapsedMessageIds: Set<string> = new Set();
  @state() fleetTodosBusy = false;
  @state() chatMainRun: ChatSubagentCard | null = null;
  @state() orchestrationReportsDone: Record<string, boolean> = {};
  /** Dedupes injected “all tasks complete” notices per session + fleet fingerprint. */
  @state() fleetCompletionNotifiedFingerprint: Record<string, string> = {};
  /** Per session: fingerprint of plan reply where user dismissed the Implement offer. */
  @state() planImplementDismissedFingerprint: Record<string, string> = {};
  /** Per-session run start timestamp for artifact file-change summaries. */
  @state() chatRunArtifactsSinceTs: Record<string, number> = {};
  /** JAISIU-3327 / UX-R-XXXX — runIds aborted via Stop button (drop trailing chat.final). */
  @state() abortedRunIds: Set<string> = new Set();
  /** Dedupes injected per-session file-change notices. */
  @state() fileChangesNotifiedFingerprint: Record<string, string> = {};
  /** Suppresses fleet chat.inject side-effects while switching sessions / reloading history. */
  @state() chatFleetAutoInjectHeld = false;
  /** Dedupes injected fleet rollup file-change notices. */
  @state() fleetFileChangesNotifiedFingerprint: Record<string, string> = {};
  @state() chatMainRunExpanded: boolean | undefined = false;
  @state() chatStreamStartedAt: number | null = null;
  /** Bumped every ~200ms while waiting for an agent reply (live wait counter). */
  @state() chatWaitNow = Date.now();
  chatWaitTicker: number | null = null;
  @state() chatRunId: string | null = null;
  @state() compactionStatus: CompactionStatus | null = null;
  @state() chatAvatarUrl: string | null = null;
  @state() chatThinkingLevel: string | null = null;
  @state() chatQueue: ChatQueueItem[] = [];
  @state() chatSubagents: ChatSubagentCard[] = [];
  @state() fleetSessionActivity:
    | import("./controllers/fleet-work-plan.ts").FleetSessionActivityView
    | null = null;
  @state() chatActivityTab: import("./views/chat-activity-panel.ts").ChatActivityTab = "all";
  @state() chatActivitySearchQuery = "";
  @state() fleetV2AnalyticsTab: import("./views/fleet-v2-analytics-panel.ts").FleetV2AnalyticsTab =
    "activity";
  @state() fleetV2DirectoryTab: import("./views/fleet-v2-agent-directory.ts").FleetV2DirectoryTab =
    "map";
  @state() fleetResetNodeBusy = false;
  @state() sessionTabActivity: import("./chat/session-tab-activity.ts").SessionTabActivityMap = {};
  /** Expanded state for in-thread subagent run rows (keyed by card id). */
  @state() chatSubagentExpanded: Record<string, boolean> = {};
  /**
   * JAISIU-XXXX — ids of *terminal* subagent rows the operator has explicitly
   * opened in the current session. Terminal runs default-collapsed; this set
   * is the only way to expand one. Cleared on session reset.
   */
  @state() chatSubagentTerminalOpen: Record<string, boolean> = {};
  @state() chatSubagentFilter: "all" | "running" = "all";
  /**
   * Parent → children hierarchy: stable id of the subagent card whose
   * descendants should be highlighted in the Subagents tab and the
   * in-thread card list. `null` means no parent is currently selected
   * (the default). Toggled by the "Focus children" button on a parent
   * card; cleared by clicking the same button again or by switching
   * chat sessions.
   */
  @state() selectedSubagentParentId: string | null = null;
  /**
   * JAISIU-3319 / Pillar H: per-card "cancel" / "retry" inflight bits.
   * Drives the row's `disabled` state so a double-click can't double-fire
   * the RPC; cleared by the lifecycle handler when the cancel-side
   * chat.final lands (or when the retry spawn resolves).
   */
  @state() chatSubagentPendingActions: Record<string, "cancel" | "retry"> = {};
  /**
   * Fan-in / synthesis lifecycle per parent session key.
   *
   * Keyed by session rather than held as a single value so navigating to
   * another chat and back restores the fan-out's synthesis state instead of
   * resetting it to "never ran".
   */
  @state() fanInSynthesisBySession: Record<
    string,
    import("./chat/fan-in-state.ts").FanInSynthesisRecord
  > = {};
  /** In-flight flags for the fan-in bar, per parent session key. */
  @state() fanInPendingBySession: Record<string, { synthesizing?: boolean; cancelling?: boolean }> =
    {};
  /** Last fan-in action error per parent session key. */
  @state() fanInErrorBySession: Record<string, string | undefined> = {};
  /** Highlighted row in the @-mention picker; clamped against the filtered list. */
  @state() chatMentionIndex = 0;
  @state() chatSlashCommandIndex = 0;
  /** Parent session + label when a subagent was opened in its own tab. */
  @state() chatTabMeta: Record<string, ChatSessionContextMeta> = {};
  /** Brief highlight on the tab row after opening a subagent session. */
  @state() chatTabHighlightKey: string | null = null;
  private chatTabHighlightTimer: number | null = null;
  @state() chatWorkspaceLoading = false;
  @state() chatWorkspaceError: string | null = null;
  @state() chatWorkspaceAgentId: string | null = null;
  @state() chatWorkspacePath: string | null = null;
  @state() chatWorkspaceScope: "sandbox" | "session" | null = null;
  @state() chatWorkspaceTruncated = false;
  @state() chatWorkspaceTree: WorkspaceTreeNode[] = [];
  @state() chatWorkspaceFileTargets: Record<
    string,
    import("./chat/fleet-session-files-tree.ts").FleetFileReadTarget
  > = {};
  @state() chatWorkspaceSelectedPath: string | null = null;
  @state() chatWorkspaceFileLoading = false;
  @state() chatWorkspaceFileError: string | null = null;
  @state() chatWorkspaceFileContent: string | null = null;
  @state() chatWorkspaceFileTruncated = false;
  @state() chatWorkspaceExpandedDirs: Record<string, boolean> = {};
  @state() chatContextPanelOpen = false;
  /** JAISIU-3588 — composer `+` popover open state. Lives on the host
   *  (not the props surface) so Lit re-renders on toggle, focus returns
   *  to the trigger on Esc, and outside-click closes cleanly. */
  @state() chatComposerPlusOpen = false;
  /** Focus index inside the `+` popover (0..4) for keyboard nav. */
  @state() chatComposerPlusFocusIndex = 0;
  @state() chatContextBreakdownLoading = false;
  @state() chatContextBreakdownError: string | null = null;
  @state() chatContextBreakdown: SessionContextBreakdown | null = null;
  @state() chatPalaceContinuityOpen = false;
  @state() chatPalaceContinuityLoading = false;
  @state() chatPalaceContinuityClearing = false;
  @state() chatPalaceContinuityMutating = false;
  /**
   * In-flight `sessions.palaceContinuity.add` request markers (one per
   * active RPC). The dropdown reads `chatPalaceContinuityPending.size`
   * to render a `Saving… (n)` pill while writes are pending. The Set
   * carries opaque local markers — the gateway schema for
   * `sessions.palaceContinuity.add` is strict and does not accept a
   * wire-level idempotency key, so this is NOT a server-dedupe store.
   */
  @state() chatPalaceContinuityPending = new Set<string>();
  /**
   * JAISIU-3327 — last time the chat-poll cadence fetched Palace
   * continuity. The poll uses this as a throttle (min 8s between fetches)
   * so we don't hammer the gateway between chat-history polls.
   * Reset on session change so the next switch re-fetches immediately.
   */
  @state() chatPalaceContinuityLastPollAt = 0;
  /**
   * JAISIU-3327 — per-session cache of Palace continuity snapshots.
   * Bounded (32 entries) to prevent unbounded growth in long-running
   * sessions that toggle between many child sessions. The
   * `chatPalaceContinuityCacheLimit` field tunes the cap from settings.
   */
  @state() chatPalaceContinuityCache = new Map<
    string,
    import("./controllers/chat-palace-continuity.ts").PalaceContinuitySnapshot
  >();
  @state() chatPalaceContinuityCacheLimit = 32;
  @state() chatPalaceContinuityError: string | null = null;
  @state() chatPalaceContinuity: PalaceContinuitySnapshot | null = null;
  @state() chatPalaceContinuityFlash = false;
  @state() chatPalaceContinuityDraft = "";
  @state() chatPalaceContinuityDraftCategory: "fact" | "decision" = "fact";
  @state() fleetProjectsLoading = false;
  @state() fleetProjectsError: string | null = null;
  @state() fleetProjects: FleetProjectSummary[] = [];
  @state() chatActiveProjectId: string | null = null;
  @state() sessionInspectorTab: import("./views/fleet-session-inspector.ts").SessionInspectorTab =
    "delegation";
  @state() sessionInspectorFilesScopeKey: string | null = null;
  @state() sessionArtifactsLoading = false;
  @state() sessionArtifactsError: string | null = null;
  @state() sessionArtifacts: import("./controllers/session-artifacts.ts").SessionArtifactRecord[] =
    [];
  @state() sessionArtifactSelectedId: string | null = null;
  @state() sessionArtifactDetailLoading = false;
  @state() sessionArtifactDetail:
    | import("./controllers/session-artifacts.ts").SessionArtifactDetail
    | null = null;
  @state() sessionArtifactEditContent = "";
  @state() sessionArtifactWriteSaving = false;
  /**
   * Inbound attachments for the open session, rendered above project changes
   * in the Files tab. Seeded from the upload response so a file the operator
   * just added appears without a Refresh click.
   */
  @state() sessionUploads: import("./controllers/session-uploads.ts").SessionUploadsState =
    emptySessionUploadsState();
  @state() sessionInspectorLogs: import("./views/usage.ts").SessionLogEntry[] | null = null;
  @state() sessionInspectorLogsLoading = false;
  @state() gatewayEventTail: import("./app-events.ts").EventLogEntry[] = [];
  @state() sessionEnvironmentLoading = false;
  @state() sessionEnvironmentError: string | null = null;
  @state() sessionEnvironment: import("./types.ts").SessionEnvironmentExplainResult | null = null;
  @state() sessionEnvironmentRecreating = false;
  @state() sessionPreviewPort = 5173;
  @state() sessionPreviewRegistering = false;
  /** Derived agent roster for the chat view */
  @state() agentRoster: AgentRosterItem[] = [];
  @state() flociAgents: import("./controllers/floci.ts").FlociAgentRecord[] = [];
  @state() agentFleetGraphLoading = false;
  @state() agentFleetGraphRefreshing = false;
  @state() agentFleetGraphError: string | null = null;
  @state() agentFleetGraph: AgentFleetGraphSnapshot | null = null;
  @state() agentsAddModalOpen = false;
  @state() agentsAddModalSaving = false;
  @state() agentsAddModalError: string | null = null;
  @state() agentsAddDraft = { id: "", name: "", workspace: "", setDefault: false };
  @state() fleetGraphEditorOpen = false;
  @state() fleetGraphEditorMode: FleetGraphEditorMode = "agent";
  @state() fleetGraphEditorTab: FleetGraphEditorTab = "topology";
  @state() fleetGraphEditorAgentId: string | null = null;
  @state() fleetGraphEditorSaving = false;
  @state() fleetGraphEditorError: string | null = null;
  @state() fleetGraphEditorAgentDraft: FleetGraphAgentDraft = {
    ...EMPTY_FLEET_GRAPH_AGENT_DRAFT,
  };
  @state() fleetGraphEditorConfigDraft: FleetGraphConfigDraft = {
    ...EMPTY_FLEET_GRAPH_CONFIG_DRAFT,
  };
  @state() fleetGraphEditorBehaviorDraft: FleetGraphBehaviorDraft = {
    ...EMPTY_FLEET_GRAPH_BEHAVIOR_DRAFT,
  };
  @state() fleetGraphEditorBindings: FleetGraphBindingDraft[] = [];
  @state() fleetGraphEditorAgentIds: string[] = [];
  @state() fleetGraphEditorConfigSnapshot: Record<string, unknown> | null = null;
  @state() fleetGraphEditorEffectiveModel = "—";
  @state() fleetGraphConfigCache: Record<string, unknown> | null = null;
  @state() fleetGuideOpen = false;
  @state() fleetGuideTab: "components" | "example" | "architecture" = "components";
  @state() chatAttachments: ChatAttachment[] = [];
  @state() chatModelCatalog: GatewayModelChoice[] = [];
  @state() chatModelCatalogLoading = false;
  @state() chatModelCatalogError: string | null = null;
  @state() chatModelPatching = false;
  // Sidebar state for tool output viewing
  @state() sidebarOpen = false;
  @state() mobileNavMoreOpen = false;
  @state() sidebarContent: string | null = null;
  @state() sidebarError: string | null = null;
  @state() splitRatio = this.settings.splitRatio;

  /** Long message viewer modal state. */
  @state() messageViewerOpen = false;
  @state() messageViewerMarkdown: string | null = null;
  @state() messageViewerMessageId: string | null = null;
  @state() messageViewerSummary = "";
  @state() messageViewerNoteDraft = "";
  @state() messageViewerNoteSaved: ViewerNoteRecord | null = null;
  @state() messageViewerJustSavedAt: number | null = null;

  /** UX-R7 / JAISIU-3297 — global search modal state. Cmd/Ctrl+K toggles. */
  @state() globalSearchOpen = false;
  @state() globalSearchQuery = "";
  @state() globalSearchQueryDraft = "";
  @state() globalSearchResults: GlobalSearchResult[] = [];
  @state() globalSearchLoading = false;
  @state() globalSearchError: string | null = null;
  /**
   * JAISIU-3652 — true while the deep-batch walker is paging
   * older, non-rendered transcripts. The modal can show this as
   * a quiet "searching deeper…" hint; the existing gateway spinner
   * stays in `globalSearchLoading`.
   */
  @state() globalSearchDeepLoading = false;

  @state() nodesLoading = false;
  @state() nodes: Array<Record<string, unknown>> = [];
  @state() nodesBusy: Set<string> = new Set();
  @state() devicesLoading = false;
  @state() devicesError: string | null = null;
  @state() devicesList: DevicePairingList | null = null;
  @state() execApprovalsLoading = false;
  @state() execApprovalsSaving = false;
  @state() execApprovalsDirty = false;
  @state() execApprovalsSnapshot: ExecApprovalsSnapshot | null = null;
  @state() execApprovalsForm: ExecApprovalsFile | null = null;
  @state() execApprovalsSelectedAgent: string | null = null;
  @state() execApprovalsTarget: "gateway" | "node" = "gateway";
  @state() execApprovalsTargetNodeId: string | null = null;
  @state() execApprovalQueue: ExecApprovalRequest[] = [];
  @state() execApprovalBusy = false;
  @state() execApprovalError: string | null = null;
  @state() fleetQuestionQueue: import("./controllers/fleet-question.ts").FleetQuestionRequest[] =
    [];
  @state() fleetQuestionBusy = false;
  @state() fleetQuestionError: string | null = null;
  @state() fleetQuestionDraft: Record<string, string[]> = {};
  @state() fleetQuestionFreeText: Record<string, string> = {};
  /** Last-submitted fleet-question answer, rendered as an echo chat
   * bubble below the question so the operator can confirm what they
   * selected (and what reached the gateway). Cleared on next question.
   * Shape is shared with `controllers/fleet-question.ts` so the view
   * and the writer in `app-fleet-interaction.ts` cannot drift — if
   * `options` / `allowMultiple` are missing per entry, the echo Lit
   * template throws `Cannot read properties of undefined` and freezes
   * the chat UI until the operator refreshes the page. */
  @state() fleetQuestionLastAnswer:
    | import("./controllers/fleet-question.ts").FleetQuestionLastAnswer
    | null = null;
  @state() fleetTodosBySession: Record<
    string,
    import("./controllers/fleet-todos.ts").FleetTodoItem[]
  > = {};
  @state() fleetTodosStripCollapsed = false;
  @state() pendingGatewayUrl: string | null = null;

  @state() configLoading = false;
  @state() configRaw = "{\n}\n";
  @state() configRawOriginal = "";
  @state() configValid: boolean | null = null;
  @state() configIssues: unknown[] = [];
  @state() configSaving = false;
  @state() configApplying = false;
  @state() updateRunning = false;
  // JAISIU-XXXX — guard `lastActiveSessionKey` against fork rehydrate.
  // The previous seed at this line copied the persisted key raw, so a
  // stale fork from a prior page load would slip through and seed
  // config-apply + chat-mount flows with a fork. The shared
  // `canonicalizeSessionKeyForRehydrate` helper also covers the
  // `?session=` URL param and the `chatOpenTabs` array.
  @state() applySessionKey = canonicalizeSessionKeyForRehydrateInternal(
    this.settings.lastActiveSessionKey,
  );
  @state() configSnapshot: ConfigSnapshot | null = null;
  @state() configSchema: unknown = null;
  @state() configSchemaVersion: string | null = null;
  @state() configSchemaLoading = false;
  @state() configUiHints: ConfigUiHints = {};
  @state() configForm: Record<string, unknown> | null = null;
  @state() configFormOriginal: Record<string, unknown> | null = null;
  @state() configFormDirty = false;
  @state() configFormMode: "form" | "raw" = "form";
  @state() configSearchQuery = "";
  @state() configActiveSection: string | null = null;
  @state() configActiveSubsection: string | null = null;

  @state() channelsLoading = false;
  @state() channelsSnapshot: ChannelsStatusSnapshot | null = null;
  @state() channelsError: string | null = null;
  @state() channelsLastSuccess: number | null = null;
  @state() whatsappLoginMessage: string | null = null;
  @state() whatsappLoginQrDataUrl: string | null = null;
  @state() whatsappLoginConnected: boolean | null = null;
  @state() whatsappBusy = false;
  @state() nostrProfileFormState: NostrProfileFormState | null = null;
  @state() nostrProfileAccountId: string | null = null;

  @state() presenceLoading = false;
  @state() presenceEntries: PresenceEntry[] = [];
  @state() presenceError: string | null = null;
  @state() presenceStatus: string | null = null;

  @state() networkLoading = false;
  @state() networkInstances: NetworkInstance[] = [];
  @state() networkError: string | null = null;
  @state() networkStatus: string | null = null;
  @state() networkSelectedId: string | null = null;
  @state() networkComparingIds: string[] = [];
  @state() networkComparison: import("../../../src/network/types.ts").InstanceComparison | null =
    null;
  @state() networkComparisonLoading = false;
  @state() networkComparisonError: string | null = null;

  @state() agentsLoading = false;
  @state() agentsList: AgentsListResult | null = null;
  @state() agentsError: string | null = null;
  @state() agentsSelectedId: string | null = null;
  @state() agentsPageTab: import("./views/agents.ts").AgentsPageTab = "roles";
  @state() fleetRoleEditor: FleetRoleEditorState = emptyFleetRoleEditorState();
  @state() llmConnect: LlmConnectState = emptyLlmConnectState();
  @state() fleetExecutionExpandedId: string | null = null;
  @state() agentsQueueStatus: import("./types.js").AgentQueueStatus | null = null;
  @state() agentsQueueLoading = false;
  @state() agentsQueueError: string | null = null;
  @state() agentsConcurrencyValue = 8;
  @state() queueServiceStatus: import("./types.js").QueueServiceStatus | null = null;
  @state() queueServiceLoading = false;
  @state() queueServiceError: string | null = null;
  @state() queueRestartLoading = false;
  @state() queueRestartError: string | null = null;
  @state() youtrackLoading = false;
  @state() youtrackSearchQuery = "#Unresolved";
  @state() youtrackSearchProject = "JAISIU";
  @state() youtrackResults: YouTrackIssue[] = [];
  @state() youtrackSelectedIssue: YouTrackIssue | null = null;
  @state() youtrackError: string | null = null;
  @state() youtrackCreateDraft: import("./controllers/youtrack.ts").YouTrackCreateDraft | null =
    null;
  @state() youtrackCreateOpen = false;
  @state() youtrackCreateError: string | null = null;
  @state() youtrackComments: import("./controllers/youtrack.ts").YouTrackComment[] = [];
  @state() youtrackCommentsLoading = false;
  @state() youtrackSavedFilters: import("./controllers/youtrack.ts").YouTrackSavedFilter[] = [];
  @state() agentsConcurrencyDirty = false;
  @state() agentsConcurrencySaving = false;
  @state() agentsImportSessionKey = "";
  @state() agentsImportLoading = false;
  @state() agentsImportError: string | null = null;
  @state()
  agentsImportCandidates: import("./chat/discover-workspace-agents.ts").WorkspaceAgentCandidate[] =
    [];
  @state() agentsImportSelectedIds: string[] = [];
  @state() agentsImportWorkspacePath: string | null = null;
  @state() agentsImportBusy = false;
  @state() agentsImportHistorySearchQuery = "";
  @state() agentsImportHistoryShowArchived = false;
  @state() agentsPanel: import("./views/agents.ts").AgentsPanel = "overview";
  @state() workPlansLoading = false;
  @state() workPlansError: string | null = null;
  @state() workPlans: import("./types.js").FleetWorkPlanSummary[] = [];
  @state() workPlanYoutrackConfigured = false;
  @state() workPlanSelectedId: string | null = null;
  @state() workPlanDetail: import("./types.js").FleetWorkPlanRecord | null = null;
  @state() workPlanDetailLoading = false;
  @state() workPlanBusy = false;
  @state() workPlanOpenDecisions: import("./types.js").FleetDecisionItem[] = [];
  @state() workPlanBibleSummary: import("./types.js").BibleFinalSummary | null = null;
  @state() workPlanNeedsDecisionOnly = false;
  @state() workPlanExpandedRoleIds: Set<string> = new Set();
  workPlanPollInterval: number | null = null;
  @state() workPlanCreateGoal = "";
  @state() workPlanCreateProject = "JAISIU";
  @state() workPlanCreateParentSessionKey = "";
  @state() workPlanCreateTargetBranch = "";
  @state() workPlanBranches: string[] = [];
  @state() workPlanBranchesLoading = false;
  @state() workPlanBranchesError: string | null = null;
  @state() workPlanSessionLogs: Record<
    string,
    import("./fleet/fleet-plan-session-logs.ts").FleetSessionLogLine[]
  > = {};
  @state() agentFilesLoading = false;
  @state() agentFilesError: string | null = null;
  @state() agentFilesList: AgentsFilesListResult | null = null;
  @state() agentFileContents: Record<string, string> = {};
  @state() agentFileDrafts: Record<string, string> = {};
  @state() agentFileActive: string | null = null;
  @state() agentFileSaving = false;
  @state() agentIdentityLoading = false;
  @state() agentIdentityError: string | null = null;
  @state() agentIdentityById: Record<string, AgentIdentityResult> = {};
  @state() agentSkillsLoading = false;
  @state() agentSkillsError: string | null = null;
  @state() agentSkillsReport: SkillStatusReport | null = null;
  @state() agentSkillsAgentId: string | null = null;

  @state() sessionsLoading = false;
  @state() sessionsResult: SessionsListResult | null = null;
  @state() sessionsResultAgentId: string | null = null;
  @state() sessionsError: string | null = null;
  @state() sessionsFilterActive = "";
  @state() sessionsFilterLimit = "120";
  @state() sessionsIncludeGlobal = true;
  @state() sessionsIncludeUnknown = false;
  @state() sessionsSearchQuery = "";
  @state() sessionHistoryPanelKey: string | null = null;
  @state() sessionHistoryMessages: unknown[] = [];
  @state() sessionHistoryLoading = false;
  @state() sessionHistoryError: string | null = null;

  @state() usageLoading = false;
  @state() usageResult: import("./types.js").SessionsUsageResult | null = null;
  @state() usageCostSummary: import("./types.js").CostUsageSummary | null = null;
  @state() usageError: string | null = null;

  @state() spendLoading = false;
  @state() spendResult: import("./types.js").NetworkSpendResult | null = null;
  @state() spendError: string | null = null;

  @state() localResourcesLoading = false;
  @state() localResourcesSnapshot: import("./types.js").LocalResourcesSnapshot | null = null;
  @state() localResourcesError: string | null = null;
  @state() localResourcesHistory: import("./types.js").LocalResourcesHistoryPoint[] = [];
  @state() localResourcesAutoRefresh = true;

  @state() localLlmLoading = false;
  @state() localLlmError: string | null = null;
  @state() localLlmSnapshot: import("./types.js").LocalResourcesSnapshot | null = null;
  @state() localLlmPrevSnapshot: import("./types.js").LocalResourcesSnapshot | null = null;
  @state() localLlmHistory: import("./types.js").LocalResourcesHistoryPoint[] = [];
  @state() localLlmConsoleLines: string[] = [];
  @state() localLlmLastLogsFetchAt: number | null = null;
  @state() localLlmLogsTailSupported = true;
  localLlmPollInterval: number | null = null;
  @state() usageStartDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageEndDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();

  @state() usageQuickRange: "today" | "7d" | "30d" | "90d" | "month" | "quarter" | "custom" = "7d";

  @state() spendStartDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() spendEndDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageSelectedSessions: string[] = [];
  @state() usageSelectedDays: string[] = [];
  @state() usageSelectedHours: number[] = [];
  @state() usageChartMode: "tokens" | "cost" = "tokens";
  @state() usageDailyChartMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeriesMode: "cumulative" | "per-turn" = "per-turn";
  @state() usageTimeSeriesBreakdownMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeries: import("./types.js").SessionUsageTimeSeries | null = null;
  @state() usageTimeSeriesLoading = false;
  @state() usageSessionLogs: import("./views/usage.js").SessionLogEntry[] | null = null;
  @state() usageSessionLogsLoading = false;
  @state() usageSessionLogsExpanded = false;
  @state() providerLoading = false;
  @state() providerUsage: UsageSummary | null = null;
  @state() providerError: string | null = null;
  @state() sessionTrace: import("./views/llm-stats-trace.js").SessionTraceResult | null = null;
  @state() sessionTraceLoading = false;
  @state() sessionTraceError: string | null = null;
  // Applied query (used to filter the already-loaded sessions list client-side).
  @state() usageQuery = "";
  // Draft query text (updates immediately as the user types; applied via debounce or "Search").
  @state() usageQueryDraft = "";
  @state() usageSessionSort: "tokens" | "cost" | "recent" | "messages" | "errors" = "recent";
  @state() usageSessionSortDir: "desc" | "asc" = "desc";
  @state() usageRecentSessions: string[] = [];
  @state() usageTimeZone: "local" | "utc" | "UTC" = "local";
  @state() usageContextExpanded = false;
  @state() usageHeaderPinned = false;
  @state() usageSessionsTab: "all" | "recent" = "all";
  @state() usageVisibleColumns: string[] = [
    "channel",
    "agent",
    "provider",
    "model",
    "messages",
    "tools",
    "errors",
    "duration",
  ];
  @state() usageLogFilterRoles: import("./views/usage.js").SessionLogRole[] = [];
  @state() usageLogFilterTools: string[] = [];
  @state() usageLogFilterHasTools = false;
  @state() usageLogFilterQuery = "";

  // Non-reactive (don’t trigger renders just for timer bookkeeping).
  usageQueryDebounceTimer: number | null = null;

  @state() cronLoading = false;
  @state() cronJobs: CronJob[] = [];
  @state() cronStatus: CronStatus | null = null;
  @state() cronError: string | null = null;
  @state() cronForm: CronFormState = { ...DEFAULT_CRON_FORM };
  @state() cronRunsJobId: string | null = null;
  @state() cronRuns: CronRunLogEntry[] = [];
  @state() cronBusy = false;
  @state() cronEditingJobId: string | null = null;

  @state() skillsLoading = false;
  @state() skillsReport: SkillStatusReport | null = null;
  @state() skillsError: string | null = null;
  @state() skillsFilter = "";
  @state() skillEdits: Record<string, string> = {};
  @state() skillsBusyKey: string | null = null;
  @state() skillMessages: Record<string, SkillMessage> = {};
  @state() skillsExpandedKeys: Set<string> = new Set();

  @state() debugLoading = false;
  @state() debugStatus: StatusSummary | null = null;
  @state() debugHealth: HealthSnapshot | null = null;
  @state() debugModels: unknown[] = [];
  @state() debugHeartbeat: unknown = null;
  @state() debugCallMethod = "";
  @state() debugCallParams = "{}";
  @state() debugCallResult: string | null = null;
  @state() debugCallError: string | null = null;

  @state() queueLoading = false;
  @state() queueError: string | null = null;
  @state() queueStatus: import("./types.js").KafkaQueueStatus | null = null;
  @state() queueRestarting = false;

  @state() devBuildInfoLoading = false;
  @state() devBuildInfo: import("./controllers/developer.ts").ControlUiBuildInfo | null = null;
  @state() devBuildInfoError: string | null = null;
  @state() devBuildRunning = false;
  @state() devBuildActiveId: string | null = null;
  @state() devBuildLines: string[] = [];
  @state() devBuildError: string | null = null;
  @state() devBuildLastOk: boolean | null = null;

  @state() logsLoading = false;
  @state() logsError: string | null = null;
  @state() logsFile: string | null = null;
  @state() logsEntries: LogEntry[] = [];
  @state() logsFilterText = "";
  @state() logsLevelFilters: Record<LogLevel, boolean> = {
    ...DEFAULT_LOG_LEVEL_FILTERS,
  };
  @state() logsAutoFollow = true;
  @state() logsTruncated = false;
  @state() logsCursor: number | null = null;
  @state() logsLastFetchAt: number | null = null;
  @state() logsLimit = 500;
  @state() logsMaxBytes = 250_000;
  @state() logsAtBottom = true;
  @state() logsMatched: number | null = null;
  @state() logsScanned: number | null = null;
  @state() logsExpandedRaw: string | null = null;
  @state() logsTailServerFilters: boolean | undefined = undefined;

  // JAISIU-types-sweep: per-prefix log-tab buffers keyed by the
  // SessionLogStatePrefix union (`"openclawLogs" | "jaisiuLogs" |
  // "gatewayLogs"` — see controllers/session-logs.ts). Each tab in
  // app-render.ts writes to these via the dynamic stateAdapter, which
  // casts through `Record<string, unknown>`; the previous code in
  // app-render.ts read them directly off `OpenClawApp` and produced
  // TS2339 errors on every read/write because no fields were declared
  // here. Declaring them once matches the runtime shape and closes all
  // 86 errors in app-render.ts (openclaw, jaisiu, gateway blocks).
  @state() openclawLogsLoading = false;
  @state() openclawLogsError: string | null = null;
  @state() openclawLogsFileCount: number | null = null;
  @state() openclawLogsEntries: import("./types.ts").LogEntry[] = [];
  @state() openclawLogsCursor: number | null = null;
  @state() openclawLogsTruncated = false;
  @state() openclawLogsFilterText = "";
  @state() openclawLogsLevelFilters: Record<LogLevel, boolean> = {
    ...DEFAULT_LOG_LEVEL_FILTERS,
  };
  @state() openclawLogsAutoFollow = true;
  @state() openclawLogsExpandedRaw: string | null = null;

  @state() jaisiuLogsLoading = false;
  @state() jaisiuLogsError: string | null = null;
  @state() jaisiuLogsFileCount: number | null = null;
  @state() jaisiuLogsEntries: import("./types.ts").LogEntry[] = [];
  @state() jaisiuLogsCursor: number | null = null;
  @state() jaisiuLogsTruncated = false;
  @state() jaisiuLogsFilterText = "";
  @state() jaisiuLogsLevelFilters: Record<LogLevel, boolean> = {
    ...DEFAULT_LOG_LEVEL_FILTERS,
  };
  @state() jaisiuLogsAutoFollow = true;
  @state() jaisiuLogsExpandedRaw: string | null = null;

  @state() gatewayLogsLoading = false;
  @state() gatewayLogsError: string | null = null;
  @state() gatewayLogsFileCount: number | null = null;
  @state() gatewayLogsEntries: import("./types.ts").LogEntry[] = [];
  @state() gatewayLogsCursor: number | null = null;
  @state() gatewayLogsTruncated = false;
  @state() gatewayLogsFilterText = "";
  @state() gatewayLogsLevelFilters: Record<LogLevel, boolean> = {
    ...DEFAULT_LOG_LEVEL_FILTERS,
  };
  @state() gatewayLogsAutoFollow = true;
  @state() gatewayLogsExpandedRaw: string | null = null;

  // JAISIU-types-sweep: Docker log buffer state. Read/written by
  // the docker-logs branch in app-render.ts and (eventually) by a
  // `docker.logs.follow` RPC. Like the openclaw / jaisiu / gateway
  // log buffers above, these mirror the field shapes the UI expects
  // — the runtime is set via `RenderContainer` registrations, the
  // type just has to declare them so consumers can read/write the
  // keys without `Record<string, unknown>` indirection.
  @state() dockerLogsEntries: string[] = [];
  @state() dockerLogsFilterText = "";
  @state() dockerLogsAutoFollow = true;
  @state() dockerLogsConnected = false;
  @state() dockerLogsError: string | null = null;
  @state() dockerLogsScrollAnchor: HTMLElement | null = null;

  @state() employeesLoading = false;
  @state() employeesError: string | null = null;
  @state() employeesList: EmployeeRow[] = [];
  @state() employeesConnectors: ConnectorInfo[] = [];
  @state() employeesSyncHistory: SyncHistoryRow[] = [];
  @state() employeesStatus: {
    enabled: boolean;
    connectorConfigured: boolean;
    activeConnector?: string;
    employeeCount: number;
  } | null = null;
  @state() employeesAccountOptions: EmployeeAccountOptions | null = null;
  @state() selectedEmployee: EmployeeRow | null = null;
  @state() showCreateForm = false;

  @state() liveSessionsLoading = false;
  @state() liveSessionsList: import("./controllers/live-sessions.ts").LiveSessionRow[] = [];
  @state() liveSessionsError: string | null = null;
  @state() liveSessionsSelectedId: string | null = null;
  @state() liveSessionsDetail: import("./controllers/live-sessions.ts").LiveSessionDetail | null =
    null;
  @state() liveSessionsDetailLoading = false;

  @state() recordSkillLoading = false;
  @state() recordSkillList: import("./controllers/record-skill.ts").RecordSkillRow[] = [];
  @state() recordSkillError: string | null = null;
  @state() recordSkillPrivacy:
    | import("./controllers/record-skill.ts").RecordSkillPrivacyNotice
    | null = null;
  @state() recordSkillSelectedId: string | null = null;
  @state() recordSkillDetail: import("./controllers/record-skill.ts").RecordSkillDetail | null =
    null;
  @state() recordSkillActiveId: string | null = null;

  @state() googleDriveStatus: {
    configured: boolean;
    clientEmail?: string;
    storagePath?: string;
    viaAccessTokenEnv?: boolean;
  } | null = null;
  @state() googleDriveBusy = false;
  @state() googleDriveMessage: string | null = null;
  @state() googleDriveJsonDraft = "";

  // Workforce state
  @state() workforceLoading = false;
  @state() workforceSaving = false;
  @state() workforceError: string | null = null;
  @state() workforceConfig: WorkforceConfig | null = null;
  @state() workforceAgents: WorkforceAgentRow[] = [];
  @state() workforceRoles: WorkforceRoleRow[] = [];
  @state() workforceUnits: WorkforceUnitRow[] = [];
  @state() workforceBindings: WorkforceBindingRow[] = [];
  @state() workforceSelectedAgentId: string | null = null;
  @state() workforceSelectedView:
    | "overview"
    | "agents"
    | "roles"
    | "units"
    | "bindings"
    | "routing"
    | "analytics"
    | "fleet" = "overview";
  @state() workforceInsights: WorkforceInsightsState | null = null;
  @state() workforceSimulateBusy = false;
  @state() workforceSimulateError: string | null = null;
  @state() workforceSimulateResult: WorkforceSimulateResultState | null = null;
  // Fleet state
  @state() fleetLoading = false;
  @state() fleetError: string | null = null;
  @state() fleetLoads: import("./app-view-state.js").FleetLoadEntry[] = [];
  @state() fleetActiveAgents: import("./app-view-state.js").FleetActiveAgent[] = [];
  @state() fleetStats: import("./app-view-state.js").FleetStats | null = null;
  @state() operatorTrustBySession: import("./app-operator-trust-state.ts").OperatorTrustBySession =
    {};
  @state() operatorTrustLastSignalAt = 0;
  @state() fleetEventHistory: import("./app-view-state.js").FleetEventRecord[] = [];
  // Multi-agent profile quick-switcher state
  @state() fleetProfiles: import("./types.js").ExecutionProfile[] = [];
  @state() fleetProfilesLoading = false;
  @state() fleetProfilesError: string | null = null;
  @state() fleetMultiAgentSwitcherOpen = false;

  client: GatewayBrowserClient | null = null;
  private chatScrollFrame: number | null = null;
  private chatScrollTimeout: number | null = null;
  private chatHasAutoScrolled = false;
  // Autoscroll is disabled. These flags only drive the "New messages" badge
  // and the explicit one-shot jump from `scrollToBottom()`.
  private chatUserNearBottom = false;
  @state() chatNewMessagesBelow = false;
  /** JAISIU-3502: ↑ chevron visible flag. */
  @state() chatNewMessagesAbove = false;
  /** JAISIU-3502: ↑ chevron armed flag (only set by accumulated
   *  upward scroll). Cleared by the auto-hide timer. */
  @state() chatNavUpArmed = false;
  /** JAISIU-3502: ↓ chevron armed flag (set by downward scroll OR
   *  noteChatMessagesGrew). Cleared by the auto-hide timer. */
  @state() chatNavDownArmed = false;
  private chatNavUpAccumulator = 0;
  private chatNavLastScrollTop = 0;
  private chatNavLastMessagesLength = 0;
  private chatNavUpTimer: number | null = null;
  private chatNavDownTimer: number | null = null;
  @state() chatPinnedUserQuestion: string | null = null;
  private nodesPollInterval: number | null = null;
  private agentsPollInterval: number | null = null;
  private subagentsPollInterval: number | null = null;
  private logsPollInterval: number | null = null;
  private debugPollInterval: number | null = null;
  private queuePollInterval: number | null = null;
  private liveSessionsPollInterval: number | null = null;
  private llmStatsCapacityPollInterval: number | null = null;
  private chatPollInterval: number | null = null;
  private postSubmitReloadInterval: number | null = null;
  private logsScrollFrame: number | null = null;
  private toolStreamById = new Map<string, ToolStreamEntry>();
  private toolStreamOrder: string[] = [];
  refreshSessionsAfterChat = new Set<string>();
  private chatDraftBySession = new Map<
    string,
    { message: string; attachments: import("./ui-types.ts").ChatAttachment[] }
  >();
  // JAISIU-3307 (Pillar B) — per-session write-through caches for the
  // inspector surfaces. Keyed by trimmed session key. Controllers
  // (`loadSessionEnvironment`, `loadChatContextBreakdown`) populate these
  // from their successful responses; `activateChatSession` consults them so
  // the inspector paints the cached snapshot synchronously instead of going
  // blank for the duration of the refetch RPC.
  //
  // The comment above used to claim these were "capped at the same limit as
  // chatPalaceContinuityCache". Only chatPalaceContinuityCache actually
  // evicted — these two had real writers and no eviction at all, so every
  // session the operator visited left an entry. Both now bound themselves via
  // `capSessionKeyedMap` on write.
  private sessionEnvironmentBySession = new Map<
    string,
    import("./types.ts").SessionEnvironmentExplainResult
  >();
  // Public, not private: `ChatContextBreakdownState` declares this slot and
  // the app instance is passed as that state into `loadChatContextBreakdown`
  // and friends. A `private` modifier made the class structurally
  // incompatible with its own state interface (TS2345).
  chatContextBreakdownBySession = new Map<
    string,
    import("./controllers/chat-context-breakdown.ts").SessionContextBreakdown
  >();
  private chatHealthBySession = new Map<string, string>();
  private sessionNavigatorHistory = createSessionNavigatorHistory(this.settings.sessionKey);
  private sessionNavigatorHistorySkipPush = false;
  basePath = "";
  private popStateHandler = () =>
    onPopStateInternal(this as unknown as Parameters<typeof onPopStateInternal>[0]);
  private themeMedia: MediaQueryList | null = null;
  private themeMediaHandler: ((event: MediaQueryListEvent) => void) | null = null;
  private topbarObserver: ResizeObserver | null = null;

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    handleConnected(this as unknown as Parameters<typeof handleConnected>[0]);
    // JAISIU-3320 / Pillar I — kick off the IndexedDB hydration in the
    // background so it does not block first paint. The Map is empty
    // until the IDB reads resolve; tab switches before that point
    // still work, they just do not restore a previously-typed draft.
    void this.hydratePillarIFromIdb();
  }

  /**
   * JAISIU-3320 / Pillar I — pull persisted UI state out of IndexedDB
   * and seed the in-memory caches that back the chat tabs. Reads
   * happen entirely in the background; failures degrade silently to
   * the in-memory-only path so a Safari-private-mode user still gets
   * a working app, just without durability across reloads.
   */
  private async hydratePillarIFromIdb(): Promise<void> {
    try {
      // 1. Pinned active job — set the settings field so the renderer
      //    can paint the "Return to active job" banner if the boot
      //    session is something other than the pinned one. Mirror to
      //    IDB so a missing entry (very first run) does not leak.
      const pinned = await idbLoadPinnedActiveJob();
      if (pinned) {
        this.applySettings({ ...this.settings, pinnedActiveJob: pinned });
      }

      // 2. Drafts — seed the Map for every open tab. Tabs whose
      //    IDB row is missing simply stay empty (the Map's prior
      //    contents, if any, are left untouched).
      const tabs = this.getChatOpenTabs();
      for (const key of tabs) {
        const trimmed = key.trim();
        if (!trimmed) continue;
        const draft = await idbLoadDraft(trimmed);
        if (!draft) continue;
        this.chatDraftBySession.set(trimmed, {
          message: draft.message,
          attachments: draft.attachments as import("./ui-types.ts").ChatAttachment[],
        });
      }
    } catch (err) {
      // Hydration must never crash boot. Surface a console warning so
      // operators can see IDB failures but keep the app functional.
      console.warn("[pillar-i] IDB hydration failed", err);
    }
  }

  /**
   * JAISIU-3320 / Pillar I — mark a session as the active fan-out
   * target so the "Return to active job" banner can pick it up. Also
   * mirrors the value into IndexedDB so a reload preserves the banner.
   */
  setPinnedActiveJob(sessionKey: string | null): void {
    const next = sessionKey && sessionKey.trim() ? sessionKey.trim() : null;
    if (this.settings.pinnedActiveJob === next) {
      return;
    }
    this.applySettings({ ...this.settings, pinnedActiveJob: next });
    void idbSavePinnedActiveJob(next);
  }

  protected firstUpdated() {
    handleFirstUpdated(this as unknown as Parameters<typeof handleFirstUpdated>[0]);
    // JAISIU-4089 — start the session-health monitor. It stamps the
    // initial verdict synchronously (so the first paint shows the
    // right pill colour) and re-evaluates on a 5s interval. The
    // monitor's interval is unref'd so it does not keep the page
    // alive on tab close.
    this.sessionHealthMonitor = startSessionHealthMonitor({
      connected: this.connected,
      hello: this.hello,
      pendingUserMessageIds: this.pendingUserMessageIds,
      lastError: this.lastError,
      thresholds: DEFAULT_SESSION_HEALTH_THRESHOLDS,
      setSessionHealthVerdict: (verdict) => {
        this.sessionHealthVerdict = verdict;
      },
    });
  }

  disconnectedCallback() {
    handleDisconnected(this as unknown as Parameters<typeof handleDisconnected>[0]);
    // JAISIU-4089 — stop the session-health poll so a leaked interval
    // does not stamp a verdict onto a destroyed element.
    this.sessionHealthMonitor?.dispose();
    this.sessionHealthMonitor = null;
    // Per-session, per-host tunnel refactor — stop the heartbeat
    // tick so a leaked interval cannot stamp a tunnel state on a
    // destroyed element. The controller is reused on reconnect.
    this.tunnelHealthController.dispose();
    // JAISIU-3502: clear the per-arrow auto-hide timers owned by the
    // controller so a leaked setTimeout cannot fire after the host
    // element is removed.
    resetChatScrollNavInternal(this as unknown as Parameters<typeof resetChatScrollNavInternal>[0]);
    // JAISIU-3474 — flush the debounced scroll-anchor IDB save so a
    // refresh at this exact moment still lands the operator on the
    // message they were last reading. Fire-and-forget; the host is
    // tearing down so there is nothing to await.
    if (this.chatScrollAnchorSaveTimer != null) {
      clearTimeout(this.chatScrollAnchorSaveTimer);
      this.chatScrollAnchorSaveTimer = null;
    }
    void this.flushScrollAnchorSave();
    // JAISIU-3653 — drop any pending jump-focus so a stale timer
    // does not run its cleanup under a freshly-mounted host.
    if (this.chatHistoryJumpFocusTimer != null) {
      window.clearTimeout(this.chatHistoryJumpFocusTimer);
      this.chatHistoryJumpFocusTimer = null;
    }
    this.chatHistoryJumpFocusMessageId = null;
    super.disconnectedCallback();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    handleUpdated(this as unknown as Parameters<typeof handleUpdated>[0], changed);
    // JAISIU-3588 (followup) — manage the `+` popover's
    // outside-click listener from a single place so the popover
    // closes on a single outside click. The listener attaches
    // whenever `chatComposerPlusOpen` is true (regardless of how
    // it was opened: button click, keyboard shortcut, or initial
    // prop) and detaches on any close path. The listener also
    // self-detaches on the first outside click so a rapid
    // double-click doesn't toggle the popover twice.
    if (changed.has("chatComposerPlusOpen")) {
      if (this.chatComposerPlusOpen) {
        registerChatComposerPlusOutsideClick(() => this.handleCloseChatComposerPlus());
      } else {
        unregisterChatComposerPlusOutsideClick();
      }
    }
    // JAISIU-3653 — when a sidebar click jump armed a focus target,
    // paint the focus class on the matched row in the NEXT render
    // cycle. We run on the next animation frame so the chat thread
    // has had time to commit the matched row into the DOM (the
    // scroll restore runs in parallel and only completes a tick or
    // two after `updateComplete` resolves for cache-hit sessions).
    if (changed.has("chatHistoryJumpFocusMessageId")) {
      void this.applyJumpFocusIfNeeded();
    }
    // JAISIU-3655 — re-run the highlight pass whenever the query,
    // the rendered transcript, the deep-batch result, OR the
    // activity-inclusion toggle changes. The pass is pure DOM
    // (`highlightMatchesIn` walks text nodes and wraps every match
    // in `<mark class="chat-search-hit">`), so it runs entirely off
    // the reactive tick — no Lit re-render. We also nudge the
    // active-match class + smooth-scroll when the counter index
    // moves.
    const highlightTriggered =
      changed.has("chatMessageSearchQuery") ||
      changed.has("chatThreadDeepBatchResult") ||
      changed.has("chatMessages") ||
      changed.has("chatMessageSearchInActivities");
    if (highlightTriggered) {
      this.runChatMessageSearchHighlightPass();
    }
    if (highlightTriggered || changed.has("chatMessageSearchCurrentIndex")) {
      this.applyChatMessageSearchActiveMatch();
    }
  }

  connect() {
    connectGatewayInternal(this as unknown as Parameters<typeof connectGatewayInternal>[0]);
  }

  /**
   * JAISIU-4089 — record a failure against the session-health monitor
   * and re-evaluate the verdict on the same tick. Called by the chat
   * controller when `chat.send` returns a retryable / fatal error,
   * by the outbox reconciler when a retry budget is exhausted, and
   * by `handleDisconnected` when the websocket drops.
   *
   * The reason should be a short, stable error code (e.g.
   * `RATE_LIMITED`, `TIMEOUT`, `OUTBOX_BUDGET`) — it ends up in the
   * indicator's `title` attribute and in the verdict's `reason`
   * field, so the operator can hover for context.
   */
  noteSessionHealthFailure(reason: string): void {
    this.sessionHealthMonitor?.noteFailure(reason);
  }

  /**
   * JAISIU-4089 — called when the gateway websocket reconnects.
   * Clears the failure buffer and re-evaluates the verdict so the
   * pill flips back to "online" without waiting a full 5s poll.
   */
  noteSessionHealthReconnect(): void {
    this.sessionHealthMonitor?.noteReconnect();
  }

  handleChatScroll(event: Event) {
    // JAISIU-3502: the per-arrow auto-hide timers are owned by the
    // scroll-nav controller in app-scroll.ts (reset on every scroll
    // event inside `handleChatScrollInternal`). No app-level timer
    // work is needed here.
    handleChatScrollInternal(
      this as unknown as Parameters<typeof handleChatScrollInternal>[0],
      event,
    );
  }

  // JAISIU-3655 — chat-thread search query handler. Resets the
  // counter state + clears the previous query's deep-batch index.
  // The actual highlight pass and the deep-batch walker are kicked
  // off via `app-render-fleet-chat.ts`'s query-change forwarder;
  // this method is the counter-state owner.
  handleChatMessageSearchQueryChange(next: string) {
    const raw = next ?? "";
    const previous = this.chatMessageSearchQuery;
    if (previous === raw) return;
    // JAISIU-3655 — gate on non-whitespace content so a pure-
    // whitespace query (" ") is treated the same as empty: no
    // anchor capture, no counter reset (the values are already
    // cleared), no deep-batch forward. Whitespace WITHIN the
    // query is preserved as-is so "can " is distinct from "can"
    // downstream.
    const previousEffective = previous.replace(/\s+/g, "").length > 0;
    const rawEffective = raw.replace(/\s+/g, "").length > 0;
    // JAISIU-3657 — empty→non-empty transition is the moment we
    // capture the pre-search reading position. The renderRoot must
    // be present (we need the chat-thread scroll container to read
    // offsetTop / clientHeight from) and the host must have a
    // non-empty session. We never overwrite an existing anchor
    // while a query is active — typing a second character must NOT
    // reset the saved position.
    if (!previousEffective && rawEffective) {
      const activeKey = (this.sessionKey ?? "").trim();
      if (activeKey) {
        const container = findThreadScrollContainer(this.renderRoot);
        if (container) {
          const anchor = captureThreadSearchAnchor(container, activeKey);
          // Always assign a new reference so Lit picks up the
          // mutation — we don't read this prop in the view, but the
          // host's `updated()` re-runs the highlight pass off the
          // search-related fields and a stale reference would skip
          // the capture entirely on a subsequent query.
          this.chatThreadSearchAnchor = anchor;
        } else {
          this.chatThreadSearchAnchor = null;
        }
      }
    }
    // Commit the new query value AFTER the anchor capture so the
    // clear path below sees `previous` reflected in
    // `handleChatMessageSearchClear` (the clear method reads
    // `chatMessageSearchQuery` at the top to decide whether to run
    // `clearHighlights` + restore).
    this.chatMessageSearchQuery = raw;
    // Clearing the query resets everything. A new query will
    // schedule a deep-batch walk in the forwarder. We trigger the
    // clear path on both the raw-empty case AND the effective-
    // empty case (pure whitespace) so a stray " " reset does not
    // leave stale marks behind.
    if (!rawEffective) {
      this.handleChatMessageSearchClear(previous);
      return;
    }
    // Reset the counter so the UI does not flash a stale count
    // from the previous query while the walker runs. The forwarder
    // will hand back the resolved total via
    // `handleChatMessageDeepBatchResult` once the walker settles.
    this.chatMessageSearchTotal = 0;
    this.chatMessageSearchCurrentIndex = 0;
    this.chatMessageSearchMatchIds = [];
    this.chatThreadDeepBatchResult = null;
    // JAISIU-3655 (followup) — flag the next highlight pass to
    // ALSO smooth-scroll the first match into view. This is the
    // operator's "first focus" moment — they just typed a query
    // and expect to land on the first match.
    this.chatMessageSearchFocusOnNextPaint = true;
  }

  // JAISIU-3657 — Esc / native-clear handler for the chat-thread
  // search input. Clears every search-related reactive field,
  // removes the `<mark class="chat-search-hit">` wraps from the
  // rendered thread (the counter hides itself off `chatMessageSearchTotal=0`,
  // so the visible "X / Y" + ↑/↓ UI disappears automatically), and
  // restores the chat scroll position to the value captured at
  // search-start time.
  //
  // The scroll write is bracketed through `withProgrammaticScroll`
  // so the resulting `scroll` event is not classified as a user
  // gesture — the existing JAISIU-3533 anchor system must not see
  // this as a real scroll. When the captured row has been shifted
  // off the rendered window (because deep-match navigation paged
  // older batches in), we drain `loadOlderChatHistory` until the
  // row appears, then restore. The drain is bounded so a deleted
  // anchor + a server that always reports `hasMoreOlder=true`
  // cannot wedge the UI.
  //
  // `previousQuery` is the query value that was active BEFORE the
  // clear. We accept it as a parameter so the highlight-cleanup
  // branch can skip work when the search was already empty (Esc
  // on a blank input is a no-op). Without this argument the field
  // would already be `""` by the time this method runs.
  handleChatMessageSearchClear(previousQuery?: string): void {
    this.chatMessageSearchTotal = 0;
    this.chatMessageSearchCurrentIndex = 0;
    this.chatMessageSearchMatchIds = [];
    this.chatThreadDeepBatchResult = null;
    this.chatMessageSearchQuery = "";
    const anchor = this.chatThreadSearchAnchor;
    this.chatThreadSearchAnchor = null;
    // We MUST clear the `<mark class="chat-search-hit">` wraps from
    // the rendered thread BEFORE the next `updated()` tick re-runs
    // the highlight pass — otherwise the next query's first pass
    // would skip wrapping (idempotency key in
    // `highlightMatchesIn` keys off query, but the leftover marks
    // from the previous query would still be in the DOM).
    const hadActiveQuery = (previousQuery ?? "").trim().length > 0;
    if (hadActiveQuery) {
      const container = findThreadScrollContainer(this.renderRoot);
      if (container) {
        try {
          clearHighlights(container);
        } catch {
          // Highlight cleanup is best-effort; a thrown DOM error
          // (rare — e.g. detached subtree) must not abort the
          // restore below.
        }
      }
    }
    // Only run the restore math when we actually had an active
    // query — pressing Esc on an empty input is a no-op.
    if (!anchor) return;
    const activeKey = (this.sessionKey ?? "").trim();
    if (!activeKey || anchor.sessionKey !== activeKey) return;
    // Wait for the next render frame so the cleared `<mark>` nodes
    // (and any deep-match prepended rows) have a final layout before
    // we measure offsets.
    void this.restoreChatThreadSearchAnchor(anchor);
  }

  // JAISIU-3657 — scroll-restore side of `handleChatMessageSearchClear`.
  // Exposed as a private method so the highlight-clearing branch can
  // kick it after Lit commits the cleared state.
  private async restoreChatThreadSearchAnchor(anchor: ChatThreadSearchAnchor): Promise<void> {
    const activeKey = (this.sessionKey ?? "").trim();
    if (!activeKey || anchor.sessionKey !== activeKey) return;
    // Two-rAF settle so the post-clear Lit commit lands + the
    // browser paints the new DOM before we measure offsets.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    const container = findThreadScrollContainer(this.renderRoot);
    if (!container) return;
    // Fast path — the row is in the rendered DOM. Restore
    // synchronously inside the programmatic-scroll bracket.
    const target = resolveThreadSearchRestoreTarget(anchor, container);
    if (target !== null) {
      void withProgrammaticScroll(
        this as unknown as Parameters<typeof withProgrammaticScroll>[0],
        () => {
          container.scrollTop = target;
        },
      );
      return;
    }
    // Slow path — deep-match navigation may have paged older batches
    // in. Drain `loadOlderChatHistory` until the anchor row is in
    // the DOM, bounded so a missing anchor + infinite `hasMoreOlder`
    // cannot wedge the UI.
    if (!this.client || !this.connected) return;
    const RESTORE_DRAIN_HARD_CAP = 50;
    const escapeAttr = (s: string) =>
      s.replace(/["\\]/g, (ch) => "\\" + ch.charCodeAt(0).toString(16).padStart(2, "0") + " ");
    let safety = 0;
    while (
      container.querySelector(`[data-msg-id="${escapeAttr(anchor.messageId)}"]`) === null &&
      this.chatHistoryHasMoreOlder &&
      !this.chatHistoryLoadingOlder &&
      safety < RESTORE_DRAIN_HARD_CAP
    ) {
      safety += 1;
      try {
        await this.loadOlderChatHistory();
      } catch {
        break;
      }
      if ((this.sessionKey ?? "").trim() !== activeKey) return;
    }
    // Re-measure post-drain. The session may have switched while
    // the drain was in flight — bail if so.
    if ((this.sessionKey ?? "").trim() !== activeKey) return;
    const finalContainer = findThreadScrollContainer(this.renderRoot);
    if (!finalContainer) return;
    const finalTarget = resolveThreadSearchRestoreTarget(anchor, finalContainer);
    if (finalTarget === null) return;
    void withProgrammaticScroll(
      this as unknown as Parameters<typeof withProgrammaticScroll>[0],
      () => {
        finalContainer.scrollTop = finalTarget;
      },
    );
  }

  // the reactive counter fields. The walker is read-only w.r.t. the
  // live cache; this handler is the contract between the walker
  // and the UI.
  handleChatMessageDeepBatchResult(
    result: import("./chat/chat-thread-search-deep-batch.ts").DeepBatchSearchResult,
  ) {
    // Stale-result guard — only accept results that target the
    // ACTIVE session AND match the current query. A later query on
    // the same session can produce an out-of-date result if the
    // walker was slow to settle.
    const activeKey = (this.sessionKey ?? "").trim();
    if (result.sessionKey !== activeKey) return;
    // JAISIU-3655 — compare raw queries so a trailing-space change
    // ("can" → "can ") is treated as a NEW query and a stale result
    // from the previous one is discarded. Trimmed equality would
    // collapse the two.
    if (result.query !== this.chatMessageSearchQuery) return;

    this.chatThreadDeepBatchResult = result;
    this.chatMessageSearchTotal = result.total;
    const ids: string[] = [];
    for (const m of result.liveMatches) ids.push(m.messageId);
    for (const m of result.deepMatches) ids.push(m.messageId);
    this.chatMessageSearchMatchIds = ids;
    // Clamp the current index to the new total. A query change
    // usually starts at 0.
    if (this.chatMessageSearchCurrentIndex >= result.total) {
      this.chatMessageSearchCurrentIndex = 0;
    }
  }

  // JAISIU-3655 — navigate between matches with the ↑/↓ buttons.
  // Pressing ↓ past the end of the LIVE matches triggers a
  // `loadOlderChatHistory` so the next match's row paints before
  // the smooth-scroll lands on it. The scroll itself is bracketed
  // by `chatProgrammaticScroll` inside `chat-thread-search-highlight`
  // so the navigation never disturbs the saved anchor.
  async handleChatMessageSearchNav(direction: number) {
    if (this.chatMessageSearchTotal <= 0) return;
    const total = this.chatMessageSearchTotal;
    const current = this.chatMessageSearchCurrentIndex;
    const nextIndex = (((current + direction) % total) + total) % total;
    this.chatMessageSearchCurrentIndex = nextIndex;
    // JAISIU-3655 (followup) — flag the next highlight pass to
    // also smooth-scroll the (newly-active) match into view.
    // ↑/↓ is the operator's explicit "move me to the next/prev
    // match" gesture, so the viewport SHOULD follow.
    this.chatMessageSearchFocusOnNextPaint = true;
    const targetId = this.chatMessageSearchMatchIds[nextIndex];
    if (!targetId) return;
    // If the target is in the live cache, the highlight pass on
    // the next tick will pick it up — we just need to nudge the
    // active-match styling. If it is NOT in the live cache, this
    // is a deep match — bring the page in.
    const live = Array.isArray(this.chatMessages) ? this.chatMessages : [];
    const idField = (m: unknown): string | undefined => {
      if (!m || typeof m !== "object") return undefined;
      const r = m as { id?: unknown; transcriptId?: unknown; messageId?: unknown };
      for (const k of ["id", "transcriptId", "messageId"] as const) {
        const v = r[k];
        if (typeof v === "string" && v.trim()) return v.trim();
      }
      return undefined;
    };
    const inLive = live.some((m) => idField(m) === targetId);
    if (inLive) return;
    // JAISIU-3656 — bring the deep match's page into the live
    // transcript. This is the ONLY path in the in-thread search
    // code that mutates `chatMessagesBySession`.
    const { navigateToChatThreadDeepMatch } =
      await import("./chat/chat-thread-search-deep-batch.ts");
    void navigateToChatThreadDeepMatch(
      this as unknown as Parameters<typeof navigateToChatThreadDeepMatch>[0],
      targetId,
    );
  }

  handleLogsScroll(event: Event) {
    handleLogsScrollInternal(
      this as unknown as Parameters<typeof handleLogsScrollInternal>[0],
      event,
    );
  }

  exportLogs(lines: string[], label: string) {
    exportLogsInternal(lines, label);
  }

  resetToolStream() {
    resetToolStreamInternal(this as unknown as Parameters<typeof resetToolStreamInternal>[0]);
  }

  resetChatScroll() {
    resetChatScrollInternal(this as unknown as Parameters<typeof resetChatScrollInternal>[0]);
  }

  /**
   * JAISIU-3502: forward to the scroll-nav controller. Called when
   * the chat transcript grows (the only legitimate non-scroll trigger
   * for the ↓ chevron).
   */
  noteChatMessagesGrew(newLength: number) {
    noteChatMessagesGrewInternal(
      this as unknown as Parameters<typeof noteChatMessagesGrewInternal>[0],
      newLength,
    );
  }

  scrollToBottom() {
    scrollChatToBottomInternal(this as unknown as Parameters<typeof scrollChatToBottomInternal>[0]);
  }

  /**
   * JAISIU-3502: scroll the chat transcript to the top.
   *
   * The previous "go to top" pill lived in the scroll container as a
   * sticky element so a click on it scrolled the container back to
   * scrollTop = 0. We keep that contract: find the chat transcript
   * scroll container, set scrollTop = 0, let the browser pick up the
   * remaining work. The scroll handler in `app-scroll.ts` will then
   * reset `chatNewMessagesAbove` + `chatNavUpArmed` synchronously on
   * the next scroll event so a click does not leave the chevron
   * stuck visible.
   */
  scrollToTop() {
    const el = this.renderRoot?.querySelector(
      ".chat-fleet-thread-scroll, .chat-thread-scroll",
    ) as HTMLElement | null;
    if (!el) return;
    // JAISIU-3534 — bracket through `withProgrammaticScroll` so the
    // explicit reset does NOT arm the ↑/↓ chevrons.
    void withProgrammaticScroll(
      this as unknown as Parameters<typeof withProgrammaticScroll>[0],
      async () => {
        el.scrollTop = 0;
      },
    );
    // Forward to the controller so all scroll-nav state is reset in
    // one place (visible flag, armed flag, accumulator, timer).
    resetChatScrollNavInternal(this as unknown as Parameters<typeof resetChatScrollNavInternal>[0]);
  }

  async loadAssistantIdentity() {
    await loadAssistantIdentityInternal(this);
  }

  async handleYouTrackSearch() {
    await searchYouTrackIssuesInternal(
      this as unknown as Parameters<typeof searchYouTrackIssuesInternal>[0],
    );
  }

  async handleYouTrackRefresh() {
    await this.handleYouTrackSearch();
  }

  async handleYouTrackSelectIssue(issueId: string) {
    await loadYouTrackIssueInternal(
      this as unknown as Parameters<typeof loadYouTrackIssueInternal>[0],
      issueId,
    );
  }

  handleYouTrackOpenCreate() {
    this.youtrackCreateOpen = true;
    this.youtrackCreateDraft = { summary: "", description: "", type: "Task", assignee: "" };
    this.youtrackCreateError = null;
  }

  handleYouTrackCloseCreate() {
    this.youtrackCreateOpen = false;
    this.youtrackCreateDraft = null;
    this.youtrackCreateError = null;
  }

  handleYouTrackCreateDraftChange(
    field: keyof NonNullable<typeof this.youtrackCreateDraft>,
    value: string,
  ) {
    if (!this.youtrackCreateDraft) return;
    this.youtrackCreateDraft = { ...this.youtrackCreateDraft, [field]: value };
  }

  async handleYouTrackCreate() {
    await createYouTrackIssueInternal(
      this as unknown as Parameters<typeof createYouTrackIssueInternal>[0],
    );
  }

  async handleYouTrackTransition(issueId: string, targetState: string) {
    await transitionYouTrackIssueInternal(
      this as unknown as Parameters<typeof transitionYouTrackIssueInternal>[0],
      issueId,
      targetState,
    );
  }

  async handleYouTrackAddComment(issueId: string, text: string) {
    await addYouTrackCommentInternal(
      this as unknown as Parameters<typeof addYouTrackCommentInternal>[0],
      issueId,
      text,
    );
  }

  handleYouTrackSaveFilter(name: string) {
    saveYouTrackFilterInternal(
      this as unknown as Parameters<typeof saveYouTrackFilterInternal>[0],
      name,
      this.youtrackSearchQuery,
      this.youtrackSearchProject,
    );
  }

  handleYouTrackDeleteFilter(name: string) {
    deleteYouTrackFilterInternal(
      this as unknown as Parameters<typeof deleteYouTrackFilterInternal>[0],
      name,
    );
  }

  handleYouTrackLoadSavedFilters() {
    loadYouTrackSavedFiltersInternal(
      this as unknown as Parameters<typeof loadYouTrackSavedFiltersInternal>[0],
    );
  }

  handleYouTrackMyIssues() {
    searchMyIssuesInternal(this as unknown as Parameters<typeof searchMyIssuesInternal>[0]);
  }

  applySettings(next: UiSettings) {
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], next);
  }

  applyShortcutsConfig(next: ShortcutsConfig) {
    this.shortcutsConfig = next;
    saveShortcuts(next);
  }

  setTab(next: Tab) {
    this.mobileNavMoreOpen = false;
    setTabInternal(this as unknown as Parameters<typeof setTabInternal>[0], next);
    if (next === "network") {
      void loadNetwork(this as unknown as Parameters<typeof loadNetwork>[0]);
    }
  }

  setHelpTopic(topic: import("./help/help-content.ts").HelpTopicId) {
    setHelpTopicInternal(this as unknown as Parameters<typeof setHelpTopicInternal>[0], topic);
  }

  async openAgentsAddModal() {
    if (this.connected && !this.configForm && !this.configLoading) {
      await loadConfig(this as unknown as Parameters<typeof loadConfig>[0]);
    }
    this.agentsAddModalError = null;
    this.agentsAddDraft = { id: "", name: "", workspace: "", setDefault: false };
    this.agentsAddModalOpen = true;
  }

  closeAgentsAddModal() {
    this.agentsAddModalOpen = false;
    this.agentsAddModalError = null;
  }

  async submitAgentsAddModal() {
    const cfg = this as unknown as Parameters<typeof addAgentToConfigForm>[0];
    if (!cfg.configForm && !cfg.configSnapshot?.config) {
      if (this.connected) {
        await loadConfig(cfg);
      }
      if (!cfg.configForm && !cfg.configSnapshot?.config) {
        this.agentsAddModalError = "Config not loaded. Connect to the gateway and try again.";
        return;
      }
    }
    const result = addAgentToConfigForm(cfg, {
      id: this.agentsAddDraft.id,
      name: this.agentsAddDraft.name,
      workspace: this.agentsAddDraft.workspace,
      setDefault: this.agentsAddDraft.setDefault,
    });
    if (!result.ok) {
      this.agentsAddModalError = result.error;
      return;
    }
    this.agentsAddModalSaving = true;
    this.agentsAddModalError = null;
    try {
      await saveConfig(cfg, {
        allowInstanceDefaultChange: this.agentsAddDraft.setDefault,
      });
      if (cfg.lastError) {
        this.agentsAddModalError = cfg.lastError;
        return;
      }
      this.agentsAddModalOpen = false;
      this.agentsSelectedId = result.agentId;
      await loadAgents(this as unknown as Parameters<typeof loadAgents>[0]);
      void this.scheduleAgentFleetGraphRefresh(100);
    } finally {
      this.agentsAddModalSaving = false;
    }
  }

  handleAgentsAddDraftChange(patch: Partial<typeof this.agentsAddDraft>) {
    this.agentsAddDraft = { ...this.agentsAddDraft, ...patch };
  }

  // ─── Multi-agent profile quick-switcher ─────────────────────────────────────

  async loadFleetProfiles() {
    if (!this.connected) return;
    if (this.fleetProfilesLoading) return;
    this.fleetProfilesLoading = true;
    this.fleetProfilesError = null;
    try {
      const result = await this.client!.request<{
        profiles: import("./types.js").ExecutionProfile[];
      }>("fleet.profiles.list", {});
      this.fleetProfiles = result.profiles ?? [];
    } catch (err) {
      this.fleetProfilesError = String(err);
    } finally {
      this.fleetProfilesLoading = false;
    }
  }

  async switchFleetProfile(profileId: string) {
    if (!this.connected) return;
    try {
      await this.client!.request("fleet.profiles.set", { profileId });
      await this.loadFleetProfiles();
      this.fleetMultiAgentSwitcherOpen = false;
    } catch (err) {
      this.fleetProfilesError = String(err);
    }
  }

  async toggleFleetMultiAgent(enabled: boolean) {
    if (!this.connected) return;
    try {
      await this.client!.request("fleet.multiAgent.toggle", { enabled });
      await this.loadFleetProfiles();
      this.fleetMultiAgentSwitcherOpen = false;
    } catch (err) {
      this.fleetProfilesError = String(err);
    }
  }

  openFleetMultiAgentSwitcher() {
    void this.loadFleetProfiles();
    this.fleetMultiAgentSwitcherOpen = true;
  }

  closeFleetMultiAgentSwitcher() {
    this.fleetMultiAgentSwitcherOpen = false;
    this.fleetProfilesError = null;
  }

  registerAgentInConfig(agentId: string) {
    const result = ensureAgentInConfigForm(
      this as unknown as Parameters<typeof ensureAgentInConfigForm>[0],
      agentId,
    );
    if (!result.ok) {
      this.lastError = result.error;
    }
  }

  async deleteAgentFromConfig(agentId: string) {
    const cfg = this as unknown as Parameters<typeof removeAgentFromConfigForm>[0];
    const result = removeAgentFromConfigForm(cfg, agentId);
    if (!result.ok) {
      this.lastError = result.error;
      return;
    }
    await saveConfig(cfg);
    if (cfg.lastError) {
      return;
    }
    const list = this.agentsList?.agents ?? [];
    const remaining = list.filter((a) => a.id !== agentId);
    this.agentsSelectedId = remaining[0]?.id ?? this.agentsList?.defaultId ?? null;
    await loadAgents(this as unknown as Parameters<typeof loadAgents>[0]);
  }

  updateAgentConfigFields(
    agentId: string,
    field: import("./controllers/agents-config.ts").AgentFieldPatch,
  ) {
    updateAgentFieldsInConfigForm(
      this as unknown as Parameters<typeof updateAgentFieldsInConfigForm>[0],
      agentId,
      field,
    );
  }

  updateSandboxFields(agentId: string, patch: SandboxFieldPatch) {
    updateSandboxFieldsInConfigForm(
      this as unknown as Parameters<typeof updateSandboxFieldsInConfigForm>[0],
      agentId,
      patch,
    );
  }

  setEmployeeDetail(employeeId: string | null) {
    setEmployeeDetailInternal(
      this as unknown as Parameters<typeof setEmployeeDetailInternal>[0],
      employeeId,
    );
  }

  setTheme(next: ThemeMode, context?: Parameters<typeof setThemeInternal>[2]) {
    setThemeInternal(this as unknown as Parameters<typeof setThemeInternal>[0], next, context);
  }

  async loadOverview() {
    await loadOverviewInternal(this as unknown as Parameters<typeof loadOverviewInternal>[0]);
  }

  handleGoogleDriveJsonDraftChange(next: string) {
    this.googleDriveJsonDraft = next;
  }

  async handleGoogleDriveSave() {
    await saveGoogleDriveServiceAccount(this as unknown as GoogleDriveHost);
  }

  async handleGoogleDriveClear() {
    await clearGoogleDriveServiceAccount(this as unknown as GoogleDriveHost);
  }

  async handleGoogleDriveTest() {
    await testGoogleDriveConnection(this as unknown as GoogleDriveHost);
  }

  async loadCron() {
    await loadCronInternal(this as unknown as Parameters<typeof loadCronInternal>[0]);
  }

  async loadEmployees() {
    await loadEmployeesDataInternal(this);
    await loadLiveSessionsInternal(this);
    await loadRecordSkillsInternal(this);
    await loadRecordSkillPrivacyInternal(this);
  }

  async loadLiveSessions() {
    await loadLiveSessionsInternal(this);
  }

  async loadRecordSkills() {
    await loadRecordSkillsInternal(this);
  }

  async loadRecordSkillPrivacy() {
    await loadRecordSkillPrivacyInternal(this);
  }

  selectLiveSession(sessionId: string | null) {
    selectLiveSessionInternal(this, sessionId);
  }

  async createLiveSession(input: LiveSessionCreateInput) {
    const sessionId = await createLiveSessionInternal(this, input);
    if (sessionId) {
      this.selectLiveSession(sessionId);
    }
  }

  async startLiveSession(sessionId: string) {
    await startLiveSessionInternal(this, sessionId);
  }

  async pauseLiveSession(sessionId: string) {
    await pauseLiveSessionInternal(this, sessionId);
  }

  async resumeLiveSession(sessionId: string) {
    await resumeLiveSessionInternal(this, sessionId);
  }

  async endLiveSession(sessionId: string) {
    await endLiveSessionInternal(this, sessionId);
  }

  async startRecordSkill(input: {
    title: string;
    desktopControllerUrl?: string;
    narrationTranscript?: string;
    privacyAcknowledged: boolean;
  }) {
    await startRecordSkillInternal(this, input);
  }

  async stopRecordSkill(recordId: string, narrationTranscript?: string) {
    await stopRecordSkillInternal(this, recordId, narrationTranscript);
  }

  async finalizeRecordSkill(recordId: string, narrationTranscript?: string) {
    await finalizeRecordSkillInternal(this, recordId, { narrationTranscript });
  }

  selectRecordSkill(recordId: string | null) {
    void selectRecordSkillInternal(this, recordId);
  }

  setSelectedEmployee(employeeId: string | null) {
    setSelectedEmployeeInternal(this, employeeId);
  }

  async updateEmployee(employeeId: string, data: Partial<EmployeeRow["employee"]>) {
    const selected = this.selectedEmployee?.employee.employeeId ?? null;
    await updateEmployeeInternal(this, employeeId, data);
    if (selected) {
      setSelectedEmployeeInternal(this, selected);
    }
  }

  async addEmployeeChannel(employeeId: string, channelId: string) {
    const selected = this.selectedEmployee?.employee.employeeId ?? null;
    await addEmployeeChannelInternal(this, employeeId, channelId);
    if (selected) {
      setSelectedEmployeeInternal(this, selected);
    }
  }

  async removeEmployeeChannel(employeeId: string, channelId: string) {
    const selected = this.selectedEmployee?.employee.employeeId ?? null;
    await removeEmployeeChannelInternal(this, employeeId, channelId);
    if (selected) {
      setSelectedEmployeeInternal(this, selected);
    }
  }

  async createEmployee(employee: Partial<EmployeeRow["employee"]>, agentId?: string) {
    await createEmployeeInternal(this, employee, agentId);
    if (employee.employeeId) {
      setSelectedEmployeeInternal(this, employee.employeeId);
    }
  }

  async deleteEmployee(employeeId: string) {
    await deleteEmployeeInternal(this, employeeId);
  }

  async runEmployeesSync(dryRun: boolean) {
    const selected = this.selectedEmployee?.employee.employeeId ?? null;
    await runEmployeesSyncInternal(this, dryRun);
    if (selected) {
      setSelectedEmployeeInternal(this, selected);
    }
  }

  async testEmployeesConnection() {
    await testEmployeesConnectionInternal(this);
  }

  activateChatSessionHost(key: string) {
    this.activateChatSession(key);
  }

  private chatTabsHost(): ChatTabsHost {
    return this as unknown as ChatTabsHost;
  }

  getChatOpenTabs(): string[] {
    return getChatOpenTabs(this.chatTabsHost());
  }

  ensureChatTabsInitialized() {
    ensureChatTabsInitialized(this.chatTabsHost());
  }

  canSessionNavigatorBack(): boolean {
    return canSessionNavigatorBack(this.sessionNavigatorHistory);
  }

  canSessionNavigatorForward(): boolean {
    return canSessionNavigatorForward(this.sessionNavigatorHistory);
  }

  sessionNavigatorBack() {
    const result = navigateSessionNavigatorBack(this.sessionNavigatorHistory);
    if (!result) {
      return;
    }
    this.sessionNavigatorHistory = result.history;
    this.sessionNavigatorHistorySkipPush = true;
    this.activateChatSession(result.sessionKey);
    this.sessionNavigatorHistorySkipPush = false;
  }

  sessionNavigatorForward() {
    const result = navigateSessionNavigatorForward(this.sessionNavigatorHistory);
    if (!result) {
      return;
    }
    this.sessionNavigatorHistory = result.history;
    this.sessionNavigatorHistorySkipPush = true;
    this.activateChatSession(result.sessionKey);
    this.sessionNavigatorHistorySkipPush = false;
  }

  activateChatSession(next: string) {
    const trimmed = next.trim();
    const prev = this.sessionKey.trim();
    if (trimmed && trimmed !== prev && !this.sessionNavigatorHistorySkipPush) {
      this.sessionNavigatorHistory = pushSessionNavigatorHistory(
        this.sessionNavigatorHistory,
        trimmed,
      );
    }
    if (trimmed && trimmed !== prev) {
      // JAISIU-3533 — synchronously capture the outgoing chat's scroll
      // anchor BEFORE we cache the outgoing transcript. The capture
      // pushes the latest anchor into `chatScrollAnchorBySession` and
      // flushes the debounced IDB save so a refresh immediately after
      // a fast A→B→A switch still lands on A's last reading row.
      // Synchronous on purpose: the 150 ms idle debounce on the scroll
      // handler could otherwise lose the very last viewport move that
      // happens between the operator's "click tab B" gesture and the
      // tab-bar paint, and we cannot wait for a debounced tick before
      // writing to the per-session map (the cache-hit restore on B
      // needs A's anchor in hand).
      void this.captureScrollAnchorForSessionSwitchOut(prev);
      const outgoingMessages = Array.isArray(this.chatMessages) ? this.chatMessages : [];
      if (outgoingMessages.length > 0) {
        this.chatSessionStore.stashActiveSession({
          sessionKey: prev,
          messages: outgoingMessages,
          pendingUserMessageIds: this.pendingUserMessageIds,
        });
        // JAISIU-types-sweep: the shared `chatMessagesBySession`
        // is the union ChatMessagesBySessionCache type; cast to the
        // wider `unknown[]` variant so Map.set accepts the value
        // under both branches.
        (this.chatMessagesBySession as Map<string, unknown[]>).set(prev, outgoingMessages);
      } else {
        // Mid-load switch: active transcript is [] but the per-session cache
        // may still hold a good copy — never overwrite it with an empty stash.
        this.chatSessionStore.writePendingIds(prev, this.pendingUserMessageIds);
      }
      // JAISIU-3656 — drop the in-thread search deep-batch cache for
      // the OUTGOING session so the walker's ephemeral index never
      // leaks across chats. The incoming session's cache (if any)
      // survives because the walker keys by sessionKey. The counter
      // resets too — a fast A→B click should never flash B with
      // A's last query's match count.
      if (prev) {
        void import("./chat/chat-thread-search-deep-batch.ts").then((mod) =>
          mod.clearActiveChatDeepSearch(prev),
        );
      }
      this.chatMessageSearchQuery = "";
      this.chatMessageSearchTotal = 0;
      this.chatMessageSearchCurrentIndex = 0;
      this.chatMessageSearchMatchIds = [];
      this.chatThreadDeepBatchResult = null;
      // JAISIU-3651 (followup) — when the operator is driving
      // search from the sidebar, the query is shared across all
      // chats. The wipe above is correct for the "fast A→B click
      // should never flash B with A's last query" case, but it
      // also wipes the sidebar-driven query when the operator
      // opens a chat from a filtered list. Re-mirror the sidebar
      // query into the in-thread highlight state here so the
      // incoming chat opens with the same phrase highlighted
      // across both the sidebar rows AND the chat-thread
      // messages.
      //
      // We don't run `handleChatMessageSearchQueryChange` here
      // because that captures a read-position anchor — the
      // operator just clicked a row, they haven't started
      // "searching" from this chat's perspective, and capturing
      // an anchor mid-click would surprise the Esc-restore flow.
      // A direct field write lets the existing `updated()` hook
      // re-run the highlight pass off the query change without
      // any anchor side effects.
      const sidebarQuery = this.chatHistorySearchQuery ?? "";
      if (sidebarQuery.replace(/\s+/g, "").length > 0) {
        this.chatMessageSearchQuery = sidebarQuery;
        // JAISIU-3655 (followup) — clicking a filtered sidebar
        // row counts as the operator's "first focus" on the
        // incoming chat. Flag the next paint to also smooth-
        // scroll the first match into view so the operator
        // doesn't have to manually page to it.
        this.chatMessageSearchFocusOnNextPaint = true;
      }
      // JAISIU-3307 (Pillar B) — write-through the inspector caches for
      // the *outgoing* session before we mutate any active state. The
      // next time the user re-activates `prev`, `activateChatSession`
      // hydrates from these maps instead of going through a blank
      // refetch. Only cache when `sessionEnvironment` is actually set;
      // nulls can come from "we don't know yet" or "disconnected", and
      // conflating those with a real cached snapshot costs us a refetch
      // later anyway.
      if (this.sessionEnvironment) {
        rememberSessionKeyedEntry(
          this.sessionEnvironmentBySession,
          prev,
          this.sessionEnvironment,
          SESSION_KEYED_CACHE_MAX_SESSIONS,
        );
      }
      if (this.chatContextBreakdown) {
        rememberSessionKeyedEntry(
          this.chatContextBreakdownBySession,
          prev,
          this.chatContextBreakdown,
          SESSION_KEYED_CACHE_MAX_SESSIONS,
        );
      }
      if (this.lastError) {
        this.chatHealthBySession.set(prev, this.lastError);
      } else {
        // A successful switch means the previous session's last error
        // is no longer relevant; if we ever come back to it the cached
        // null is the right starting point.
        this.chatHealthBySession.delete(prev);
      }
      // JAISIU-3327 — clear in-flight stream state from the *previous*
      // session. Without this, chat-A's `chatStream` text bleeds into
      // chat-B's view and chat-B's own deltas get dropped at the
      // `chatRunId` gate (which still points at chat-A's runId).
      // The tab-layer path also clears these via `clearTransientChat`
      // → `clearChatWaitState`; we null them here so the cleanup is
      // local to the session switch and survives any future refactor
      // of the tab-layer path.
      this.chatStream = null;
      this.chatReasoningStream = null;
      this.chatStreamStartedAt = null;
      this.chatRunId = null;
      // JAISIU-chat-switch-reload — clear the outgoing session's
      // pending flags so the destination tab doesn't briefly inherit
      // chat-A's `sending=true` (composer toolbar pending ring stays
      // painted) or `collectQueueDepth>0` ("Queued (n)" banner pinned
      // to the wrong session). The destination tab's restore below
      // re-paints both from the per-session snapshot if applicable.
      this.chatSending = false;
      this.chatCollectQueueDepth = 0;
      // JAISIU-activity-always-visible — also clear the hint / file /
      // main-run / injection fields so the destination session's
      // restore below starts from a clean slate.
      this.chatLiveRunHint = null;
      this.chatActiveFileToolPath = null;
      this.chatMainRun = null;
      // Parent → children hierarchy is per-session: a parent card on
      // chat-A is unlikely to exist on chat-B, so leaving the selection
      // stuck would orphan the highlight on the next visit. Clear on
      // every switch; the operator can re-focus the children in one
      // click on the new session.
      this.selectedSubagentParentId = null;
      // JAISIU-XXXX — reset the tri-state so the next live run defaults to
      // expanded again. Sticky toggles from a prior terminal run would
      // otherwise leave the new live card collapsed.
      this.chatMainRunExpanded = undefined;
      if (this.chatInjectedAt != null) {
        this.chatInjectedAt = null;
      }
      // JAISIU-3307 — let the per-session subagent stash carry the cards.
      // The WeakMap at subagents-by-session.ts holds onto them for the
      // duration of the app shell's lifetime; we mirror them onto a
      // reactive state field below so Lit re-renders the inspector.
      // Calling `clearTransientChat` (tab-layer path) already stashes;
      // here we stash explicitly because the heavy path doesn't go
      // through `clearTransientChat`.
      stashChatSubagentsForSession(this, prev, this.chatSubagents ?? []);
      recordBackgroundRuns(this, prev, this.sessionBackgroundRuns);
      this.sessionBackgroundRuns = new Set();
      this.sessionBackgroundRunIds = [];
      // Do NOT clear `chatSubagents` here. `activateChatSessionTab` runs a
      // few lines below and stashes `host.sessionKey` (still `prev` at that
      // point) against `host.chatSubagents` — clearing the field first made
      // that second stash write `[]` over the cards we just saved, for the
      // SAME key. `stashChatSubagentsForSession` overwrites unconditionally
      // (`store.delete` + `set`, plus an IndexedDB mirror), so the outgoing
      // session's subagent cards were destroyed in memory and on disk: the
      // inspector came back empty when the operator switched back.
      //
      // No frame renders in between — Lit batches reactive writes, and
      // `activateChatSessionTab` assigns the restored cards synchronously
      // (app-chat-tabs.ts `host.chatSubagents = restoreChatSubagentsForSession(...)`).
      // Leaving the field populated is therefore both safe and necessary.
      if (this.chatFileDiffOpen) {
        void import("./app-file-diff.ts").then(({ closeChatFileDiff }) => closeChatFileDiff(this));
      }
    }
    const sessionChanged = Boolean(trimmed && trimmed !== prev);
    if (sessionChanged && trimmed) {
      syncUrlWithSessionKeyInternal(
        this as unknown as Parameters<typeof syncUrlWithSessionKeyInternal>[0],
        trimmed,
        true,
      );
    }
    const outgoingMessages = Array.isArray(this.chatMessages) ? this.chatMessages : [];
    const nextMessages = selectChatMessagesForSession({
      prevSessionKey: prev,
      nextSessionKey: trimmed,
      prevMessages: outgoingMessages,
      cache: this.chatMessagesBySession,
    });
    if (sessionChanged) {
      this.chatFleetAutoInjectHeld = true;
      clearLiveState(this, trimmed);
      this.chatHydrationMode = nextMessages.warm ? "warm" : "cold";
      this.chatHistoryHydrationError = null;
      this.chatHydrationOutgoing =
        !nextMessages.warm && outgoingMessages.length > 0 && prev
          ? { sessionKey: prev, messages: outgoingMessages }
          : null;
      this.chatTranscriptLoading = !nextMessages.warm && !nextMessages.messages.length;
    }
    // JAISIU-chatswitch-fastpath: apply chatMessages + sessionKey BEFORE
    // the tab-host side effects (draft stash, clearTransientChat, draft
    // restore, settings write). Lit batches reactive writes within a
    // microtask, so the renderer sees one consistent frame: new
    // sessionKey + new transcript together.
    this.chatMessages = nextMessages.messages as unknown[];
    activateChatSessionTab(this.chatTabsHost(), this.chatDraftBySession, trimmed);
    this.chatComposerMode = resolveEffectiveChatComposerMode({
      sessionKey: trimmed,
      globalMode: this.settings.chatComposerMode,
      bySession: this.settings.chatComposerModeBySession,
      tabComposerMode: this.chatTabMeta[trimmed]?.composerMode,
    });
    // UX-PLAN Pillar F — restore per-session send-option toggles on
    // session switch. Falls back to the global `chatSendSelfImprovement`
    // (legacy default) for selfImprovement, and to `false` for the other
    // two. Mutually-exclusive behaviour with the multitask composer mode
    // stays in `handleChatComposerModeChange`; the resolver here never
    // produces a self-contradictory combination.
    const sendOpts = resolveChatSendOptions(
      this.settings.chatSendOptionsBySession,
      {
        orchestrateWithAll: false,
        createNewProject: false,
        selfImprovement: this.settings.chatSendSelfImprovement,
      },
      trimmed,
    );
    this.chatSendOrchestrateAll = sendOpts.orchestrateWithAll;
    this.chatSendCreateNewProject = sendOpts.createNewProject;
    this.chatSendSelfImprovement = sendOpts.selfImprovement;
    this.sessionTabActivity = clearSessionTabUnread(this.sessionTabActivity, trimmed);
    // JAISIU-chatswitch-fastpath: chatMessages is now assigned above,
    // before the tab-host side effects. The selectChatMessagesForSession
    // call has moved up to the top of the sessionChanged block — its
    // return value (`nextMessages`) is consumed immediately.
    // JAISIU-3307 — restore the inspector surfaces from the per-session
    // cache before assigning active state. Each setter is a no-op when
    // the cache misses, so this is a strictly additive fast-path.
    const cachedEnvironment = this.sessionEnvironmentBySession.get(trimmed);
    if (cachedEnvironment) {
      this.sessionEnvironment = cachedEnvironment;
    }
    const cachedBreakdown = this.chatContextBreakdownBySession.get(trimmed);
    if (cachedBreakdown) {
      this.chatContextBreakdown = cachedBreakdown;
    }
    const cachedHealth = this.chatHealthBySession.get(trimmed);
    this.lastError = cachedHealth ?? null;
    // Hydrate subagent cards for the new session. Stash is keyed by
    // `childSessionKey` and lives in a per-host WeakMap; the helper
    // consults both the stash and the canonical `sessionsResult` so
    // when the server has since learned about new children we still
    // surface them.
    const restoredSubagents = restoreChatSubagentsForSession(this, trimmed, this.sessionsResult);
    if (restoredSubagents.length > 0 || this.chatSubagents.length === 0) {
      this.chatSubagents = restoredSubagents;
    }
    // JAISIU-activity-always-visible — restore the per-session live
    // snapshot. Without this, switching to a chat whose run is still
    // in flight leaves the activity bubble blank until the next
    // gateway event lands (which can be many seconds — operators
    // reported the symptom as "no indication the session is working
    // until the last thinking message lands"). The snapshot is
    // consumed (not peeked) so subsequent tab switches don't replay a
    // stale "Working…" indicator for an already-finished run.
    const restoredBackgroundRuns = consumeBackgroundRuns(this, trimmed);
    if (restoredBackgroundRuns.size > 0) {
      this.sessionBackgroundRuns = restoredBackgroundRuns;
      this.sessionBackgroundRunIds = [...restoredBackgroundRuns];
    }
    this.chatHistoryGeneration = (this.chatHistoryGeneration ?? 0) + 1;
    // Per-session, per-host tunnel refactor — repaint the visible
    // tunnel map for the new session. The manager keeps records for
    // every session we've ever connected to, but the inspector only
    // shows the active session's tunnels. Doing this in
    // `activateChatSession` (instead of only on tunnel events)
    // ensures the chip paints the right record immediately after a
    // session switch, without waiting for the next heartbeat.
    const tunnelNext: Record<string, TunnelRecord> = {};
    for (const rec of this.tunnelManager.list()) {
      if (rec.key.sessionId === trimmed) {
        tunnelNext[rec.key.hostId] = rec;
      }
    }
    this.tunnelRecords = tunnelNext;
    // JAISIU-3473 — reset the upward-pagination cursor on session
    // switch. The previous chat's cursor is meaningless for the new
    // session; the next `loadChatHistory` will re-pin it. The pager
    // is also explicitly disabled until the new history arrives so a
    // stray scroll event during the transition cannot fire a
    // `before=<oldCursor>` request against the wrong session.
    this.chatHistoryOldestLoadedId = null;
    this.chatHistoryHasMoreOlder = false;
    // JAISIU-3536 — reset the newest-direction cursor too.
    this.chatHistoryNewestLoadedId = null;
    this.chatHistoryHasMoreNewer = false;
    this.chatHistoryLoadingOlder = false;
    this.chatHistoryLoadingNewer = false;
    this.chatHistoryLoadingCentered = false;
    this.chatHistoryError = null;
    // JAISIU-3474 — clear the restore-pending flag and the in-memory
    // scroll anchor on session switch. The new session's anchor is
    // loaded separately by `loadScrollAnchorForActiveSession` from
    // the connected/firstUpdated watchers — the explicit switch path
    // also kicks it off here so a manual tab click restores just as
    // a refresh does.
    this.chatScrollRestorationPending = false;
    this.chatScrollAnchor = null;
    // JAISIU-3502: notify the scroll-nav controller that the chat
    // transcript grew. Strictly-increasing length is the only valid
    // "a new message arrived below the fold" trigger for the
    // ↓ chevron — equal-length replacements (status swap, role
    // redaction, scroll restore) do NOT arm it.
    this.noteChatMessagesGrew(this.chatMessages.length);

    // JAISIU-3307 — fire the AbortController for any in-flight
    // chat.history from the *previous* session. Network replies for
    // the old key will still arrive but the mutation branch inside
    // `loadChatHistory` short-circuits via `signal.aborted`.
    if (this.chatHistoryAbort) {
      this.chatHistoryAbort.abort();
    }
    // JAISIU-3327 — force a fresh Palace continuity fetch on session switch.
    this.chatPalaceContinuityLastPollAt = 0;
    this.chatLoading = nextMessages.warm ? false : nextMessages.loading;
    this.pendingUserMessageIds = restorePendingUserMessageIdsForSession(
      this.pendingUserMessageIdsBySession,
      trimmed,
    );
    void import("./app-gateway.ts").then(({ refreshFleetSessionActivity }) =>
      refreshFleetSessionActivity(
        this as unknown as Parameters<typeof refreshFleetSessionActivity>[0],
        trimmed,
      ),
    );
    const historyOpts =
      sessionChanged || nextMessages.loading
        ? {
            force: true,
            fullHistory: true as const,
            ...(nextMessages.warm ? { quiet: true as const } : {}),
          }
        : { quiet: true };
    void (async () => {
      try {
        await this.loadChatHistoryOpts(historyOpts);
        if (this.sessionKey.trim() !== trimmed) {
          return;
        }
        this.chatHistoryHydrationError = null;
        // JAISIU-chatswitch-fastpath (G): cache write is owned by the
        // loadChatHistoryOpts wrapper — no explicit set() needed here.
        if (sessionChanged) {
          void this.refreshFleetInteraction();
          void this.refreshFleetProjects();
          this.syncChatSendProjectSelection();
          void this.refreshChatWorkspace();
          void this.refreshChatHistorySessions();
          if (this.connected && (sessionChanged || !this.chatContextBreakdown)) {
            void loadChatContextBreakdown(this);
          }
          if (this.connected) {
            void loadChatPalaceContinuity(this, { quiet: true });
          }
        }
        // JAISIU-3474 — after a manual session switch, restore the
        // scroll position for the NEW session. This mirrors the
        // bootstrap path: load the anchor (await IDB), then ask the
        // cursor pager to drain older pages if the anchor is older
        // than the initial window, then place the viewport.
        if (this.sessionKey.trim() === trimmed) {
          await this.loadScrollAnchorForActiveSession();
          const anchorId = this.chatScrollAnchor?.messageId?.trim();
          await restorePersistedScrollAnchor(
            this as unknown as Parameters<typeof restorePersistedScrollAnchor>[0],
          );
          if (nextMessages.warm && anchorId) {
            this.chatScrollHighlightMessageId = anchorId;
            window.setTimeout(() => {
              if (this.chatScrollHighlightMessageId === anchorId) {
                this.chatScrollHighlightMessageId = null;
              }
            }, 700);
          }
        }
      } catch (err) {
        if (this.sessionKey.trim() === trimmed) {
          this.chatHistoryHydrationError = err instanceof Error ? err.message : String(err);
        }
      } finally {
        if (this.sessionKey.trim() === trimmed) {
          this.chatFleetAutoInjectHeld = false;
          this.chatTranscriptLoading = false;
          this.chatHydrationOutgoing = null;
          this.chatHydrationMode = "idle";
        }
      }
    })();
    this.chatWorkspaceSelectedPath = null;
    this.chatWorkspaceFileContent = null;
    this.chatWorkspaceFileError = null;
    this.chatSelfImprovementRepoWorkspace = null;
    if (this.chatSendSelfImprovement && this.client && this.connected) {
      void handleChatSendSelfImprovementChange(this, true);
    }
    this.chatSendProjectId = pruneChatSendProjectId(trimmed, this.chatSendProjectId, []);
    this.chatActiveProjectId = null;
    closeChatContextPanel(this);
    resetChatPalaceContinuityForSessionChange(this);
    clearChatPendingMessageEdit(
      this as unknown as Parameters<typeof clearChatPendingMessageEdit>[0],
    );
    if (this.tab === "logs" && this.settings.logsScopeLinked) {
      this.logsCursor = null;
      void reloadLogs(this as unknown as import("./app.ts").OpenClawApp, { reset: true });
    }
  }

  handleOpenChatContextPanel() {
    openChatContextPanel(this);
  }

  handleCloseChatContextPanel() {
    closeChatContextPanel(this);
  }

  handleToggleChatComposerPlus() {
    // The listener is attached/detached from `updated()` keyed
    // off the `chatComposerPlusOpen` state change — keeps the
    // single-click guarantee in one place.
    this.chatComposerPlusOpen = !this.chatComposerPlusOpen;
    if (this.chatComposerPlusOpen) {
      this.chatComposerPlusFocusIndex = 0;
    }
  }

  handleCloseChatComposerPlus() {
    // Same: the listener is detached from `updated()`.
    this.chatComposerPlusOpen = false;
  }

  handleSetChatComposerPlusFocusIndex(next: number) {
    this.chatComposerPlusFocusIndex = next;
  }

  handleRefreshChatContextBreakdown() {
    void loadChatContextBreakdown(this);
  }

  handleToggleChatPalaceContinuity() {
    toggleChatPalaceContinuity(this);
  }

  handleCloseChatPalaceContinuity() {
    closeChatPalaceContinuity(this);
  }

  handleRefreshChatPalaceContinuity() {
    void loadChatPalaceContinuity(this, { includeMemorySnippets: true });
  }

  handleClearChatPalaceContinuity() {
    void clearChatPalaceContinuityForSession(this);
  }

  handleRemoveChatPalaceContinuityPoint(pointId: string) {
    void removeChatPalaceContinuityPoint(this, pointId);
  }

  handleChatPalaceContinuityDraftChange(draft: string) {
    setChatPalaceContinuityDraft(this, draft);
  }

  handleChatPalaceContinuityDraftCategoryChange(category: "fact" | "decision") {
    setChatPalaceContinuityDraftCategory(this, category);
  }

  handleAddChatPalaceContinuityPoint() {
    void addChatPalaceContinuityPoint(this);
  }

  onSubagentSpawned(card: ChatSubagentCard) {
    const childKey = card.childSessionKey?.trim();
    if (!childKey) {
      return;
    }
    this.sessionTabActivity = setSessionTabRunning(this.sessionTabActivity, childKey, true);
    if (!this.settings.chatAutoOpenSubagentTabs) {
      return;
    }
    this.openSubagentChatTab({
      childKey,
      parentKey: this.sessionKey,
      label: card.label,
    });
  }

  handleChatActivityTab(tab: import("./views/chat-activity-panel.ts").ChatActivityTab) {
    this.chatActivityTab = tab === "explore" ? "exec" : tab;
  }

  handleChatActivitySearch(query: string) {
    this.chatActivitySearchQuery = query;
  }

  handleFleetV2AnalyticsTab(
    tab: import("./views/fleet-v2-analytics-panel.ts").FleetV2AnalyticsTab,
  ) {
    this.fleetV2AnalyticsTab = tab;
  }

  handleFleetV2DirectoryTab(
    tab: import("./views/fleet-v2-agent-directory.ts").FleetV2DirectoryTab,
  ) {
    this.fleetV2DirectoryTab = tab;
  }

  async handleFleetResetNode() {
    if (!this.connected || this.fleetResetNodeBusy) {
      return;
    }
    // Dry-run preview first (gateway requires confirm:true; preview shows what
    // would be deleted so the confirmation is informed — JAISIU-2186 phase-3).
    const preview = await previewFleetNodeReset(
      this as unknown as Parameters<typeof previewFleetNodeReset>[0],
    );
    const previewSubagents = preview?.purgedLingeringSubagents?.stoppedActive ?? 0;
    const previewLine = preview
      ? `${preview.deletedSessions ?? 0} chat session(s), ${preview.archivedTranscripts ?? 0} transcript(s) archived, ${preview.stoppedEmbeddedRuns ?? 0} run(s) stopped, ${previewSubagents} subagent(s) cancelled`
      : "preview unavailable";
    const confirmed = window.confirm(
      `Reset Node will stop all active agent runs, delete every session except a fresh main session, and clear orchestration queues.\n\nPreview: ${previewLine}.\n\nChat history for removed sessions is archived on disk, not kept in the UI. Continue?`,
    );
    if (!confirmed) {
      return;
    }
    const result = await resetFleetNode(this as unknown as Parameters<typeof resetFleetNode>[0]);
    if (!result?.ok) {
      this.lastError = this.lastError ?? "Reset Node failed.";
      return;
    }
    this.lastError = null;
    // Mirror resetChatSession: wipe live UI first so soft polls cannot paint the
    // pre-reset transcript / orphan subagent cards back onto the reminted main.
    clearChatWaitState(this as unknown as Parameters<typeof clearChatWaitState>[0]);
    this.resetToolStream();
    this.chatMessage = "";
    this.chatAttachments = [];
    this.chatQueue = [];
    syncChatComposeDraft("");
    this.chatRunId = null;
    this.chatStream = null;
    this.chatReasoningStream = null;
    this.chatStreamStartedAt = null;
    this.chatThinkingLevel = null;
    this.chatLiveRunHint = null;
    this.chatMainRun = null;
    this.chatMainRunExpanded = undefined;
    this.chatSubagents = [];
    // Drop every stashed per-session subagent card so `restoreChatSubagentsForSession`
    // cannot paint orphan cards back onto tabs that get reactivated after the
    // server-side registry wipe.
    clearChatSubagentsForHost(this);
    // Cancel any pending inject-path bubble flash. The auto-clear setTimeout
    // in the chat.final handler fires at ~850ms; resetting the stamp now
    // drops the bubble on the next render without waiting for the timer.
    if (this.chatInjectedAt != null) {
      this.chatInjectedAt = null;
    }
    this.chatMessages = [];
    this.pendingUserMessageIds = [];
    this.chatSessionStore.clear();
    this.chatHistoryGeneration = (this.chatHistoryGeneration ?? 0) + 1;
    // Fleet Control staleness verification — wipe the clock on a hard
    // reset. Subsequent loadChatHistory will stamp lastSyncedAt fresh.
    this.chatSync = { lastSyncedAt: null, lastEventAt: null, lastWsCloseAt: null };
    // JAISIU-3327 — force a fresh Palace continuity fetch on session switch.
    this.chatPalaceContinuityLastPollAt = 0;
    // JAISIU-3327 — Palace continuity is keyed by session. The old snapshot
    // belongs to the archived main session; without this clear, the
    // inspector dropdown would show the prior session's points / handoff
    // state until the next chat.final triggers a refresh, which may be
    // many turns away.
    this.chatPalaceContinuity = null;
    this.chatPalaceContinuityError = null;
    this.chatPalaceContinuityPending = new Set<string>();
    this.chatPalaceContinuityFlash = false;

    const mainKey =
      (typeof result.mainSessionKey === "string" && result.mainSessionKey.trim()) ||
      resolveMainSessionKey(this.hello, this.sessionsResult) ||
      this.sessionKey;
    this.applySettings({
      ...this.settings,
      sessionKey: mainKey,
      chatOpenTabs: [mainKey],
    });
    this.sessionNavigatorHistorySkipPush = true;
    this.activateChatSession(mainKey);
    this.sessionNavigatorHistorySkipPush = false;

    await this.refreshSubagentRegistry();
    // Reset clears roster cards; the next sessions.list + registry poll
    // repopulates from gateway truth.
    this.chatSubagents = [];
    await loadSessions(
      this as unknown as Parameters<typeof loadSessions>[0],
      { activeMinutes: CHAT_SESSIONS_ACTIVE_MINUTES },
      { force: true },
    );
    await loadChatHistory(this as unknown as Parameters<typeof loadChatHistory>[0], {
      force: true,
    });
    void this.refreshChatHistorySessions();
  }

  handleToggleChatActivityPanelCollapsed() {
    this.chatActivityPanelCollapsed = !this.chatActivityPanelCollapsed;
  }

  handleToggleChatCheckpointsCollapsed() {
    this.chatCheckpointsCollapsed = !this.chatCheckpointsCollapsed;
  }

  /**
   * JAISIU-3551 — flip the collapsed state of a chat message. Cloning the
   * Set on every mutation is required because Lit's `@state` only triggers
   * a re-render when the reference changes.
   */
  handleToggleChatCollapsedMessage(messageId: string) {
    if (!messageId) return;
    const next = new Set(this.chatCollapsedMessageIds);
    if (next.has(messageId)) {
      next.delete(messageId);
    } else {
      next.add(messageId);
    }
    this.chatCollapsedMessageIds = next;
  }

  /**
   * JAISIU-3473 — fetch the next-older page of chat history and prepend it.
   * Called by the scroll handler when the operator approaches the top of
   * the currently loaded transcript. Idempotent: returns immediately if
   * an older-load is already in flight, if the server has reported no
   * more older messages, or if the cursor is unset.
   */
  /**
   * JAISIU-3474 — explicit `loadChatHistory` entry point for the
   * bootstrap. The controller function (`loadChatHistory` from
   * `controllers/chat.ts`) is already invoked on session switch and
   * WS `onHello`, but this wrapper guarantees the call fires from
   * the lifecycle hooks (`firstUpdated` / `connected` watcher) so
   * a refresh never lands on an empty transcript. Idempotent
   * (the loader has an in-flight guard).
   */
  async loadChatHistory() {
    return this.loadChatHistoryOpts({ fullHistory: true });
  }

  /**
   * Manual retry after a chat-history hydration error. The active
   * transcript (either the warm cache or the outgoing shell) is kept
   * visible; only the hairline flips back to "loading…" until the
   * in-flight `chat.history` resolves. Idempotent — repeated clicks
   * during one inflight request collapse onto the same promise.
   */
  async retryHistoryHydration(): Promise<void> {
    this.chatHistoryHydrationError = null;
    const target = this.sessionKey.trim();
    if (!target) {
      return;
    }
    await this.loadChatHistoryOpts({ force: true, fullHistory: true });
  }

  /** Wire per-session chat caches onto plain controller state objects (tests, stubs). */
  wireChatControllerState(state: import("./controllers/chat.ts").ChatState): void {
    if (state === (this as unknown as import("./controllers/chat.ts").ChatState)) {
      // App already exposes the live store maps — assigning would throw on readonly fields.
      return;
    }
    state.chatMessagesBySession = this.chatMessagesBySession;
    state.pendingUserMessageIdsBySession = this.pendingUserMessageIdsBySession;
    // JAISIU-4089 — let the chat controller report RPC failures to
    // the session-health monitor without an extra import cycle.
    state.noteSessionHealthFailure = (reason) => this.noteSessionHealthFailure(reason);
  }

  async loadChatHistoryOpts(opts?: {
    quiet?: boolean;
    force?: boolean;
    fullHistory?: boolean;
  }): Promise<void> {
    const target = this.sessionKey.trim();
    const host = this as unknown as Parameters<typeof loadChatHistory>[0];
    this.wireChatControllerState(host);
    await loadChatHistory(host, opts);
    // JAISIU-chatswitch-fastpath (G): keep the per-session cache warm on
    // EVERY successful history resolution, not just session switches.
    // Without this, a chat.send that lands while the user is on a
    // different tab updates `chatMessages` but never writes to the
    // cache for the originating session — so the next re-activation of
    // that session would paint stale data until the next quiet poll
    // completes (~6s latency). We only write when the loaded session
    // still matches the requested key; if the user switched away
    // mid-load, the session-switch path's post-await check handles it.
    if (target && this.sessionKey.trim() === target) {
      (this.chatMessagesBySession as Map<string, unknown[]>).set(target, this.chatMessages);
    }
  }

  /**
   * JAISIU-3651 — read-only snapshot of the per-session message cache.
   *
   * Used by the chat-history sidebar render pipeline to compute a
   * body-text index on demand, so the sidebar "Search chats…" input
   * can match loaded message bodies in addition to title + preview.
   * The reference is intentionally the live cache map: callers must
   * not mutate it. The cache is keyed by trimmed session key and
   * holds `unknown[]` of pre-render messages.
   */
  getChatMessagesBySessionForSidebar(): ReadonlyMap<string, unknown[]> {
    return this.chatMessagesBySession as ReadonlyMap<string, unknown[]>;
  }

  async loadOlderChatHistory() {
    await loadOlderChatHistory(this);
  }

  /**
   * JAISIU-3536 — page newer history (downward direction). Fires when
   * the operator scrolls past the rendered bottom AND a saved anchor
   * exists (otherwise the newer direction is owned by polling /
   * streaming, see JAISIU-3535). Idempotent: a duplicate call while
   * a request is in-flight returns the SAME promise — verified by
   * `chat-history-dedupe.node.test.ts`.
   */
  loadNewerChatHistory(): Promise<void> {
    const inflight = (
      this as unknown as {
        __chatHistoryNewerInflight?: Promise<void>;
      }
    ).__chatHistoryNewerInflight;
    if (inflight) return inflight;
    const p = (async () => {
      try {
        const { loadNewerChatHistory } = await import("./app-chat-cursor-parity.ts");
        await loadNewerChatHistory(this);
      } finally {
        (
          this as unknown as {
            __chatHistoryNewerInflight?: Promise<void>;
          }
        ).__chatHistoryNewerInflight = undefined;
      }
    })();
    (
      this as unknown as {
        __chatHistoryNewerInflight?: Promise<void>;
      }
    ).__chatHistoryNewerInflight = p;
    return p;
  }

  /**
   * JAISIU-3536 — clear the latest history-fetch error toast. Called
   * after a successful Retry OR when the operator dismisses the
   * toast. The transcript + scroll position are guaranteed intact.
   */
  clearChatHistoryError(): void {
    this.chatHistoryError = null;
  }

  /**
   * JAISIU-3473 — drain every older page into the DOM. Invoked when the
   * operator enters an in-thread search query so the filter sees the
   * complete conversation history, not just the rendered window. Runs
   * in the background; concurrent invocations coalesce via the
   * in-flight guard inside `loadOlderChatHistory`.
   */
  async drainOlderChatHistory() {
    await drainOlderChatHistory(this);
  }

  /**
   * JAISIU-3474 — load the persisted scroll anchor for the active
   * session from IndexedDB and arm `chatScrollRestorationPending` so
   * the history bootstrap commits a scroll restore after Lit renders
   * the initial page. No-op when IDB is unavailable, when there is no
   * anchor for the active session, or when one is already pending.
   */
  async loadScrollAnchorForActiveSession() {
    const sessionKey = this.sessionKey.trim();
    if (!sessionKey) return;
    if (this.chatScrollRestorationPending) return;
    try {
      const { idbLoadScrollAnchor } = await import("./storage/idb-session-cache.ts");
      const entry = await idbLoadScrollAnchor(sessionKey);
      if (!entry) return;
      if (this.sessionKey.trim() !== sessionKey) return;
      // JAISIU-3533 — defensive scope check: IDB is keyed by
      // sessionKey but a stale record could leak through a session
      // rename. Mirror in the per-session map regardless of pending
      // flag (the cache-hit restore path reads from the map).
      // The new fields (positionRatio / sectionIndex /
      // viewportHeight) flow through unchanged; legacy IDB
      // records without them are tolerated by the schema with
      // safe defaults (see idb-session-cache.ts).
      const rec = {
        sessionKey: entry.sessionKey,
        messageId: entry.messageId,
        viewportOffset: entry.viewportOffset,
        positionRatio: entry.positionRatio,
        sectionIndex: entry.sectionIndex,
        viewportHeight: entry.viewportHeight,
        capturedAt: entry.capturedAt,
        transcriptRevision: entry.transcriptRevision,
      };
      this.chatScrollAnchorBySession = new Map(this.chatScrollAnchorBySession);
      this.chatScrollAnchorBySession.set(entry.sessionKey, rec);
      this.chatScrollAnchor = rec;
      this.chatScrollRestorationPending = true;
    } catch {
      // IDB unavailable (Safari private mode, SSR test env). The
      // app keeps working — the operator just lands at the bottom
      // on refresh, same as before this ticket.
    }
  }

  /**
   * JAISIU-3533 — synchronously capture the outgoing chat's scroll
   * anchor before a session switch commits. Called from the
   * `activateChatSession` (or equivalent) hook so the previous
   * chat's last read position is in the map before any await
   * races the new chat's history load. Flushes the debounced save
   * synchronously to IDB so the anchor survives the very next
   * refresh, not just the next debounce window.
   */
  async captureScrollAnchorForSessionSwitchOut(outgoingSessionKey: string): Promise<void> {
    const trimmed = (outgoingSessionKey ?? "").trim();
    if (!trimmed) return;
    const anchor = this.chatScrollAnchor;
    if (!anchor || anchor.sessionKey !== trimmed) return;
    this.chatScrollAnchorBySession = new Map(this.chatScrollAnchorBySession);
    this.chatScrollAnchorBySession.set(trimmed, anchor);
    if (this.chatScrollAnchorSaveTimer != null) {
      clearTimeout(this.chatScrollAnchorSaveTimer);
      this.chatScrollAnchorSaveTimer = null;
    }
    try {
      const { idbSaveScrollAnchor } = await import("./storage/idb-session-cache.ts");
      await idbSaveScrollAnchor(anchor);
    } catch {
      /* IDB unavailable — best-effort */
    }
  }

  /**
   * JAISIU-3474 — capture the topmost visible message id from the
   * active chat scroll container and persist it (debounced) to IDB
   * keyed by the active session. Called from `handleChatScroll` so
   * the next refresh lands the operator on roughly the same
   * message they were last reading. A debounced IDB save keeps the
   * write rate low (one write per ~250 ms of scroll inactivity
   * rather than one per scroll event).
   */
  scheduleScrollAnchorSave() {
    if (this.chatScrollAnchorSaveTimer != null) {
      clearTimeout(this.chatScrollAnchorSaveTimer);
    }
    this.chatScrollAnchorSaveTimer = window.setTimeout(() => {
      this.chatScrollAnchorSaveTimer = null;
      void this.flushScrollAnchorSave();
    }, 150);
  }

  /**
   * JAISIU-3653 — paint the transient focus highlight on the row
   * that a sidebar click jump landed on. Runs from `updated()` when
   * `chatHistoryJumpFocusMessageId` changes; paints the class on the
   * matched row on the next animation frame so the chat thread has
   * committed the row into the DOM (cache-hit + pre-paint can finish
   * on a different tick), then schedules a timer to clear the field.
   *
   * The class itself is consumed by `chat-search-jump-focus` rules
   * in `fleet-theme.css` (added alongside this ticket) — a soft
   * accent ring that fades after `JUMP_FOCUS_DURATION_MS`. The
   * timer is owned by the host so a session switch / search-clear
   * mid-pulse does not leak stale focus onto the next chat.
   */
  async applyJumpFocusIfNeeded(): Promise<void> {
    const focusId = (this.chatHistoryJumpFocusMessageId ?? "").trim();
    if (!focusId) return;
    // Clear any previously scheduled clear-timer so a fresh
    // jump (rapid A→B clicks) doesn't lose its focus to a stale
    // clear from the previous jump.
    if (this.chatHistoryJumpFocusTimer != null) {
      window.clearTimeout(this.chatHistoryJumpFocusTimer);
      this.chatHistoryJumpFocusTimer = null;
    }
    try {
      await this.updateComplete;
    } catch {
      /* host tearing down — skip */
      return;
    }
    // Two rAFs cover the cache-hit pre-paint + centered-fetch
    // paths: the row may not be in the DOM until either completes.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    // Re-check the target after the awaits so a fast session
    // switch during the wait does not paint focus onto the wrong
    // chat.
    if ((this.chatHistoryJumpFocusMessageId ?? "").trim() !== focusId) {
      return;
    }
    if (this.sessionKey.trim() === "" || this.sessionKey.trim() === null) return;
    const { JUMP_FOCUS_CLASS, JUMP_FOCUS_DURATION_MS } =
      await import("./chat/chat-sidebar-search-jump.ts");
    const root = this.renderRoot;
    if (!root) return;
    const escaped = focusId.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
    const row = root.querySelector<HTMLElement>(`[data-msg-id="${escaped}"]`);
    if (!row) {
      // Row not present yet (centered fetch still in-flight).
      // Leave the focus field armed; the next `updated()` tick
      // (which fires when messages commit) will retry via the
      // same code path.
      return;
    }
    row.classList.add(JUMP_FOCUS_CLASS);
    // Schedule the field clear. The CSS class itself fades on
    // its own via animation-end, but clearing the field
    // guarantees a re-armed jump (rapid clicks) starts from a
    // known clean state.
    this.chatHistoryJumpFocusTimer = window.setTimeout(() => {
      this.chatHistoryJumpFocusTimer = null;
      if ((this.chatHistoryJumpFocusMessageId ?? "").trim() === focusId) {
        this.chatHistoryJumpFocusMessageId = null;
      }
    }, JUMP_FOCUS_DURATION_MS);
  }

  /**
   * JAISIU-3655 — re-run the in-thread search highlight pass.
   * Pure DOM mutation; does NOT dirty a Lit re-render. Walks the
   * rendered chat-thread scroll container, wraps every match in
   * `<mark class="chat-search-hit">`, and stores the resulting
   * mark handles in `chatMessageSearchMarks` so the nav handler
   * can index into them by `chatMessageSearchCurrentIndex`.
   *
   * The pass runs SYNCHRONOUSLY from `updated()` — the Lit commit
   * we just observed guarantees the transcript is in the DOM.
   * `clearHighlights` is called by `highlightMatchesIn` first, so a
   * query change cleanly replaces the previous set.
   *
   * No-op when the chat thread is not mounted, the active query is
   * empty (the operator hasn't searched yet), or the transcript is
   * empty (no messages to highlight). On clear/Esc the
   * `handleChatMessageSearchClear` path already wipes marks via
   * `clearHighlights` and we re-run this method on the cleared
   * query so `chatMessageSearchMarks` resets to `[]`.
   */
  private runChatMessageSearchHighlightPass(): void {
    const root = this.renderRoot;
    if (!root) return;
    const container = findThreadScrollContainer(root);
    if (!container) {
      this.chatMessageSearchMarks = [];
      return;
    }
    const rawQuery = this.chatMessageSearchQuery ?? "";
    // JAISIU-3655 — gate on non-whitespace content (not raw
    // length) so a query like "can " passes the gate; a pure-
    // whitespace query ("   ") still bails. Pass the RAW query
    // (with leading/trailing whitespace if present) into the
    // highlight helper so trailing space is significant — the
    // helper's `buildSearchRegex` translates leading / trailing
    // whitespace into a required `\s+` in the regex.
    const effective = rawQuery.replace(/\s+/g, "");
    if (effective.length === 0) {
      // No active query — make sure no stale marks survive a
      // session switch / clear path that bypassed the explicit
      // clear.
      try {
        clearHighlights(container);
      } catch {
        /* best-effort */
      }
      this.chatMessageSearchMarks = [];
      return;
    }
    try {
      this.chatMessageSearchMarks = highlightMatchesIn(container, rawQuery, {
        searchInActivities: this.chatMessageSearchInActivities,
      });
    } catch {
      // Highlight pass is best-effort; a thrown DOM error
      // (detached subtree, etc) must not crash the host.
      this.chatMessageSearchMarks = [];
    }
    // JAISIU-3655 (followup) — the counter should respect the
    // activity toggle. With the toggle ON we keep the
    // deep-batch walker's full count (live + deep pages, which
    // can be much larger than the marks currently rendered).
    // With the toggle OFF the walker still counts matches
    // inside the activity section but the highlight pass skips
    // them — the operator would see "30 / 200" with only 30
    // yellow marks and be confused. In that case we trust the
    // actual mark count as the source of truth.
    if (!this.chatMessageSearchInActivities) {
      this.chatMessageSearchTotal = this.chatMessageSearchMarks.length;
      // Clamp the current index so the counter never reads
      // "154 / 30" when the marks array shrank.
      if (this.chatMessageSearchCurrentIndex >= this.chatMessageSearchMarks.length) {
        this.chatMessageSearchCurrentIndex = 0;
      }
    }
    // With the toggle ON we leave chatMessageSearchTotal at
    // whatever the deep-batch walker set (handler writes 0
    // initially and the deep-batch result overwrites it once
    // the walker settles).
  }

  /**
   * JAISIU-3655 — apply the `--active` modifier to the current
   * mark AND smooth-scroll it into view IF the operator has
   * just established or navigated the search
   * (`chatMessageSearchFocusOnNextPaint`).
   *
   * The scroll flag exists so the viewport only moves when the
   * operator's gesture explicitly asked for it:
   * 1. They just typed a query (first focus) — `handleChatMessageSearchQueryChange`
   *    sets the flag.
   * 2. They pressed ↑/↓ — `handleChatMessageSearchNav` sets it.
   *
   * Other reactive ticks (transcript updates from streaming,
   * deep-batch result settling, activity toggle flip) re-paint
   * the active mark in place WITHOUT scrolling — the operator
   * is mid-read in those cases and a surprise scroll would feel
   * like the page jumped.
   *
   * The `--active` modifier itself is always applied so the
   * operator can see which match is "you are here" even when
   * the viewport does not move.
   */
  private applyChatMessageSearchActiveMatch(): void {
    const marks = this.chatMessageSearchMarks;
    if (marks.length === 0) {
      this.chatMessageSearchFocusOnNextPaint = false;
      return;
    }
    const index = this.chatMessageSearchCurrentIndex;
    const active = setActiveMatch(marks, index);
    if (!active) {
      this.chatMessageSearchFocusOnNextPaint = false;
      return;
    }
    const root = this.renderRoot;
    if (!root) return;
    const container = findThreadScrollContainer(root);
    if (!container) {
      this.chatMessageSearchFocusOnNextPaint = false;
      return;
    }
    if (!this.chatMessageSearchFocusOnNextPaint) {
      // No scroll — just paint. The flag stays as-is; a future
      // ↑/↓ or first-focus event will re-arm it.
      return;
    }
    this.chatMessageSearchFocusOnNextPaint = false;
    void withProgrammaticScroll(
      this as unknown as Parameters<typeof withProgrammaticScroll>[0],
      () => {
        scrollToMatch(active, container);
      },
    );
  }

  /**
   * JAISIU-3533 — force-flush the debounced anchor save. Invoked on
   * `disconnectedCallback`, on session switch-out, and from the
   * scroll listener after the 150 ms idle debounce so the IDB
   * record is consistent with the in-memory state when the host
   * element is torn down (the operator could refresh at any moment).
   * The capturedAt is fresh, the transcriptRevision is the live
   * counter so a later restore can sanity-check the slice.
   */
  async flushScrollAnchorSave() {
    const anchor = this.chatScrollAnchor;
    const sessionKey = this.sessionKey.trim();
    if (!anchor || anchor.sessionKey !== sessionKey || !sessionKey) {
      return;
    }
    const rec = {
      ...anchor,
      capturedAt: Date.now(),
      transcriptRevision: this.chatScrollTranscriptRevision || String(Date.now()),
      // Refresh positionRatio / sectionIndex from the live host at the
      // moment of the flush so the latest scroll-driven values land in
      // IDB. The anchor reference is the live in-memory mirror, so the
      // spread already carries them; the explicit re-pass below is a
      // belt-and-braces guard against the spread getting out of sync
      // with the schema.
      positionRatio: anchor.positionRatio,
      sectionIndex: anchor.sectionIndex,
      viewportHeight: anchor.viewportHeight,
    };
    this.chatScrollAnchorBySession = new Map(this.chatScrollAnchorBySession);
    this.chatScrollAnchorBySession.set(sessionKey, rec);
    try {
      const { idbSaveScrollAnchor } = await import("./storage/idb-session-cache.ts");
      await idbSaveScrollAnchor(rec);
    } catch {
      // IDB unavailable — skip silently. The in-memory mirror still
      // works for the rest of the session; only refresh-survival is
      // affected.
    }
  }

  async refreshChatCheckpoints() {
    await refreshChatCheckpoints(this);
  }

  async handleRestoreCheckpoint(sinceTs: number) {
    await handleRestoreCheckpoint(this, sinceTs);
  }

  handleComposerReviewAll() {
    void import("./app-chat-cursor-parity.ts").then(({ handleComposerReviewAll }) =>
      handleComposerReviewAll(this),
    );
  }

  handleComposerKeepAll() {
    void import("./app-chat-cursor-parity.ts").then(({ handleComposerKeepAll }) =>
      handleComposerKeepAll(this),
    );
  }

  handleComposerUndoAll() {
    void import("./app-chat-cursor-parity.ts").then(({ handleComposerUndoAll }) =>
      handleComposerUndoAll(this),
    );
  }

  handleAcceptArtifactChange(changeId: string) {
    void import("./app-chat-cursor-parity.ts").then(({ handleAcceptArtifactChange }) =>
      handleAcceptArtifactChange(this, changeId),
    );
  }

  async handleRejectArtifactChange(changeId: string) {
    await handleRejectArtifactChange(this, changeId);
  }

  handleOpenArtifactInProjectFiles(path: string, artifactSessionKey?: string) {
    void import("./app-chat-cursor-parity.ts").then(({ handleOpenArtifactInProjectFiles }) =>
      handleOpenArtifactInProjectFiles(this, path, artifactSessionKey),
    );
  }

  handleChatFileDiffSplitViewChange(next: boolean) {
    this.chatFileDiffSplitView = next;
  }

  async handleFleetTodoStatusChange(
    id: string,
    status: import("./controllers/fleet-todos.ts").FleetTodoStatus,
  ) {
    await handleFleetTodoStatusChange(this, id, status);
  }

  handleFileContextSelect(path: string) {
    void import("./app-chat-cursor-parity.ts").then(({ handleFileContextSelect }) =>
      handleFileContextSelect(this, path),
    );
  }

  handleFolderContextSelect(path: string) {
    void import("./app-chat-cursor-parity.ts").then(({ handleFolderContextSelect }) =>
      handleFolderContextSelect(this, path),
    );
  }

  bumpSessionTabUnread(sessionKey: string) {
    this.sessionTabActivity = bumpSessionTabUnreadMap(this.sessionTabActivity, sessionKey);
  }

  cacheActiveChatMessages() {
    const key = this.sessionKey.trim();
    if (key) {
      this.chatSessionStore.writeMessages(key, this.chatMessages);
    }
  }

  async refreshAgentFleetGraph() {
    await loadAgentFleetGraph(this as unknown as Parameters<typeof loadAgentFleetGraph>[0]);
    try {
      this.fleetGraphConfigCache = await fetchFleetGraphConfig(
        this as unknown as Parameters<typeof fetchFleetGraphConfig>[0],
      );
    } catch {
      this.fleetGraphConfigCache = null;
    }
  }

  handleFleetGraphNodePositionChange(nodeId: string, x: number, y: number) {
    this.applySettings({
      ...this.settings,
      fleetGraphNodePositions: {
        ...this.settings.fleetGraphNodePositions,
        [nodeId]: { x, y },
      },
    });
  }

  handleFleetGraphResetLayout() {
    this.applySettings({
      ...this.settings,
      fleetGraphNodePositions: {},
    });
  }

  handleFleetGraphEditorTab(tab: FleetGraphEditorTab) {
    setFleetGraphEditorTab(this as unknown as Parameters<typeof setFleetGraphEditorTab>[0], tab);
  }

  handleFleetGraphSaveBehavior() {
    void saveFleetGraphBehaviorTab(
      this as unknown as Parameters<typeof saveFleetGraphBehaviorTab>[0],
    );
  }

  handleOpenFleetGuide() {
    this.fleetGuideOpen = true;
    this.fleetGuideTab = "architecture";
  }

  reapplyAgentFleetGraphLiveOverlay() {
    reapplyAgentFleetGraphLiveOverlay(
      this as unknown as Parameters<typeof reapplyAgentFleetGraphLiveOverlay>[0],
    );
  }

  scheduleAgentFleetGraphRefresh(delayMs = 300) {
    scheduleAgentFleetGraphRefreshDebounced(
      this as unknown as Parameters<typeof scheduleAgentFleetGraphRefreshDebounced>[0],
      delayMs,
    );
  }

  handleFleetGraphAddAgent() {
    void openFleetGraphCreateAgent(
      this as unknown as Parameters<typeof openFleetGraphCreateAgent>[0],
    );
  }

  handleFleetGraphEditRoutes() {
    void openFleetGraphInboundEditor(
      this as unknown as Parameters<typeof openFleetGraphInboundEditor>[0],
    );
  }

  handleFleetGraphNodeClick(agentId: string) {
    void openFleetGraphAgentEditor(
      this as unknown as Parameters<typeof openFleetGraphAgentEditor>[0],
      agentId,
    );
  }

  handleFleetGraphLiveEdgeClick(childSessionKey: string) {
    this.openSubagentChatTab({
      childKey: childSessionKey,
      parentKey: this.sessionKey,
    });
  }

  handleEditAgentConfigFromFleet(agentId: string) {
    void openFleetGraphAgentEditor(
      this as unknown as Parameters<typeof openFleetGraphAgentEditor>[0],
      agentId,
    );
  }

  handleOpenAgentsTabForAgent(agentId: string) {
    this.agentsSelectedId = agentId.trim() || this.agentsSelectedId;
    this.setTab("agents");
  }

  handleFleetGraphEditorClose() {
    closeFleetGraphEditor(this as unknown as Parameters<typeof closeFleetGraphEditor>[0]);
  }

  handleFleetGraphEditorSubmit() {
    void submitFleetGraphEditor(this as unknown as Parameters<typeof submitFleetGraphEditor>[0]);
  }

  handleFleetGraphEditorDelete() {
    void deleteFleetGraphAgentFromEditor(
      this as unknown as Parameters<typeof deleteFleetGraphAgentFromEditor>[0],
    );
  }

  async refreshSessionEnvironment() {
    await loadSessionEnvironment(this as unknown as Parameters<typeof loadSessionEnvironment>[0]);
  }

  async refreshFlociAgents() {
    const res = await loadFlociAgents(this.client, this.connected);
    if (res) {
      this.flociAgents = res.agents;
    }
  }

  async refreshSubagentRegistry() {
    if (!this.client || !this.connected) {
      return;
    }
    const runs = await loadSubagentRegistryRuns(this.client, this.sessionKey);
    this.chatSubagents = syncCardsFromRegistry(this.sessionKey, this.chatSubagents ?? [], runs);
    // JAISIU-PROC-INSPCT-COHERENCE — overlay live procset truth (ps + log
    // mtime) onto the cards we just synced. Each card whose childSessionKey
    // has a procset binding gets its status replaced with the procset-derived
    // state so the Inspector reflects what's actually happening, not what
    // the registry row last reported. Cards without a binding pass through.
    const childKeys = (this.chatSubagents ?? [])
      .map((c) => c.childSessionKey?.trim())
      .filter((k): k is string => Boolean(k));
    if (childKeys.length > 0) {
      const procsetStatuses = await loadProcsetStatuses(this.client, childKeys);
      this.chatSubagents = overlayProcsetStatusOnCards(this.chatSubagents ?? [], procsetStatuses);
    }
    // JAISIU-3320 / Pillar I — once every fan-out child has settled
    // (done / error / cancelled / not-started) the pinned-active-job
    // banner no longer applies. Clear it so the next dispatch can
    // re-pin. IDB is mirrored inside `setPinnedActiveJob`.
    this.maybeClearPinnedActiveJob();
  }

  /**
   * JAISIU-3320 / Pillar I — clear the pinned-active-job marker when
   * no subagent cards are still running for the active session. Called
   * after registry refresh + user-cancel updates.
   */
  private maybeClearPinnedActiveJob(): void {
    const pinned = this.settings.pinnedActiveJob;
    if (!pinned || pinned !== this.sessionKey.trim()) {
      return;
    }
    const cards = this.chatSubagents ?? [];
    const stillRunning = cards.some((c) => isSubagentRunActive(c.status));
    if (stillRunning) {
      return;
    }
    this.setPinnedActiveJob(null);
  }

  /**
   * Drop a latched `Subagent · …` composer hint once registry + procset
   * reconcile show no in-flight children. Without this, the sticky latch
   * survives transient null hints and keeps blinking after every unit
   * has stalled or terminated.
   */
  private maybeClearStickyLiveRunHintWhenIdle(): void {
    const cards = this.chatSubagents ?? [];
    if (cards.some((c) => isSubagentRunActive(c.status))) {
      return;
    }
    // JAISIU-types-sweep: `ToolStreamHost` is a structural snapshot
    // of the properties `clearStickyLiveRunHint` reads; OpenClawApp
    // is a structural supertype at runtime. The cast keeps the
    // helper's typed contract honest about what it actually
    // inspects.
    clearStickyLiveRunHint(this as unknown as ToolStreamHost);
  }

  async handleRecreateSessionEnvironment() {
    await recreateSessionEnvironment(this as unknown as SessionEnvironmentState);
    void this.refreshChatWorkspace();
  }

  handleSessionEnvironmentOpenWorkspace() {
    this.applySettings({
      ...this.settings,
      chatPanelsCollapsed: {
        ...this.settings.chatPanelsCollapsed,
        workspace: false,
      },
    });
  }

  handleSessionPreviewPortChange(port: number) {
    if (Number.isFinite(port)) {
      this.sessionPreviewPort = Math.min(9999, Math.max(3000, Math.round(port)));
    }
  }

  async handleRegisterSessionPreview() {
    await registerSessionPreview(
      this as unknown as Parameters<typeof registerSessionPreview>[0],
      this.sessionPreviewPort,
    );
  }

  async handleClearSessionPreview() {
    await clearSessionPreview(this as unknown as Parameters<typeof clearSessionPreview>[0]);
  }

  syncChatSendProjectSelection() {
    this.chatSendProjectId = pruneChatSendProjectId(
      this.sessionKey,
      this.chatSendProjectId,
      this.fleetProjects,
    );
  }

  syncActiveFleetProject() {
    const active = resolveActiveFleetProject({
      client: this.client,
      connected: this.connected,
      sessionKey: this.sessionKey,
      agentsList: this.agentsList,
      agentsDefaultId: this.agentsList?.defaultId,
      fleetProjectsLoading: this.fleetProjectsLoading,
      fleetProjectsError: this.fleetProjectsError,
      fleetProjects: this.fleetProjects,
      chatActiveProjectId: this.chatActiveProjectId,
    });
    this.chatActiveProjectId = active?.id ?? null;
  }

  async refreshFleetProjects() {
    await loadFleetProjects(this as unknown as Parameters<typeof loadFleetProjects>[0]);
    this.syncActiveFleetProject();
    this.syncChatSendProjectSelection();
  }

  async handleCreateFleetProject(name: string) {
    const project = await createFleetProject(
      this as unknown as Parameters<typeof createFleetProject>[0],
      name,
    );
    if (!project) {
      this.lastError = this.fleetProjectsError ?? "Could not create project.";
      return;
    }
    this.chatActiveProjectId = project.id;
    this.chatSendProjectId = project.id;
    this.openChatTab(project.sessionKey);
    this.activateChatSession(project.sessionKey);
    this.applySettings({
      ...this.settings,
      fleetChatMainTab: "chat",
    });
  }

  async handleOpenFleetProject(project: FleetProjectSummary) {
    this.chatActiveProjectId = project.id;
    this.chatSendProjectId = project.id;
    this.openChatTab(project.sessionKey);
    this.activateChatSession(project.sessionKey);
    this.applySettings({
      ...this.settings,
      fleetChatMainTab: "chat",
    });
  }

  handleSelectFleetProjectForFiles(project: FleetProjectSummary) {
    this.chatActiveProjectId = project.id;
    void this.refreshChatWorkspace();
  }

  async handleRenameFleetProject(project: FleetProjectSummary, name: string) {
    await updateFleetProject(
      this as unknown as Parameters<typeof updateFleetProject>[0],
      project.id,
      name,
    );
  }

  async handleDeleteFleetProject(project: FleetProjectSummary) {
    const deleted = await deleteFleetProject(
      this as unknown as Parameters<typeof deleteFleetProject>[0],
      project.id,
    );
    if (!deleted) {
      this.lastError = this.fleetProjectsError ?? "Could not delete project.";
      return;
    }
    const sessionKey = deleted.sessionKey;
    if (this.client && this.connected) {
      try {
        await this.client.request("sessions.delete", {
          key: sessionKey,
          deleteTranscript: true,
        });
      } catch (err) {
        this.lastError = String(err);
      }
    }
    if (this.chatActiveProjectId === project.id) {
      this.chatActiveProjectId = null;
    }
    const main = resolveMainSessionKey(this.hello, this.sessionsResult) ?? this.sessionKey;
    const tabs = removeChatOpenTab(this.getChatOpenTabs(), sessionKey, main);
    this.applySettings({ ...this.settings, chatOpenTabs: tabs });
    if (this.sessionKey === sessionKey) {
      this.activateChatSession(tabs[0] ?? main);
    } else {
      closeChatTabInternal(this.chatTabsHost(), this.chatDraftBySession, sessionKey);
    }
    // Per spec: refresh the FULL chat-history catalog (no 120-min
    // recency filter). The activeMinutes filter used to overwrite
    // `sessionsResult` and the chat-history sidebar lost every row
    // older than 2 hours on every switch/close.
    void this.refreshChatHistorySessions();
    void this.refreshChatWorkspace();
  }

  async handleDiscoverFleetProjects() {
    const n = await discoverFleetProjects(
      this as unknown as Parameters<typeof discoverFleetProjects>[0],
    );
    if (n > 0) {
      // Same as above: full chat-history catalog, no recency filter.
      void this.refreshChatHistorySessions();
    }
  }

  handleSessionInspectorTab(tab: import("./views/fleet-session-inspector.ts").SessionInspectorTab) {
    this.sessionInspectorTab = tab;
    if (tab === "files") {
      void this.refreshSessionArtifacts();
    }
    if (tab === "background") {
      void this.refreshSessionInspectorBackground();
    }
  }

  /**
   * Operator pressed "Reconnect" in the inspector's "Tunnels" tab.
   * Drops the sticky `dead` flag, restarts the gateway client, and
   * lets the controller's own `onTunnelEvent`/`onTunnelHeartbeat`
   * callbacks repaint the chip. The composer is intentionally NOT
   * touched — there is no text to write even on success.
   */
  handleTunnelReconnect(key: { sessionId: string; hostId: string }) {
    this.tunnelHealthController.reconnect(
      this as unknown as Parameters<typeof this.tunnelHealthController.reconnect>[0],
      key,
    );
  }

  /**
   * Drop a tunnel record entirely (e.g. when the operator closes the
   * session). Doesn't affect the gateway client — only the manager.
   */
  handleTunnelForget(key: { sessionId: string; hostId: string }) {
    this.tunnelManager.forget(key);
    // Re-rebuild the visible map (same shape the controller uses on
    // every event).
    const next: Record<string, TunnelRecord> = {};
    for (const rec of this.tunnelManager.list()) {
      if (rec.key.sessionId === this.sessionKey) {
        next[rec.key.hostId] = rec;
      }
    }
    this.tunnelRecords = next;
  }

  async refreshSessionArtifacts() {
    const host = this as unknown as Parameters<typeof loadSessionArtifacts>[0] & {
      filesScopeSessionKey?: string;
    };
    host.filesScopeSessionKey = this.sessionInspectorFilesScopeKey ?? this.sessionKey;
    await loadSessionArtifacts(host);
  }

  async refreshSessionInspectorBackground() {
    await loadInspectorSessionLogs(
      this as unknown as Parameters<typeof loadInspectorSessionLogs>[0],
    );
    void this.refreshSessionEnvironment();
  }

  /**
   * Build the fleet-wide aggregation of subagent cards grouped by their
   * parent session. The active session is excluded — the panel renders
   * those cards as the top group from `state.chatSubagents`.
   */
  getOtherSubagentBuckets(): Array<{
    parentKey: string;
    cards: import("./ui-types.ts").ChatSubagentCard[];
  }> {
    const active = this.sessionKey.trim();
    const buckets = collectAllStashedSubagents(this);
    return buckets
      .filter((b) => b.parentKey.trim() !== active)
      .map((b) => ({ parentKey: b.parentKey, cards: b.cards }));
  }

  /**
   * Public accessor for the inspector's per-session gateway event ring.
   * Returns a shallow copy so callers cannot mutate the host's internal map.
   * When the host hasn't recorded any events for a session the key is absent.
   */
  getGatewayEventsBySession(): Record<string, EventLogEntry[]> {
    return { ...this.eventLogBySession };
  }

  /**
   * Push a single gateway event into the host's per-session ring. Also keeps
   * `gatewayEventTail` (flat) in sync so the inspector can render the flat
   * fallback when no session slicing is desired. Caps both rings to avoid
   * unbounded growth — the cap is intentionally generous (200 / 50).
   *
   * The per-session map is bounded by KEY as well as by depth, via
   * `pushPerSessionRing`. This writer used to unshift directly, so a key
   * added here escaped that bound entirely.
   */
  recordGatewayEvent(entry: EventLogEntry, sessionKey?: string | null): void {
    const capped = { ...entry };
    const key = sessionKey?.trim() || null;
    if (key) {
      pushPerSessionRing(this.eventLogBySession, capped, [key]);
    }
    const tail = [capped, ...this.gatewayEventTail].slice(0, 50);
    this.gatewayEventTail = tail;
  }

  handleSelectSessionArtifact(changeId: string) {
    void loadSessionArtifactDetail(
      this as unknown as Parameters<typeof loadSessionArtifactDetail>[0],
      changeId,
    );
  }

  handleSessionInspectorEditResponses(childSessionKey: string) {
    this.openChatTab(childSessionKey);
    this.sessionInspectorTab = "delegation";
  }

  handleSessionInspectorEditSandboxFiles(childSessionKey: string) {
    this.sessionInspectorFilesScopeKey = childSessionKey;
    this.sessionInspectorTab = "files";
    void this.refreshSessionArtifacts();
  }

  async handleSessionInspectorSelectWorkspaceFile(path: string) {
    this.chatWorkspaceSelectedPath = path;
    await this.openChatWorkspaceFile(path);
    this.sessionArtifactEditContent = this.chatWorkspaceFileContent ?? "";
  }

  async handleSessionInspectorSaveWorkspaceFile() {
    const ok = await saveSessionWorkspaceFile(
      this as unknown as Parameters<typeof saveSessionWorkspaceFile>[0],
    );
    if (ok && this.sessionArtifactSelectedId) {
      void loadSessionArtifactDetail(
        this as unknown as Parameters<typeof loadSessionArtifactDetail>[0],
        this.sessionArtifactSelectedId,
      );
    }
    void this.refreshChatWorkspace();
  }

  async refreshChatWorkspace() {
    await loadFleetSessionFiles(this as unknown as Parameters<typeof loadFleetSessionFiles>[0]);
  }

  async openChatWorkspaceFile(relativePath: string) {
    await readFleetSessionFile(
      this as unknown as Parameters<typeof readFleetSessionFile>[0],
      relativePath,
    );
  }

  toggleChatWorkspaceDir(path: string) {
    const next = { ...this.chatWorkspaceExpandedDirs };
    if (next[path]) {
      delete next[path];
    } else {
      next[path] = true;
    }
    this.chatWorkspaceExpandedDirs = next;
  }

  openChatTab(key: string) {
    openChatTabInternal(this.chatTabsHost(), this.chatDraftBySession, key);
  }

  openSubagentChatTab(params: {
    childKey: string;
    parentKey?: string;
    label?: string;
    task?: string;
    runId?: string;
  }) {
    const childKey = params.childKey.trim();
    if (!childKey) {
      return;
    }
    const parentKey = (params.parentKey ?? this.sessionKey).trim();
    const label = params.label?.trim() || childKey;
    const activeCard = this.chatSubagents?.find((c) => c.childSessionKey === childKey);
    const stashed = lookupSubagentCard(this, childKey, this.sessionsResult, this.chatTabMeta)?.card;
    const card = activeCard ?? stashed;
    const task = params.task?.trim() || card?.task?.trim();
    const runId = params.runId?.trim() || card?.runId?.trim();
    this.chatTabMeta = {
      ...this.chatTabMeta,
      [childKey]: {
        parentKey,
        label,
        ...(task ? { task } : {}),
        ...(runId ? { runId } : {}),
      },
    };
    this.openChatTab(childKey);
    this.chatTabHighlightKey = childKey;
    if (this.chatTabHighlightTimer != null) {
      window.clearTimeout(this.chatTabHighlightTimer);
    }
    this.chatTabHighlightTimer = window.setTimeout(() => {
      if (this.chatTabHighlightKey === childKey) {
        this.chatTabHighlightKey = null;
      }
      this.chatTabHighlightTimer = null;
    }, 4000);
    scrollChatSessionTabIntoView(childKey);
  }

  closeChatTab(key: string) {
    if (this.chatTabMeta[key]) {
      const nextMeta = { ...this.chatTabMeta };
      delete nextMeta[key];
      this.chatTabMeta = nextMeta;
    }
    if (this.chatTabHighlightKey === key) {
      this.chatTabHighlightKey = null;
    }
    closeChatTabInternal(this.chatTabsHost(), this.chatDraftBySession, key);
  }

  handleRequestDeleteSession(key: string) {
    requestSessionDelete(this, key);
  }

  async handleSessionsDeleteConfirm() {
    const trimmed = this.sessionsDeleteConfirm?.key?.trim();
    if (!trimmed) {
      return;
    }
    const deleted = await confirmSessionDelete(this);
    if (!deleted) {
      this.lastError = this.sessionsError ?? "Could not delete session.";
      return;
    }
    await this.applySessionDeletedInUi(trimmed, {
      reminted: deleted.reminted,
      sessionId: deleted.sessionId,
    });
  }

  handleSessionsDeleteCancel() {
    cancelSessionDelete(this);
  }

  handleRequestDeleteAllSessions(keys: string[], rows: import("./types.ts").GatewaySessionRow[]) {
    this.sessionsRowsForDeleteAll = rows;
    requestSessionDeleteAll(this, keys, rows);
  }

  async handleSessionsDeleteAllConfirm() {
    const deletedKeys = await confirmSessionDeleteAll(this);
    if (deletedKeys.length === 0) {
      this.lastError = this.sessionsError ?? "Could not delete any sessions.";
    }
    // UX-R-bulk-active-delete (JAISIU-3327, operator 2026-09-17): if
    // the operator deleted the chat they currently have open in the
    // main panel, switch to another tab (or the main session) so the
    // UI doesn't keep showing the transcript of a session that no
    // longer exists. `applySessionDeletedInUi` is the same path the
    // single-row delete uses, so behaviour stays consistent.
    if (this.sessionKey && deletedKeys.includes(this.sessionKey)) {
      await this.applySessionDeletedInUi(this.sessionKey);
    }
    // JAISIU-3327 / UX-R2 — when the bulk path was triggered from the
    // sidebar's select-mode toolbar, exit select mode and clear the
    // selection so the operator is not stuck looking at dangling checks
    // for rows that may have just been deleted (or rejected by the
    // main-session guard).
    this.chatHistorySelectMode = false;
    this.chatHistorySelectedKeys = [];
    void this.refreshChatHistorySessions();
  }

  handleSessionsDeleteAllCancel() {
    cancelSessionDeleteAll(this);
  }

  async handleViewSessionHistory(sessionKey: string) {
    this.sessionHistoryPanelKey = sessionKey;
    this.sessionHistoryMessages = [];
    this.sessionHistoryError = null;
    this.sessionHistoryLoading = true;
    if (!this.client || !this.connected) {
      this.sessionHistoryError = "Not connected to gateway.";
      this.sessionHistoryLoading = false;
      return;
    }
    try {
      const res = await this.client.request<{ messages?: unknown[] }>("chat.history", {
        sessionKey,
        limit: 200,
      });
      this.sessionHistoryMessages = Array.isArray(res.messages) ? res.messages : [];
    } catch (err) {
      this.sessionHistoryError = String(err);
    } finally {
      this.sessionHistoryLoading = false;
    }
  }

  handleCloseSessionHistory() {
    this.sessionHistoryPanelKey = null;
    this.sessionHistoryMessages = [];
    this.sessionHistoryError = null;
    this.sessionHistoryLoading = false;
  }

  async handleDeleteChatSession(key: string) {
    this.handleRequestDeleteSession(key);
  }

  /**
   * JAISIU-3327 / UX-R2 — bulk-delete the currently-selected chat sessions.
   * Delegates to `requestBulkDelete` / `confirmBulkDelete` from the
   * `chat-history-bulk-select` controller so the request → confirm
   * lifecycle matches the single-row delete path. The controller requires
   * the rows so the confirm modal can show titles + protect the main
   * session, hence the `sessionsResult` plumbing.
   */
  handleBulkDeleteSelectedChatSessions() {
    const keys = [...this.chatHistorySelectedKeys];
    if (!keys.length) {
      return;
    }
    // Pass the FULL sessions catalog as `rows` so the controller can
    // resolve the real main session (systemSent=true). If we filtered
    // `rows` down to the selected subset, the main-session lookup
    // would mark whichever selected row was first as "main" and drop
    // it silently — leaving the operator looking at "Delete 0 chats?"
    // after they clearly selected one. UX-R-confirm-count (JAISIU-3327).
    const rows = this.sessionsResult?.sessions ?? [];
    requestBulkDelete(this, keys, [...rows]);
  }

  /**
   * Bulk-archive every selected chat. Mirrors the single-row archive
   * path (handleArchiveChatSession) so per-row edge cases (main-session
   * guard, tab switching when archiving the active tab) are handled
   * consistently. Selection is preserved across the archive call so the
   * operator can chain operations in one pass.
   */
  handleBulkArchiveSelectedChatSessions() {
    const cleaned = buildBulkArchiveKeys(this.chatHistorySelectedKeys);
    if (!cleaned.length) {
      return;
    }
    for (const key of cleaned) {
      this.handleArchiveChatSession(key);
    }
    void this.refreshChatHistorySessions();
  }

  /**
   * JAISIU-3327 / UX-R2 — push the flattened visible-keys list into the
   * App state so the sidebar's "Select all (N)" counter stays truthful.
   * Called from `app-render-fleet-chat.ts` whenever the catalog changes.
   */
  setChatHistoryAllSelectableKeys(keys: readonly string[]) {
    this.chatHistoryAllSelectableKeys = [...keys];
  }

  private async applySessionDeletedInUi(
    trimmed: string,
    opts?: { reminted?: boolean; sessionId?: string },
  ) {
    const main = resolveMainSessionKey(this.hello, this.sessionsResult) ?? this.sessionKey;
    const tabs = removeChatOpenTab(this.getChatOpenTabs(), trimmed, main);
    this.applySettings(removeArchivedSessionKey({ ...this.settings, chatOpenTabs: tabs }, trimmed));
    if (opts?.reminted) {
      this.chatMessagesBySession?.delete(trimmed);
      if (this.sessionKey === trimmed) {
        this.chatMessages = [];
        this.chatSessionStore.clear();
        this.chatHistoryGeneration = (this.chatHistoryGeneration ?? 0) + 1;
      }
    }
    if (this.sessionKey === trimmed) {
      const next = tabs[0] ?? main;
      this.activateChatSession(next);
      if (opts?.reminted) {
        await loadChatHistory(this as unknown as Parameters<typeof loadChatHistory>[0], {
          force: true,
        });
      }
    }
    void this.refreshChatHistorySessions();
  }

  handleManageSessions() {
    this.applySettings({
      ...this.settings,
      fleetChatMainTab: "chat",
      fleetChatHistoryCollapsed: false,
    });
  }

  handleShowArchivedSessions() {
    this.chatHistoryShowArchived = true;
    this.handleManageSessions();
  }

  handleOpenAgentsFromChat() {
    const agentId = resolveAgentIdForSession(this.sessionKey);
    if (agentId) {
      this.agentsSelectedId = agentId;
    }
    this.setTab("agents");
  }

  handleAgentsPageTabChange(tab: import("./views/agents.ts").AgentsPageTab) {
    this.agentsPageTab = tab;
    if (tab === "roles") {
      hydrateFleetRolesFromConfig(this);
    }
    if (tab === "import") {
      void this.refreshAgentsImportSessions();
      if (this.agentsImportSessionKey.trim()) {
        void this.discoverAgentsForImportSession(this.agentsImportSessionKey);
      } else if (this.sessionKey.trim()) {
        void this.discoverAgentsForImportSession(this.sessionKey);
      }
    }
    if (tab === "settings") {
      void this.loadAgentQueueStatus();
    }
  }

  async loadAgentQueueStatus() {
    if (!this.client || !this.connected) {
      return;
    }
    this.agentsQueueLoading = true;
    this.agentsQueueError = null;
    try {
      const status = await this.client.request<import("./types.js").AgentQueueStatus>(
        "agent.queue.list",
        {},
      );
      this.agentsQueueStatus = status;
      // Initialize concurrency value from status
      if (status && !this.agentsConcurrencyDirty) {
        this.agentsConcurrencyValue = status.maxConcurrent;
      }
    } catch (err) {
      this.agentsQueueError = String(err);
    } finally {
      this.agentsQueueLoading = false;
    }
  }

  handleConcurrencyChange(value: number) {
    this.agentsConcurrencyValue = value;
    this.agentsConcurrencyDirty = true;
  }

  async handleConcurrencySave() {
    if (!this.client || !this.connected) {
      return;
    }
    this.agentsConcurrencySaving = true;
    try {
      const ok = await patchConfig(this as unknown as Parameters<typeof patchConfig>[0], {
        agents: {
          defaults: {
            subagents: {
              maxConcurrent: this.agentsConcurrencyValue,
            },
          },
        },
      });
      if (ok) {
        this.agentsConcurrencyDirty = false;
        await this.loadAgentQueueStatus();
      }
    } finally {
      this.agentsConcurrencySaving = false;
    }
  }

  async loadQueueServiceStatus() {
    if (!this.client || !this.connected) {
      return;
    }
    this.queueServiceLoading = true;
    this.queueServiceError = null;
    try {
      const status = await this.client.request<import("./types.js").QueueServiceStatus>(
        "queue.service.status",
        {},
      );
      this.queueServiceStatus = status;
    } catch (err) {
      this.queueServiceError = String(err);
    } finally {
      this.queueServiceLoading = false;
    }
  }

  async restartQueueService() {
    if (!this.client || !this.connected) {
      return;
    }
    if (
      !window.confirm("Restart the jaisiu-kafka-queue service? This will interrupt active jobs.")
    ) {
      return;
    }
    this.queueRestartLoading = true;
    this.queueRestartError = null;
    try {
      await this.client.request("queue.service.restart", {});
      await this.loadQueueServiceStatus();
    } catch (err) {
      this.queueRestartError = String(err);
    } finally {
      this.queueRestartLoading = false;
    }
  }

  async refreshAgentsImportSessions() {
    await loadSessionsForAgentsImport(
      this as unknown as Parameters<typeof loadSessionsForAgentsImport>[0],
    );
  }

  async discoverAgentsForImportSession(sessionKey: string) {
    await discoverAgentsFromSession(
      this as unknown as Parameters<typeof discoverAgentsFromSession>[0],
      sessionKey,
    );
  }

  handleAgentsImportSelectAllNew() {
    this.agentsImportSelectedIds = this.agentsImportCandidates
      .filter((c) => !c.alreadyRegistered)
      .map((c) => c.id);
  }

  handleAgentsImportClearSelection() {
    this.agentsImportSelectedIds = [];
  }

  handleAgentsImportToggle(agentId: string) {
    toggleAgentsImportSelection(
      this as unknown as Parameters<typeof toggleAgentsImportSelection>[0],
      agentId,
    );
  }

  async handleAgentsImportSelected() {
    const result = await importSelectedAgentsFromChat(
      this as unknown as Parameters<typeof importSelectedAgentsFromChat>[0],
    );
    if (result.ok) {
      await loadAgents(this as unknown as Parameters<typeof loadAgents>[0]);
      if (result.imported[0]) {
        this.agentsSelectedId = result.imported[0];
      }
      this.agentsPageTab = "list";
      this.agentsImportError = null;
    } else if (result.error) {
      this.agentsImportError = result.error;
      this.lastError = result.error;
    }
  }

  handleNewAgentFromChat() {
    this.setTab("agents");
    void this.openAgentsAddModal();
  }

  async handleNewEmptyChatTab() {
    await handleNewEmptyChatTabInternal(this.chatTabsHost(), this.chatDraftBySession);
    void this.refreshChatHistorySessions();
  }

  async handleArchiveAndNewChat() {
    await handleArchiveAndNewChatInternal(this.chatTabsHost(), this.chatDraftBySession);
    void this.refreshChatHistorySessions();
  }

  handleArchiveChatSession(key: string) {
    archiveChatSessionInUi(this.chatTabsHost(), this.chatDraftBySession, key);
    void this.refreshChatHistorySessions();
  }

  /**
   * Rename a chat/session by overwriting its `label`.
   *
   * Per spec:
   * - Only the title (label) changes. No other session field is touched —
   *   we send `{ label }` and nothing else.
   * - It must NOT create another session and must NOT switch the active
   *   one: the gateway's `sessions.patch` mutates the row in place and
   *   `loadSessions` inside `patchSession` only refreshes the catalog.
   * - The active `sessionKey` is left untouched, so renaming the open
   *   chat does not navigate the user away.
   * - On success we refetch the chat-history catalog so the sidebar list
   *   shows the new title without waiting for the next polling tick.
   */
  async handleRenameChatSession(key: string, newTitle: string) {
    const trimmedKey = key.trim();
    if (!trimmedKey || !this.client || !this.connected) {
      return;
    }
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      // Empty rename is a no-op (the prompt stays open on the caller side).
      return;
    }
    await patchSession(this, trimmedKey, { label: trimmedTitle });
    void this.refreshChatHistorySessions();
  }

  handleRestoreArchivedChat(key: string) {
    restoreArchivedChatSession(this.chatTabsHost(), key);
  }

  handleOpenChatHistorySession(key: string, opts?: { jumpToMessageId?: string | null }) {
    // JAISIU-3653 — arm the next session switch with a one-shot
    // jump target. We set the field BEFORE activating so the
    // capture-scroll-anchor / cache-hit pre-paint paths inside
    // `activateChatSession` see it on their very first read.
    // The field is single-shot; `activateChatSession` clears it
    // once the chat has opened, so unrelated navigation
    // (clicking a tab, opening via the session picker) never
    // accidentally inherits the jump target.
    const jumpTo = (opts?.jumpToMessageId ?? "").trim();
    this.chatHistoryPendingJumpToMessageId = jumpTo.length > 0 ? jumpTo : null;
    openChatHistorySessionInternal(this.chatTabsHost(), this.chatDraftBySession, key);
    // Per spec: opening an older chat should cause it to move to the top
    // of the list once it gets bumped by activity (`updated_at`). The
    // server bumps `updated_at` on activity, but the client-side list is
    // only refreshed by the polling loop. Fire a one-shot refetch so the
    // newly-active chat is reflected in the sidebar without waiting for
    // the next poll tick.
    void this.refreshChatHistorySessions();
  }

  async refreshChatHistorySessions() {
    await loadChatHistoryCatalog(this as unknown as Parameters<typeof loadChatHistoryCatalog>[0]);
  }

  /**
   * JAISIU-3653 — resolve the stable `messageId` of the first row
   * in `sessionKey` whose `extractText` contains the active
   * sidebar query. Pure lookup, no side effects.
   *
   * Looks first in the live `chatMessagesBySession` cache (the
   * batch the chat thread is already rendering) and falls back to
   * the deep-batch index populated by the global search modal.
   * Returns `null` when:
   *
   *   - the active sidebar query is empty or shorter than
   *     `SIDEBAR_JUMP_MIN_QUERY_LENGTH` (mirrors the helper's own
   *     short-circuit so the sidebar does not pay for sub-min
   *     queries on every render)
   *   - no row in either source contains the query
   *
   * Passed straight to the sidebar view as
   * `resolveJumpToMessageId`; the view decides when (and whether)
   * to consult it.
   */
  resolveSidebarJumpToMessageId(sessionKey: string): string | null {
    const trimmedKey = (sessionKey ?? "").trim();
    if (!trimmedKey) return null;
    const query = this.chatHistorySearchQuery ?? "";
    // JAISIU-3655 — gate on non-whitespace content length so a
    // query like "can " (3 letters + 1 space) passes the gate
    // the same way "can" does; pure-whitespace still bails.
    if (query.replace(/\s+/g, "").length < SIDEBAR_JUMP_MIN_QUERY_LENGTH) return null;
    const live = this.chatMessagesBySession.get(trimmedKey) ?? null;
    return findFirstMatchingMessageIdAnywhere(trimmedKey, live, query);
  }

  async handleForkCurrentChatTab() {
    await handleForkCurrentChatTabInternal(this.chatTabsHost(), this.chatDraftBySession);
  }

  async loadChatModelCatalog() {
    await loadChatModelCatalogInternal(this);
  }

  async handleChatModelChange(modelRef: string) {
    if (!this.connected) {
      return;
    }
    this.chatModelPatching = true;
    this.chatModelCatalogError = null;
    try {
      await patchSession(this, this.sessionKey, { model: modelRef.trim() || null });
      if (this.sessionsError) {
        this.chatModelCatalogError = this.sessionsError;
      }
    } finally {
      this.chatModelPatching = false;
    }
  }

  handleChatTargetChange(sessionKey: string) {
    const trimmed = sessionKey.trim();
    if (!trimmed || trimmed === this.sessionKey) {
      return;
    }
    if (getChatOpenTabs(this.chatTabsHost()).includes(trimmed)) {
      this.activateChatSession(trimmed);
      return;
    }
    this.openChatTab(trimmed);
  }

  handleChatComposerModeChange(mode: FleetComposerMode) {
    const sessionKey = this.sessionKey.trim();
    const bySession = { ...(this.settings.chatComposerModeBySession ?? {}) };
    if (sessionKey) {
      bySession[sessionKey] = mode;
    }
    this.chatComposerMode = mode;
    this.applySettings({
      ...this.settings,
      chatComposerMode: mode,
      chatComposerModeBySession: bySession,
    });
    if (mode === "multitask") {
      // Pillar F — write through the per-session mirror so the
      // multitask→orchestrate fan-out doesn't leak to other sessions.
      this.writeChatSendOption("orchestrateWithAll", true);
      this.writeChatSendOption("selfImprovement", false);
      this.writeChatSendOption("createNewProject", false);
    } else if (mode !== "agent") {
      this.writeChatSendOption("orchestrateWithAll", false);
      this.writeChatSendOption("selfImprovement", false);
      this.writeChatSendOption("createNewProject", false);
    }
  }

  /**
   * UX-PLAN Pillar F — write one of the chat-send toggles through the
   * per-session mirror. Replaces the legacy global booleans with a
   * session-scoped record so toggles do not leak between sessions.
   */
  writeChatSendOption(field: keyof FleetChatSendOptions, value: boolean) {
    const sessionKey = this.sessionKey.trim();
    if (field === "orchestrateWithAll" && this.chatSendOrchestrateAll !== value) {
      this.chatSendOrchestrateAll = value;
    } else if (field === "createNewProject" && this.chatSendCreateNewProject !== value) {
      this.chatSendCreateNewProject = value;
    } else if (field === "selfImprovement" && this.chatSendSelfImprovement !== value) {
      this.chatSendSelfImprovement = value;
    }
    if (!sessionKey) {
      // No session context — fall back to the global default.
      this.applySettings({
        ...this.settings,
        [field]: value,
      });
      return;
    }
    const bySession = setChatSendOptionForSession(
      this.settings.chatSendOptionsBySession,
      sessionKey,
      field,
      value,
    );
    this.applySettings({
      ...this.settings,
      chatSendOptionsBySession: bySession,
    });
  }

  async handleAbortChat() {
    // JAISIU-3503 — the toast is fired from inside handleAbortChatInternal
    // (in app-chat.ts) via host.notifyRunStopped?.() so every abort
    // entry-point — Stop button, /stop slash command, footer Stop run
    // — gives the same operator-visible confirmation.
    await handleAbortChatInternal(this as unknown as Parameters<typeof handleAbortChatInternal>[0]);
  }

  /**
   * JAISIU-3503 — show a transient "Run stopped" confirmation in the
   * chat composer for the session that was aborted. Per-session so a
   * tab switch mid-toast does not flash it onto the new tab. Auto-
   * clears after CHAT_STOP_TOAST_MS so it never lingers past the
   * operator's next action.
   */
  notifyRunStopped(sessionKey: string) {
    const key = sessionKey?.trim() || "agent:main:main";
    this.chatStopToastBySession = { ...this.chatStopToastBySession, [key]: "Run stopped" };
    const prior = this.chatStopToastTimers.get(key);
    if (prior != null) {
      window.clearTimeout(prior);
    }
    const handle = window.setTimeout(() => {
      this.chatStopToastTimers.delete(key);
      const next = { ...this.chatStopToastBySession };
      delete next[key];
      this.chatStopToastBySession = next;
      this.requestUpdate();
    }, CHAT_STOP_TOAST_MS);
    this.chatStopToastTimers.set(key, handle);
  }

  /**
   * JAISIU-3503 — clear any pending "Run stopped" toast for the given
   * session. Called from the draft-change and send paths so the toast
   * vanishes as soon as the operator starts typing the next prompt.
   */
  clearRunStoppedToast(sessionKey: string) {
    const key = sessionKey?.trim();
    if (!key) {
      return;
    }
    if (this.chatStopToastBySession[key] == null) {
      return;
    }
    const prior = this.chatStopToastTimers.get(key);
    if (prior != null) {
      window.clearTimeout(prior);
      this.chatStopToastTimers.delete(key);
    }
    const next = { ...this.chatStopToastBySession };
    delete next[key];
    this.chatStopToastBySession = next;
  }

  async handleStopThinking() {
    await handleStopThinkingInternal(
      this as unknown as Parameters<typeof handleStopThinkingInternal>[0],
    );
  }

  async handleEditUserMessage(
    rewindTarget: import("./chat/message-transcript.ts").MessageRewindTarget,
    draft: string,
  ) {
    await handleEditUserMessageInternal(
      this as unknown as Parameters<typeof handleEditUserMessageInternal>[0],
      rewindTarget,
      draft,
    );
  }

  handleCancelPendingMessageEdit() {
    clearChatPendingMessageEdit(
      this as unknown as Parameters<typeof clearChatPendingMessageEdit>[0],
    );
    this.chatMessage = "";
    this.chatAttachments = [];
    syncChatComposeDraft("");
  }

  async handleChatEditRewindConfirm() {
    await confirmChatEditRewindAndSend(
      this as unknown as Parameters<typeof confirmChatEditRewindAndSend>[0],
    );
  }

  handleChatEditRewindCancel() {
    cancelChatEditRewindConfirm(
      this as unknown as Parameters<typeof cancelChatEditRewindConfirm>[0],
    );
  }

  async handleRegenerateAssistant() {
    await handleRegenerateAssistantInternal(
      this as unknown as Parameters<typeof handleRegenerateAssistantInternal>[0],
    );
  }

  dismissPlanImplementOffer(fingerprint: string) {
    dismissPlanImplementOfferInternal(
      this as unknown as Parameters<typeof dismissPlanImplementOfferInternal>[0],
      fingerprint,
    );
  }

  async handleImplementPlan() {
    await handleImplementPlanInternal(
      this as unknown as Parameters<typeof handleImplementPlanInternal>[0],
    );
  }

  handleInsertIntoComposer(text: string) {
    const next = this.chatMessage.trim() ? `${this.chatMessage}\n\n${text}` : text;
    this.chatMessage = next;
    syncChatComposeDraft(next);
  }

  handleQueueModeSelect(mode: string) {
    const normalized = mode.trim();
    const allowed = ["steer", "collect", "followup", "interrupt", "steer-backlog"] as const;
    if ((allowed as readonly string[]).includes(normalized)) {
      // Pillar F — write to the per-session mirror so the toggle
      // does not carry forward between sessions. The view reads
      // through `resolveFleetQueueMode` which falls back to the
      // global default when no per-session entry exists.
      const sessionKey = this.sessionKey.trim();
      const bySession = { ...(this.settings.fleetDefaultQueueModeBySession ?? {}) };
      if (sessionKey) {
        bySession[sessionKey] = normalized as (typeof allowed)[number];
      }
      this.applySettings({
        ...this.settings,
        fleetDefaultQueueMode: normalized as (typeof allowed)[number],
        fleetDefaultQueueModeBySession: bySession,
      });
    }
    void this.handleSendChat(`/queue ${mode}`);
  }

  toggleMainRunExpand() {
    // JAISIU-XXXX — tri-state so the row can distinguish "no toggle yet"
    // (default to expanded for live runs, collapsed for terminal runs) from
    // an explicit operator toggle.
    this.chatMainRunExpanded = !this.chatMainRunExpanded;
  }

  removeQueuedMessage(id: string) {
    removeQueuedMessageInternal(
      this as unknown as Parameters<typeof removeQueuedMessageInternal>[0],
      id,
    );
  }

  editQueuedMessage(id: string) {
    editQueuedMessageInternal(
      this as unknown as Parameters<typeof editQueuedMessageInternal>[0],
      id,
    );
  }

  async handleResetChatTab() {
    if (!this.connected) {
      return;
    }
    const confirmed = window.confirm(
      "Reset this chat? The current transcript is archived on disk and replaced with a fresh session.",
    );
    if (!confirmed) {
      return;
    }
    this.wireChatControllerState(this as unknown as Parameters<typeof loadChatHistory>[0]);
    // JAISIU-types-sweep: this cast was to `ResetSessionIdentityHost`,
    // which is the wrong view for this call — `resetChatSession` takes
    // the chat-CONTROLLER host (`ChatState`), and `ChatHost` declares
    // `chatMessages`/`client` as optional while `ChatState` requires
    // them, so the two structural views can never be satisfied by one
    // declaration. `OpenClawApp` is genuinely both; line 5168 above
    // already casts to `loadChatHistory`'s param the same way.
    const resetResult = await resetChatSession(
      this as unknown as Parameters<typeof resetChatSession>[0],
    );
    if (!resetResult.ok) {
      this.lastError = this.lastError ?? "Could not reset session.";
      return;
    }
    applyResetSessionIdentityHost(this, resetResult);
    this.resetChatScroll();
    void loadSessions(this as unknown as Parameters<typeof loadSessions>[0]);
    void this.refreshChatHistorySessions();
  }

  async handleSendChat(
    messageOverride?: string,
    opts?: Parameters<typeof handleSendChatInternal>[2],
  ) {
    await handleSendChatInternal(
      this as unknown as Parameters<typeof handleSendChatInternal>[0],
      messageOverride,
      opts,
    );
  }

  async handleWhatsAppStart(force: boolean) {
    await handleWhatsAppStartInternal(this, force);
  }

  async handleWhatsAppWait() {
    await handleWhatsAppWaitInternal(this);
  }

  async handleWhatsAppLogout() {
    await handleWhatsAppLogoutInternal(this);
  }

  async handleChannelConfigSave() {
    await handleChannelConfigSaveInternal(this);
  }

  async handleChannelConfigReload() {
    await handleChannelConfigReloadInternal(this);
  }

  handleNostrProfileEdit(accountId: string, profile: NostrProfile | null) {
    handleNostrProfileEditInternal(this, accountId, profile);
  }

  handleNostrProfileCancel() {
    handleNostrProfileCancelInternal(this);
  }

  handleNostrProfileFieldChange(field: keyof NostrProfile, value: string) {
    handleNostrProfileFieldChangeInternal(this, field, value);
  }

  async handleNostrProfileSave() {
    await handleNostrProfileSaveInternal(this);
  }

  async handleNostrProfileImport() {
    await handleNostrProfileImportInternal(this);
  }

  handleNostrProfileToggleAdvanced() {
    handleNostrProfileToggleAdvancedInternal(this);
  }

  async refreshFleetInteraction() {
    await refreshFleetInteractionState(this);
  }

  handleFleetQuestionToggleOption(questionId: string, optionId: string, allowMultiple: boolean) {
    fleetQuestionToggleOption(this, questionId, optionId, allowMultiple);
  }

  handleFleetQuestionFreeTextChange(questionId: string, value: string) {
    fleetQuestionFreeTextChange(this, questionId, value);
  }

  async handleFleetQuestionSubmit() {
    await fleetQuestionSubmit(this);
    // After the gateway resolves the question, the operator's reply
    // lands in the transcript. Re-fetch the chat so the assistant's
    // follow-up appears without a manual reload — important on long-
    // running sessions where the operator may not be looking at the
    // chat when the resolve fires. The agent runtime can take a few
    // seconds (especially under load) to commit the transcript, so
    // we poll aggressively for ~30s and back off to one more reload
    // after that. The regular chat poll (every 5s) is what the
    // dashboard was waiting on before — too slow to feel like
    // "selecting in chat should just work".
    this.schedulePostSubmitChatReload();
  }

  private schedulePostSubmitChatReload(): void {
    if (this.postSubmitReloadInterval != null) {
      window.clearInterval(this.postSubmitReloadInterval);
      this.postSubmitReloadInterval = null;
    }
    this.fetchChatHistoryAfterAsk();
    this.postSubmitReloadInterval = window.setInterval(() => {
      if (this.fleetQuestionBusy) {
        return;
      }
      if (!this.lastSubmittedQuestionActive()) {
        this.clearFleetAnswerEchoIfAssistantContinued();
        if (this.postSubmitReloadInterval != null) {
          window.clearInterval(this.postSubmitReloadInterval);
          this.postSubmitReloadInterval = null;
        }
        return;
      }
      this.fetchChatHistoryAfterAsk();
    }, 2000);
    window.setTimeout(() => {
      if (this.postSubmitReloadInterval != null) {
        window.clearInterval(this.postSubmitReloadInterval);
        this.postSubmitReloadInterval = null;
      }
      this.fetchChatHistoryAfterAsk();
      this.clearFleetAnswerEchoIfAssistantContinued();
    }, 30_000);
  }

  private fetchChatHistoryAfterAsk(): void {
    void loadChatHistory(this as unknown as Parameters<typeof loadChatHistory>[0], {
      quiet: true,
      force: true,
    })
      .then(() => {
        this.clearFleetAnswerEchoIfAssistantContinued();
      })
      .catch(() => {
        // Quiet failures — the regular poll is the safety net.
      });
  }

  private lastSubmittedQuestionActive(): boolean {
    return shouldKeepPollingAfterFleetAnswer({
      answerSubmittedAtMs: this.fleetQuestionLastAnswer?.submittedAtMs ?? null,
      newestAssistantTimestampMs: newestAssistantTimestampMs(this.chatMessages),
    });
  }

  /** Once the assistant continues past the Ask answer, drop the sticky
   *  echo so the newest turn is the last message in the thread. */
  private clearFleetAnswerEchoIfAssistantContinued(): void {
    if (
      !shouldClearFleetAnswerEcho({
        answerSubmittedAtMs: this.fleetQuestionLastAnswer?.submittedAtMs ?? null,
        newestAssistantTimestampMs: newestAssistantTimestampMs(this.chatMessages),
      })
    ) {
      return;
    }
    this.fleetQuestionLastAnswer = null;
  }

  handleToggleFleetTodosStripCollapsed() {
    this.fleetTodosStripCollapsed = !this.fleetTodosStripCollapsed;
  }

  handleOpenChatFileDiff(changeId: string, path: string, artifactSessionKey?: string) {
    void import("./app-file-diff.ts").then(({ openChatFileDiff }) =>
      openChatFileDiff(this, { changeId, path, artifactSessionKey }),
    );
  }

  handleCloseChatFileDiff() {
    void import("./app-file-diff.ts").then(({ closeChatFileDiff }) => closeChatFileDiff(this));
  }

  handleChatFileDiffViewChange(view: "diff" | "file") {
    this.chatFileDiffView = view;
  }

  async handleExecApprovalDecision(decision: "allow-once" | "allow-always" | "deny") {
    const active = this.execApprovalQueue[0];
    if (!active || !this.client || this.execApprovalBusy) {
      return;
    }
    this.execApprovalBusy = true;
    this.execApprovalError = null;
    try {
      await this.client.request("exec.approval.resolve", {
        id: active.id,
        decision,
      });
      this.execApprovalQueue = this.execApprovalQueue.filter((entry) => entry.id !== active.id);
    } catch (err) {
      this.execApprovalError = `Exec approval failed: ${String(err)}`;
    } finally {
      this.execApprovalBusy = false;
    }
  }

  handleGatewayUrlConfirm() {
    const nextGatewayUrl = this.pendingGatewayUrl;
    if (!nextGatewayUrl) {
      return;
    }
    this.pendingGatewayUrl = null;
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], {
      ...this.settings,
      gatewayUrl: nextGatewayUrl,
    });
    this.connect();
  }

  handleGatewayUrlCancel() {
    this.pendingGatewayUrl = null;
  }

  // Sidebar handlers for tool output viewing
  handleOpenSidebar(content: string) {
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
      this.sidebarCloseTimer = null;
    }
    this.sidebarContent = content;
    this.sidebarError = null;
    this.sidebarOpen = true;
  }

  /** Long message viewer modal: open with the given markdown + stable messageId. */
  handleOpenMessageViewer(markdown: string, messageId: string) {
    const messageIdEffective = messageId || messageIdFromMarkdownForViewer(markdown);
    const lines = markdown.split(/\n/).length;
    const chars = markdown.length;
    const size = chars >= 1024 ? `${Math.round(chars / 1024)} KB` : `${chars} characters`;
    this.messageViewerMarkdown = markdown;
    this.messageViewerMessageId = messageIdEffective;
    this.messageViewerSummary = `Long message · ${lines} lines · ${size}`;
    const scope = { sessionKey: this.sessionKey, messageId: messageIdEffective };
    const existing = getViewerNote(scope);
    this.messageViewerNoteSaved = existing;
    this.messageViewerNoteDraft = existing?.note ?? "";
    this.messageViewerJustSavedAt = null;
    this.messageViewerOpen = true;
  }

  handleCloseMessageViewer() {
    this.messageViewerOpen = false;
    this.messageViewerJustSavedAt = null;
  }

  // ───── UX-R7 / JAISIU-3297 — global search (Cmd/Ctrl+K) ─────
  /** Open the global search modal. Bound to Ctrl+K / Cmd+K via app-lifecycle. */
  openGlobalSearch() {
    this.globalSearchOpen = true;
    this.globalSearchQueryDraft = this.globalSearchQuery;
    // Focus the input on the next tick (after the modal renders).
    queueMicrotask(() => {
      const input = this.querySelector<HTMLInputElement>(
        '[data-testid="fleet-global-search-input"]',
      );
      input?.focus();
      input?.select();
    });
  }

  /** Close the global search modal. Bound to ESC, overlay click, and Ctrl+K (toggle). */
  closeGlobalSearch() {
    cancelDebouncedGlobalSearch();
    this.globalSearchOpen = false;
    this.globalSearchQueryDraft = "";
    this.globalSearchQuery = "";
    this.globalSearchResults = [];
    this.globalSearchError = null;
    this.globalSearchLoading = false;
    this.globalSearchDeepLoading = false;
    // JAISIU-3652 — drop the per-session ephemeral text index the
    // deep walker accumulated while the modal was open. Keeping
    // it across opens would leak memory for sessions the operator
    // may never search again.
    clearDeepBatchSearchState();
  }

  /**
   * JAISIU-3652 — adapter that hands the deep walker the slice of
   * app state it needs. Returns `null` when the gateway is not
   * connected; the walker treats that as a no-op.
   */
  private getGlobalSearchDeepSource(): GlobalSearchDeepSource | null {
    if (!this.client || !this.connected) {
      return null;
    }
    return {
      listSessions: () => this.sessionsResult?.sessions ?? [],
      // JAISIU-types-sweep: the deep walker accepts
      // `ReadonlyMap<string, unknown[]>`; the shared field is the
      // wider union cache so we coerce via the unknown[] branch
      // (the values are still the same rows).
      getMessagesBySession: () => this.chatMessagesBySession as ReadonlyMap<string, unknown[]>,
      isConnected: () => this.connected,
      getClient: () => this.client,
    };
  }

  /**
   * Apply the draft query. The modal renders the draft on every
   * keystroke; this handler debounces (250ms) before firing the RPC.
   * Mirrors `usageQueryDraft` → `usageQuery` on the llm-stats panel.
   */
  handleGlobalSearchQueryChange(next: string) {
    this.globalSearchQueryDraft = next;
    // JAISIU-3652 — install the deep source lazily so a closed
    // modal does not retain a live adapter closure. The walker is
    // best-effort: if `client`/`connected`/`sessionsResult` are
    // absent, the source returns null and the controller skips the
    // deep path.
    const host = this as unknown as Parameters<typeof runDebouncedGlobalSearch>[0] & {
      globalSearchDeepSource?: GlobalSearchDeepSource;
    };
    host.globalSearchDeepSource = this.getGlobalSearchDeepSource() ?? undefined;
    if (!next.trim()) {
      cancelDebouncedGlobalSearch();
      this.globalSearchQuery = "";
      this.globalSearchResults = [];
      this.globalSearchError = null;
      this.globalSearchLoading = false;
      this.globalSearchDeepLoading = false;
      return;
    }
    runDebouncedGlobalSearch(host, next);
  }

  /** Open a chat from a global-search hit. Delegates to the same path the sidebar uses. */
  handleOpenGlobalSearchResult(result: GlobalSearchResult) {
    const key = result.sessionKey;
    if (!key) return;
    this.handleOpenChatHistorySession(key);
    this.closeGlobalSearch();
  }

  handleMessageViewerNoteChange(next: string) {
    this.messageViewerNoteDraft = next;
    if (this.messageViewerJustSavedAt != null) {
      this.messageViewerJustSavedAt = null;
    }
  }

  handleSaveMessageViewerNote() {
    if (!this.messageViewerMessageId) {
      return;
    }
    const scope = { sessionKey: this.sessionKey, messageId: this.messageViewerMessageId };
    const record = saveViewerNote(scope, this.messageViewerNoteDraft);
    this.messageViewerNoteSaved = record;
    if (record) {
      this.messageViewerJustSavedAt = Date.now();
      this.messageViewerNoteDraft = record.note;
    } else {
      this.messageViewerJustSavedAt = null;
      this.messageViewerNoteDraft = "";
    }
  }

  handleCloseSidebar() {
    this.sidebarOpen = false;
    // Clear content after transition
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
    }
    this.sidebarCloseTimer = window.setTimeout(() => {
      if (this.sidebarOpen) {
        return;
      }
      this.sidebarContent = null;
      this.sidebarError = null;
      this.sidebarCloseTimer = null;
    }, 200);
  }

  handleSplitRatioChange(ratio: number) {
    const newRatio = Math.max(0.4, Math.min(0.7, ratio));
    this.splitRatio = newRatio;
    this.applySettings({ ...this.settings, splitRatio: newRatio });
  }

  handleToggleSubagentExpand(id: string) {
    const next = { ...this.chatSubagentExpanded };
    next[id] = !next[id];
    this.chatSubagentExpanded = next;
  }

  handleToggleTerminalSubagentOpen(id: string) {
    const next = { ...this.chatSubagentTerminalOpen };
    next[id] = !next[id];
    this.chatSubagentTerminalOpen = next;
  }

  handleSubagentFilterChange(next: "all" | "running") {
    this.chatSubagentFilter = next;
  }

  handleCollapseAllSubagents() {
    this.chatSubagentExpanded = {};
    // JAISIU-XXXX — also drop per-session terminal-open flags so the
    // collapse-all affordance wipes both live and finished run state.
    this.chatSubagentTerminalOpen = {};
    // Parent-selection is a visual-only affordance; clearing it here
    // makes the collapse-all button a one-stop "back to flat view".
    this.selectedSubagentParentId = null;
  }

  /**
   * Parent → children hierarchy: set/clear the parent whose descendants
   * should be highlighted. Pass `null` to clear; passing the same id
   * again toggles selection off (matches the renderer contract).
   * Switching chat sessions automatically clears the selection — see
   * `selectedSubagentParentId` reset in the session-change path.
   */
  handleSelectSubagentParent(parentId: string | null) {
    if (parentId === null) {
      this.selectedSubagentParentId = null;
      return;
    }
    this.selectedSubagentParentId = this.selectedSubagentParentId === parentId ? null : parentId;
  }

  handleJumpLatestRunningSubagent() {
    const rows = this.querySelectorAll(
      '.chat-subagent-row[data-run-status="running"], .chat-subagent-row[data-run-status="spawning"], .chat-subagent-row[data-run-status="queued"]',
    );
    const last = rows[rows.length - 1];
    last?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  /**
   * JAISIU-3319 / Pillar H: cancel an in-flight subagent from the parent
   * thread. Optimistically flips the card to `cancelled` so the button
   * grey-out happens immediately; the RPC result will reconcile via
   * `chat.final` + a subsequent registry poll (SubagentCard.status
   * derives from the registry anyway).
   */
  async handleCancelSubagentRun(card: ChatSubagentCard) {
    if (!this.client || !card.childSessionKey) return;
    if (this.chatSubagentPendingActions[card.id]) return;
    this.chatSubagentPendingActions = { ...this.chatSubagentPendingActions, [card.id]: "cancel" };
    try {
      const res = await requestSessionsCancel(this.client, {
        childSessionKey: card.childSessionKey,
        reason: "user cancelled from subagent card",
      });
      if (!res.ok) {
        console.warn(`[subagent-cancel] RPC failed for ${card.childSessionKey}: ${res.error}`);
      }
      // Optimistically reflect the cancel locally; the registry poll will
      // re-derive the canonical status within a few seconds.
      this.chatSubagents = this.chatSubagents.map((c) =>
        c.id === card.id ? { ...c, status: "cancelled", error: "cancelled by user" } : c,
      );
      // JAISIU-3320 / Pillar I — see if the user-cancel closed out the
      // last running fan-out child.
      this.maybeClearPinnedActiveJob();
    } finally {
      const { [card.id]: _pending, ...rest } = this.chatSubagentPendingActions;
      this.chatSubagentPendingActions = rest;
    }
  }

  /**
   * Stop the whole fan-out: terminate every in-flight child of the current
   * session in one `subagents.cancelAll` call.
   *
   * Cards flip to `cancelled` immediately so the aggregate status is honest
   * right away; the registry poll reconciles the canonical outcome.
   */
  async handleCancelAllSubagents(opts: { parentSessionKey: string; childSessionKeys?: string[] }) {
    const client = this.client;
    const parentSessionKey = opts.parentSessionKey.trim();
    if (!client || !parentSessionKey) return;
    this.setFanInPending(parentSessionKey, { cancelling: true });
    this.setFanInError(parentSessionKey, undefined);
    try {
      const res = await requestCancelAllSubagents(client, {
        parentSessionKey,
        childSessionKeys: opts.childSessionKeys,
        reason: "orchestration stopped by operator",
      });
      if (!res.ok) {
        // Per AGENTS.md, silence is not health: a stop that did not land must
        // be visible rather than looking like an idle system.
        console.warn(`[subagent-cancel-all] RPC failed for ${parentSessionKey}: ${res.error}`);
        this.setFanInError(parentSessionKey, `Could not stop subagents: ${res.error}`);
        return;
      }
      const stopped = new Set(res.cancelledChildSessionKeys);
      this.chatSubagents = this.chatSubagents.map((c) =>
        c.childSessionKey && stopped.has(c.childSessionKey)
          ? { ...c, status: "cancelled", error: "stopped by operator", updatedAt: Date.now() }
          : c,
      );
      this.maybeClearPinnedActiveJob();
    } finally {
      this.setFanInPending(parentSessionKey, { cancelling: false });
    }
  }

  /**
   * Ask the parent session to synthesize its fan-out results.
   *
   * The verdict is injected into the parent transcript (`chat.inject`,
   * `fleet-complete` label), so it is persisted server-side: navigating away
   * and back re-renders it from history rather than losing it. Until the
   * parent's reply lands the bar shows "Synthesizing…"; the gateway handler
   * flips it to complete when the synthesis message arrives.
   */
  async handleSynthesizeFanIn(opts: { force?: boolean }) {
    const client = this.client;
    const parentSessionKey = this.sessionKey.trim();
    if (!client || !parentSessionKey) return;

    const resultLabels = this.chatSubagents
      .filter((c) => c.status === "done")
      .map((c) => c.label?.trim())
      .filter((l): l is string => Boolean(l));

    this.setFanInPending(parentSessionKey, { synthesizing: true });
    this.setFanInError(parentSessionKey, undefined);
    this.setFanInSynthesis(parentSessionKey, { state: "requested", requestedAt: Date.now() });
    try {
      const res = await requestFanInSynthesis(client, {
        parentSessionKey,
        resultLabels,
        force: opts.force,
      });
      if (!res.ok) {
        console.warn(`[fan-in-synthesis] inject failed for ${parentSessionKey}: ${res.error}`);
        this.setFanInError(parentSessionKey, `Could not start synthesis: ${res.error}`);
        this.setFanInSynthesis(parentSessionKey, {
          state: "failed",
          requestedAt: Date.now(),
          detail: res.error,
        });
        return;
      }
      this.setFanInSynthesis(parentSessionKey, { state: "running", requestedAt: Date.now() });
    } finally {
      this.setFanInPending(parentSessionKey, { synthesizing: false });
    }
  }

  /** Mark a fan-in synthesis as landed (called when the parent reply arrives). */
  markFanInSynthesized(parentSessionKey: string) {
    this.setFanInSynthesis(parentSessionKey, { state: "done" });
    this.setFanInError(parentSessionKey, undefined);
  }

  private setFanInSynthesis(
    sessionKey: string,
    record: import("./chat/fan-in-state.ts").FanInSynthesisRecord,
  ) {
    this.fanInSynthesisBySession = { ...this.fanInSynthesisBySession, [sessionKey]: record };
  }

  private setFanInPending(
    sessionKey: string,
    patch: { synthesizing?: boolean; cancelling?: boolean },
  ) {
    const prev = this.fanInPendingBySession[sessionKey] ?? {};
    this.fanInPendingBySession = {
      ...this.fanInPendingBySession,
      [sessionKey]: { ...prev, ...patch },
    };
  }

  private setFanInError(sessionKey: string, error: string | undefined) {
    this.fanInErrorBySession = { ...this.fanInErrorBySession, [sessionKey]: error };
  }

  /**
   * JAISIU-3319 / Pillar H: respawn a settled subagent with the same task +
   * agent + model. Reuses the orchestrator's spawn RPC rather than rolling
   * a new fan-out — the operator clicked "retry" on one card, not the
   * whole fleet.
   */
  async handleRetrySubagentRun(card: ChatSubagentCard) {
    if (!this.client || !card.task) return;
    if (this.chatSubagentPendingActions[card.id]) return;
    if (card.status !== "error" && card.status !== "cancelled" && card.status !== "done") return;
    this.chatSubagentPendingActions = { ...this.chatSubagentPendingActions, [card.id]: "retry" };
    try {
      const res = await requestSessionsSpawn(this.client, {
        sessionKey: this.sessionKey,
        task: card.task,
        label: card.label,
        agentId: card.agentId,
        model: card.model,
      });
      if (!res.ok) {
        console.warn(`[subagent-retry] RPC failed for ${card.id}: ${res.error}`);
      }
    } finally {
      const { [card.id]: _pending, ...rest } = this.chatSubagentPendingActions;
      this.chatSubagentPendingActions = rest;
    }
  }

  render() {
    return renderApp(this as unknown as AppViewState);
  }
}
